import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import * as fabric from 'fabric';
import { CanvasArea } from '../components/CanvasArea';
import {
  Rulers,
  Grid,
  Guides,
  GridSettingsDialog,
  NewGuideDialog,
  SmartGuides,
} from '../components/CanvasOverlays';
import { AlignPanel } from '../components/AlignPanel';
import {
  computeAlignDeltas,
  computeDistributeDeltas,
  type AlignAnchor,
  type AlignTarget,
  type DistributeAxis,
} from '../lib/layer-align';
import { EditorToolbar } from '../components/EditorToolbar';
import { HistoryPanel } from '../components/HistoryPanel';
import { EditorTabBar } from '../components/EditorTabBar';
import { ToolPalette } from '../components/ToolPalette';
import { PropertiesPanel } from '../components/PropertiesPanel';
import { RETOUCH_VARIANT_ORDER, VARIANT_CHIPS } from '../components/RetouchPanel';
import { AIToolPickerDialog } from '../components/AIToolPickerDialog';
import { StatusBar } from '../components/StatusBar';
import { ColorRangeDialog } from '../components/ColorRangeDialog';
import { RefineEdgeWorkspace } from '../components/RefineEdgeWorkspace';
import { LiquifyWorkspace } from '../components/LiquifyWorkspace';
import { ExportDialog, type ExportDestination } from '../components/ExportDialog';
import {
  AdjustmentsModal,
  adjustmentDisplayName,
  type AdjustmentKind,
} from '../components/adjustments';
import type { AdjustmentConfig } from '../types';
import { ConfirmCloseDialog, type SaveDestination } from '../components/ConfirmCloseDialog';
import { RasterizeBeforeWarpDialog } from '../components/RasterizeBeforeWarpDialog';
import { RasterizeBeforePaintDialog } from '../components/RasterizeBeforePaintDialog';
import { invalidatePaintSurfaceSrcCache } from '../lib/paint-surface-serializer';
import type { AIStatus, SmartCreateInsertModel } from '../components/PropertiesPanel';
import { IconLegendDialog } from '../components/IconLegendDialog';
import { CommandPalette } from '../components/CommandPalette';
import { KeyboardSettingsDialog } from '../components/KeyboardSettingsDialog';
import { TemplatePickerDialog } from '../components/TemplatePickerDialog';
import { SaveTemplateDialog } from '../components/SaveTemplateDialog';
import { DocumentSettingsDialog } from '../components/DocumentSettingsDialog';
import { CanvasSizeDialog, type AnchorFactor } from '../components/CanvasSizeDialog';
import type { DocumentTemplate } from '../lib/builtin-templates';
import { CommandRegistry, isMacPlatform, type CommandContext } from '../lib/command-registry';
import {
  buildEditorCommands,
  TOOL_HISTORY_LABELS,
  type EditorCommandHandlers,
} from '../lib/editor-commands';
import {
  loadKeyboardOverrides,
  saveKeyboardOverrides,
  type KeyboardOverrides,
} from '../lib/keyboard-overrides';
import { runSmartDelete } from '../lib/smart-delete';
import { pathToSvgData } from '../lib/vector-path';
import {
  parsePsdToDocument,
  PsdBitDepthError,
  PsdColorModeError,
  summarizePsdWarnings,
  type PsdImportResult,
} from '../lib/psd-import';
import { savePsdStash, gcOrphanedStashes, PsdStashQuotaError } from '../lib/psd-source-stash';
import { exportDocumentToPsd } from '../lib/psd-export';
import { exportDocumentToSvg } from '../lib/svg-export';
import { exportDocumentToPdf, PDF_IMAGE_JPEG_QUALITY } from '../lib/pdf-export';
import { saveBlobLocally } from '../lib/save-blob-locally';
import {
  encodeSliceCanvas,
  exportSlicesAsBlobs,
  bundleSlicesIntoZip,
  type SliceExportFormat,
} from '../lib/slice-export';
import {
  buildActiveLayerRegionCanvas,
  buildSelectionRegionCanvas,
  buildVisibleLayersRegionCanvas,
  scaleRegionCanvas,
} from '../lib/region-export';
import {
  parseSvgToDocument,
  summarizeSvgWarnings,
  SvgScriptError,
  SvgMalformedError,
  SvgEntityDeclarationError,
  SvgLayerCountError,
  SvgNestingDepthError,
  type SvgImportResult,
} from '../lib/svg-import';
import { rasterizeSvgFragment, SvgRasterizeError } from '../lib/svg-rasterize';
import { saveSvgStash, SvgStashQuotaError } from '../lib/svg-source-stash';
import api from '@/lib/api';
import { useImglyRemoveBackground } from '../lib/background-removal';
import { toast } from '@/components/ui/use-toast';
import { FiasImagePickerDialog } from '../components/FiasImagePickerDialog';
import { LayersPanel } from '../components/LayersPanel';
import { useImageEditorState } from '../hooks/useImageEditorState';
import { useFabricCanvas, getObjectLayerId } from '../hooks/useFabricCanvas';
import { getDestructiveAdjustmentLayerIds } from '../lib/adjustment-commit';
import { useCanvasHistory, SERIALIZED_PROPS } from '../hooks/useCanvasHistory';
import { useImageExport } from '../hooks/useImageExport';
import { useIsLandscape } from '../hooks/useIsLandscape';
import { useDocumentTabs } from '../hooks/useDocumentTabs';
import {
  loadAllSessions,
  loadLastActiveDocumentId,
  saveSession,
  saveLastActiveDocumentId,
  clearAllSessions,
  type PersistedSession,
} from '../lib/session-store';
import {
  SMART_CREATE_MAX_INPUT_EDGE_PX,
  STABILITY_MIN_INPUT_EDGE_PX,
  RECRAFT_MIN_INPUT_EDGE_PX,
  OPENAI_MIN_INPUT_EDGE_PX,
  FEATHER_RADIUS_RATIO_DEFAULT,
  computeFeatherRadius,
  featherMask,
  fitCanvasToMaxDim,
  flattenForExport,
  loadImageToCanvas,
  toGrayscaleMask,
} from '../lib/inpaint-helpers';
import { downloadSvgLocal, uploadSvgToFias } from '../lib/save-svg';
import {
  clearRenderCache,
  createCompositorCache,
  createProxyScratchHolder,
  hasUnimplementedSlowPath,
  renderDocument,
  requiresCustomComposite,
  type CompositorCache,
  type ProxyScratchHolder,
} from '../lib/render-pipeline';
import { hasRasterizableTarget } from '../lib/layer-tree';
import {
  generatePolygonPath,
  generateStarPath,
  generateArrowPath,
  shouldRegenerateVectorPrimitive,
} from '../lib/shape-generators';
import { pathBoolean, type BooleanOp } from '../lib/path-ops';
import { traceMaskAlphaToVectorPath, canvasRectPath } from '../lib/vector-mask';
import {
  VectorizeSaveDialog,
  type VectorizeSaveDestination,
} from '../components/VectorizeSaveDialog';
import type {
  CanvasNativeExportFormat,
  FiasUploadFormat,
  EditorTool,
  ExportFormat,
  ExportSettings,
  SliceRect,
  ImageToImageEngine,
  EditorOutputMode,
  InpaintEngine,
  RemoveBackgroundEngine,
  ReplaceBackgroundEngine,
  ReplaceBackgroundLightDirection,
  ReplaceBackgroundRecraftStyle,
  UpscaleMode,
  UpscaleEngine,
  UpscaleFactor,
  ControlMode,
  Layer,
  EditorDocument,
  LayerType,
  BlendMode,
  LayerEffects,
  LayerMask,
  ShapeSettings,
  VectorPath,
} from '../types';

/** Map a filename's extension to the matching ExportFormat. Used to
 *  seed the Export dialog's format dropdown so opening a JPEG and
 *  clicking Save / Save As lands the dialog on JPEG (not PNG). Falls
 *  back to `'png'` when the extension is missing / unrecognized. */
function inferExportFormatFromFileName(name: string | null | undefined): ExportFormat {
  const lower = name?.toLowerCase() ?? '';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'jpeg';
  if (lower.endsWith('.webp')) return 'webp';
  if (lower.endsWith('.avif')) return 'avif';
  if (lower.endsWith('.tiff') || lower.endsWith('.tif')) return 'tiff';
  if (lower.endsWith('.psd') || lower.endsWith('.psb')) return 'psd';
  if (lower.endsWith('.svg')) return 'svg';
  if (lower.endsWith('.pdf')) return 'pdf';
  return 'png';
}

/** Whether the Async Clipboard API can read images out of the OS
 *  clipboard. Available in secure contexts on modern browsers. Used
 *  to enable the toolbar Paste button even when the Fabric internal
 *  clipboard is empty — so a user with a screenshot in their OS
 *  clipboard can paste via the button (mobile especially, where
 *  Ctrl+V isn't an option). */
const SUPPORTS_OS_CLIPBOARD_READ =
  typeof navigator !== 'undefined' && typeof navigator.clipboard?.read === 'function';

/** Default at-creation radius / half-extent for vector primitives.
 *  Used when the shape tool drops a fresh polygon / star / arrow
 *  onto the canvas; the slider-driven regenerate flow overrides
 *  this with the layer's current scene-space half-extent so a
 *  scaled primitive doesn't snap back to default size when its
 *  parameters change. */
const DEFAULT_VECTOR_PRIMITIVE_RADIUS = 60;

/** Build a `VectorPath` for one of the Tier 2 Group A vector
 *  primitives (polygon / star / arrow) centered at `center`. Used
 *  by both the shape-tool creation flow and the slider-driven
 *  regenerate flow — keeps the per-primitive defaults + stroke/
 *  fill wiring in one place so the two paths can't drift.
 *
 *  `halfExtent` is the radius (polygon / star) or half-length
 *  (arrow) the generator targets. At creation time the caller
 *  omits it and the default is used; on slider-driven regenerate
 *  the caller passes the layer's current scene-space half-extent
 *  so the visual size stays put across slider ticks (e.g. a
 *  polygon scaled to 200×200 stays 200×200 when Sides changes).
 *
 *  Caveat: the regenerate path normalizes back to uniform
 *  proportions — a non-uniform user scale (squished polygon)
 *  becomes circular again on regen. Intentional v1 trade-off
 *  documented in the audit (S5). */
function generateVectorShapePath(
  settings: ShapeSettings,
  center: { x: number; y: number },
  halfExtent: number = DEFAULT_VECTOR_PRIMITIVE_RADIUS,
): VectorPath {
  let path: VectorPath;
  if (settings.type === 'polygon') {
    path = generatePolygonPath(center, {
      sides: settings.polygonSides ?? 6,
      radius: halfExtent,
      cornerRadius: settings.cornerRadius,
    });
  } else if (settings.type === 'star') {
    path = generateStarPath(center, {
      points: settings.starPoints ?? 5,
      outerRadius: halfExtent,
      innerRadiusRatio: settings.starInnerRadiusRatio ?? 0.5,
    });
  } else {
    // 'arrow' — direction along +x by default; the user can
    // rotate via the Move tool's bounding-box rotation handle.
    path = generateArrowPath({
      from: { x: center.x - halfExtent, y: center.y },
      to: { x: center.x + halfExtent, y: center.y },
      headSize: settings.arrowHeadSize ?? 24,
      headShape: settings.arrowHeadShape ?? 'triangle',
      lineWidth: settings.strokeWidth || 4,
    });
  }
  path.stroke = { color: settings.stroke, width: settings.strokeWidth };
  path.fill = settings.fill;
  return path;
}

/** Per-mode running-status label shown in the AI banner during a
 *  Control / Guided call. The four endpoints have visibly different
 *  runtimes, so the label tells the user which operation is taking
 *  the time. */
const CONTROL_RUNNING_LABEL: Record<ControlMode, string> = {
  'from-sketch': 'Generating from sketch',
  'match-structure': 'Matching structure',
  'match-style': 'Matching style',
  'style-transfer': 'Transferring style',
};

/** Soft cap on adjustment-layer count per document. Each adjustment
 *  owns a full-canvas backing canvas (4K ≈ 64MB), so unbounded
 *  stacking exhausts browser memory without warning. The cap
 *  matches a typical pro-photo retouch envelope; users hitting it
 *  should flatten or split. Surfaced via toast at the creation
 *  site, not enforced in the reducer (the reducer trusts its
 *  caller, matching the rest of the layer-mutation API). */
const MAX_ADJUSTMENT_LAYERS = 16;

/** Maximum PSD file size accepted on import. `file.arrayBuffer()`
 *  allocates the entire file into JS heap; without a hard ceiling
 *  a 1GB PSD would OOM-kill the tab (and on WSL, the whole VM per
 *  CLAUDE.md's resource-constraints note). 500MB covers the vast
 *  majority of real-world PSDs while leaving heap headroom for the
 *  parser's working set (~3x the byte size for materialized layer
 *  canvases). Tier 2 Group F sub-piece #2. */
const MAX_PSD_BYTES = 500 * 1024 * 1024;
/** SVG cap — much smaller than PSD because SVGs are mostly text and
 *  large files (>10MB) usually indicate embedded base64 images that
 *  are better imported as separate raster files. The SVG parser AND
 *  the rasterizer both have their own caps (parser pre-check size +
 *  rasterizer 8192-px dimension limit); this is the outer-most gate. */
const MAX_SVG_BYTES = 20 * 1024 * 1024;

/** New-layer name when Control runs in 'new-layer' output mode. The
 *  per-mode names make the layers panel readable when a user runs
 *  several variations on the same source. */
const CONTROL_LAYER_LABEL: Record<ControlMode, string> = {
  'from-sketch': 'From Sketch',
  'match-structure': 'Match Structure',
  'match-style': 'Match Style',
  'style-transfer': 'Style Transfer',
};

export function EditorPage() {
  const removeBackgroundViaEntity = useImglyRemoveBackground();
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isLandscape = useIsLandscape();
  const [hasImage, setHasImage] = useState(false);
  const layerIdSeqRef = useRef(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [fiasPickerOpen, setFiasPickerOpen] = useState(false);
  // Transparency-checkerboard underlay toggle (the CSS pattern that
  // shows through transparent pixels). Distinct from the measurement
  // grid feature in `state.viewSettings.gridVisible` (Tier 2 Group D
  // workspace UI) — different overlay, different semantics, different
  // toggle. See `components/CanvasOverlays/Grid.tsx`.
  const [transparencyCheckerVisible, setTransparencyCheckerVisible] = useState(false);
  // Tier 2 Group D — Grid Settings modal open state. Lives at
  // EditorPage level so the View dropdown can trigger it.
  const [gridSettingsOpen, setGridSettingsOpen] = useState(false);
  // Tier 2 Group D — New Guide modal. Numeric-input creation path
  // that pairs with the drag-from-ruler workflow (for cases where
  // the user wants an exact scene-space coordinate).
  const [newGuideOpen, setNewGuideOpen] = useState(false);
  // Tier 3 Group D Slice 1c — templates dialog state.
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  // Tier 3 Group D Slice 1d — document-settings dialog state.
  const [documentSettingsOpen, setDocumentSettingsOpen] = useState(false);
  // Canvas-size dialog (Image → Canvas Size…). Anchor-based resize
  // without resampling pixels.
  const [canvasSizeOpen, setCanvasSizeOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  // Tier 3 Group C Phase 3 — History panel (right-edge floating
  // dock). Toggled via the `view.toggle-history-panel` command;
  // hidden by default to avoid eating canvas width for users who
  // don't care to inspect history.
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  // Tier 3 Group C Phase 1 — Command Palette. `commandRegistryRef`
  // is stable across renders so commands registered from effects
  // keep their slot; `paletteOpen` drives both the modal and a gate
  // in the legacy keydown switch (suppresses editor shortcuts while
  // the palette is consuming keystrokes).
  const commandRegistryRef = useRef<CommandRegistry>(null as unknown as CommandRegistry);
  if (commandRegistryRef.current === null) {
    commandRegistryRef.current = new CommandRegistry();
  }
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [keyboardSettingsOpen, setKeyboardSettingsOpen] = useState(false);
  const isMac = useMemo(() => isMacPlatform(), []);
  // Adjustments modal — `activeAdjustmentKind` drives which kind's
  // panel is mounted inside `AdjustmentsModal`; `null` keeps the
  // dialog closed. The modal itself is generic across kinds and
  // looks each one up in the spec registry. `adjustmentSessionId`
  // bumps on every open so the modal can remount its inner body
  // even when the user re-opens the same kind back-to-back —
  // forces `useState` initializers to re-run with fresh state.
  // The body captures the source ImageData itself in a post-mount
  // effect so the (potentially multi-MB) `getImageData` doesn't
  // block the modal from appearing.
  //
  // `adjustmentTargets` holds an entry per layer the adjustment
  // writes into — single-element for a non-group active layer,
  // multi-element when the active layer is a group (one entry per
  // rasterizable descendant). Each entry: the live paint canvas
  // (what the spec writes into) + the wrapping Fabric object (what
  // we mark `dirty` so the next render re-reads the modified
  // pixels). Resolved at open-time via `getActiveAdjustmentTargets`.
  const [activeAdjustmentKind, setActiveAdjustmentKind] = useState<AdjustmentKind | null>(null);
  const [adjustmentSessionId, setAdjustmentSessionId] = useState(0);
  const [adjustmentTargets, setAdjustmentTargets] = useState<
    Array<{ canvas: HTMLCanvasElement; fabricObj: fabric.FabricObject }>
  >([]);
  // Non-destructive edit mode: tracks the adjustment-layer node
  // currently open in the modal (id + its config at open-time, used
  // for Cancel-reverts). Null when the modal is opened in
  // destructive mode (or closed entirely). Set / cleared together
  // with `activeAdjustmentKind` so the two stay aligned.
  const [editingAdjustment, setEditingAdjustment] = useState<{
    layerId: string;
    original: AdjustmentConfig;
  } | null>(null);
  // AI tool state — banner + per-capability prompts. Each capability
  // gets its own prompt field so switching between Smart Create and
  // Inpaint doesn't clobber what the user has typed.
  const [aiStatus, setAiStatus] = useState<AIStatus>({ kind: 'idle' });

  // Tier 1 Group D — Selection refinement modals. Color Range and
  // Refine Edge are both modal-shaped (one a small dialog, the
  // other a full-screen workspace), opened from the StatusBar
  // chip popover and from the MarqueePanel.
  const [colorRangeOpen, setColorRangeOpen] = useState(false);
  const [refineEdgeOpen, setRefineEdgeOpen] = useState(false);
  // Liquify — Tier 1 Group E final piece. Same shape as Refine
  // Edge: the workspace owns its own working state, we just snap
  // the source at open time and route the apply-result back into
  // the active layer.
  const [liquifyOpen, setLiquifyOpen] = useState(false);
  const [liquifySource, setLiquifySource] = useState<HTMLCanvasElement | null>(null);
  // Rasterize-before-warp prompt — Distort / Perspective / Warp
  // need a FabricImage target; clicking one of those chips while a
  // vector / shape / text layer is active opens this dialog. The
  // pending mode is what we'll switch to after the user accepts
  // the rasterize. Null = dialog closed.
  const [pendingWarpMode, setPendingWarpMode] = useState<'distort' | 'perspective' | 'warp' | null>(
    null,
  );
  // Rasterize-before-paint prompt — brush / eraser / retouch / fill /
  // AI on a shape / text / vector layer opens this dialog. Set by
  // the canvas hook's `onRasterizeBeforePaintNeeded`; cleared on
  // user confirm / cancel below. The paint op that triggered the
  // prompt was aborted (`ensurePaintSurface` returned null); after
  // confirm the user redoes their action against the now-raster
  // layer.
  const [pendingRasterizeBeforePaint, setPendingRasterizeBeforePaint] = useState<{
    layerId: string;
    sourceType: 'shape' | 'text' | 'vector';
    action: 'brush' | 'eraser' | 'retouch' | 'fill' | 'ai';
  } | null>(null);
  /** Source canvas snapshot for the Color Range modal and Refine
   *  Edge workspace, taken at open time so the preview is stable
   *  while the user works. Stored as a canvas (NOT ImageData) —
   *  see ColorRangeDialog's comment for the perf rationale (4K
   *  ImageData is ~32MB; passing canvases lets consumers drawImage
   *  cheaply and getImageData lazily only where needed). */
  const [sourceCanvas, setSourceCanvas] = useState<HTMLCanvasElement | null>(null);
  const [smartCreatePrompt, setSmartCreatePrompt] = useState('');
  const [smartCreateInsertModel, setSmartCreateInsertModel] =
    useState<SmartCreateInsertModel>('flux-2-pro');
  // Inpaint (mask + prompt). Used to be Smart Create's "Blend" mode;
  // now its own picker entry per the AI tool restructuring. The feather
  // ratio drives the post-process edge blend — 0 = hard edges, 0.2 =
  // very soft (FEATHER_RADIUS_RATIO_DEFAULT = 5%).
  //
  // `engine` picks the provider — Stability (cheapest, ~4 credits) /
  // Recraft (purpose-built for replacement, ~5 credits) / OpenAI
  // gpt-image-1 (best text rendering, ~5 credits). `negativePrompt`
  // is forwarded to engines that support it (Stability); ignored by
  // others. `growMaskPx` dilates the mask outward server-side.
  const [inpaintPrompt, setInpaintPrompt] = useState('');
  const [inpaintNegativePrompt, setInpaintNegativePrompt] = useState('');
  const [inpaintEngine, setInpaintEngine] = useState<InpaintEngine>('stability');
  const [inpaintGrowMaskPx, setInpaintGrowMaskPx] = useState<number>(0);
  const [inpaintFeatherRatio, setInpaintFeatherRatio] = useState<number>(
    FEATHER_RADIUS_RATIO_DEFAULT,
  );
  // Engine-conditional style preset. Empty string = engine default.
  // Reset on engine change because the valid value sets are disjoint
  // (Stability's 11 presets vs. Recraft's 4 modes); a stale value from
  // the other engine would either get rejected upstream or silently
  // drop. The reset wrapper lives below.
  const [inpaintStylePreset, setInpaintStylePreset] = useState<string>('');
  const handleInpaintEngineChange = useCallback((engine: InpaintEngine) => {
    setInpaintEngine(engine);
    setInpaintStylePreset('');
  }, []);
  // Smart Delete (paid Stability erase, escalation from the free
  // MI-GAN-based Free Delete). `growMask` dilates the mask outward
  // server-side before Stability's erase model runs — useful when the
  // marquee just barely catches the object. `feather` softens the
  // boundary on composite (same post-process as Inpaint).
  const [eraseGrowMaskPx, setEraseGrowMaskPx] = useState<number>(0);
  const [eraseFeatherRatio, setEraseFeatherRatio] = useState<number>(FEATHER_RADIUS_RATIO_DEFAULT);
  // Search & Replace (Stability auto-segments via `searchPrompt` and
  // replaces with content matching `searchReplacePrompt`). No marquee
  // required. `growMask` cleans up edge halos when Stability's
  // segmentation just misses an edge.
  const [searchReplaceSearchPrompt, setSearchReplaceSearchPrompt] = useState('');
  const [searchReplacePrompt, setSearchReplacePrompt] = useState('');
  const [searchReplaceNegativePrompt, setSearchReplaceNegativePrompt] = useState('');
  const [searchReplaceGrowMaskPx, setSearchReplaceGrowMaskPx] = useState<number>(0);
  // Optional Stability style preset (empty = engine default) and seed
  // (null = engine random). Seed is `number | null` so a blank input
  // doesn't get sent as `0` and lock the user into a specific seed.
  const [searchReplaceStylePreset, setSearchReplaceStylePreset] = useState<string>('');
  const [searchReplaceSeed, setSearchReplaceSeed] = useState<number | null>(null);
  // Search & Recolor (Stability auto-segments via `selectPrompt` and
  // recolors with the color/effect in `prompt`). Same pattern as Search
  // & Replace, just preserves shape.
  const [searchRecolorSelectPrompt, setSearchRecolorSelectPrompt] = useState('');
  const [searchRecolorPrompt, setSearchRecolorPrompt] = useState('');
  const [searchRecolorNegativePrompt, setSearchRecolorNegativePrompt] = useState('');
  const [searchRecolorGrowMaskPx, setSearchRecolorGrowMaskPx] = useState<number>(0);
  const [searchRecolorStylePreset, setSearchRecolorStylePreset] = useState<string>('');
  const [searchRecolorSeed, setSearchRecolorSeed] = useState<number | null>(null);
  // Outpaint extends the canvas in any of 4 directions. ≥1 must be > 0.
  // Optional prompt guides the new content; creativity 0..1 controls
  // adherence to the source (default 0.5).
  const [outpaintLeftPx, setOutpaintLeftPx] = useState<number>(0);
  const [outpaintRightPx, setOutpaintRightPx] = useState<number>(0);
  const [outpaintUpPx, setOutpaintUpPx] = useState<number>(0);
  const [outpaintDownPx, setOutpaintDownPx] = useState<number>(0);
  const [outpaintPrompt, setOutpaintPrompt] = useState('');
  const [outpaintCreativity, setOutpaintCreativity] = useState<number>(0.5);
  const [outpaintNegativePrompt, setOutpaintNegativePrompt] = useState('');
  const [outpaintStylePreset, setOutpaintStylePreset] = useState<string>('');
  const [outpaintSeed, setOutpaintSeed] = useState<number | null>(null);
  // Image to Image — strength-based reimagining via Stability's
  // generate/* endpoints in image-to-image mode. `strength` follows
  // Stability's convention (0 = identical, 1 = ignore source); 0.35
  // is a moderate redraw default that keeps the source recognizable.
  const [i2iPrompt, setI2iPrompt] = useState('');
  const [i2iEngine, setI2iEngine] = useState<ImageToImageEngine>('core');
  const [i2iStrength, setI2iStrength] = useState<number>(0.35);
  const [i2iNegativePrompt, setI2iNegativePrompt] = useState('');
  const [i2iStylePreset, setI2iStylePreset] = useState<string>('');
  const [i2iSeed, setI2iSeed] = useState<number | null>(null);
  // Default to non-destructive — the new layer is easy to compare with
  // the original or discard by deleting it. Users can switch to
  // 'replace' for iterative refinement on the same target.
  const [i2iMode, setI2iMode] = useState<EditorOutputMode>('new-layer');
  // Replace Background — Recraft default (cheap, prompt-only); Stability
  // upsell for photoreal compositing with relighting and reference-image
  // BGs. Default mode is 'new-layer' for the same A/B comparison reasons
  // as I2I.
  const [replaceBgEngine, setReplaceBgEngine] = useState<ReplaceBackgroundEngine>('recraft');
  const [replaceBgPrompt, setReplaceBgPrompt] = useState('');
  const [replaceBgReferenceBlob, setReplaceBgReferenceBlob] = useState<Blob | null>(null);
  const [replaceBgLightReferenceBlob, setReplaceBgLightReferenceBlob] = useState<Blob | null>(null);
  const [replaceBgLightDirection, setReplaceBgLightDirection] =
    useState<ReplaceBackgroundLightDirection>('none');
  const [replaceBgLightStrength, setReplaceBgLightStrength] = useState<number>(0.7);
  // Stability relights the subject as part of every call — there's no
  // way to skip it server-side. `preserveSubject` near 1.0 dampens the
  // relight effect, so we default to 0.7 (faithful original) instead of
  // 0.5. Users wanting a more dramatic relight can pull the slider down.
  const [replaceBgPreserveSubject, setReplaceBgPreserveSubject] = useState<number>(0.7);
  const [replaceBgPreserveOriginalBg, setReplaceBgPreserveOriginalBg] = useState<number>(0);
  const [replaceBgStyle, setReplaceBgStyle] = useState<ReplaceBackgroundRecraftStyle | ''>('');
  const [replaceBgSeed, setReplaceBgSeed] = useState<number | null>(null);
  const [replaceBgMode, setReplaceBgMode] = useState<EditorOutputMode>('new-layer');
  // Upscale — three modes. Default to fast/Stability/4× because that's
  // the cheapest and most predictable starting point. Engine state
  // persists across mode switches so a user who picked Recraft once
  // doesn't lose their choice when they bounce through HQ (which forces
  // Stability) and back. Output stays non-destructive by default.
  const [upscaleMode, setUpscaleMode] = useState<UpscaleMode>('fast');
  const [upscaleEngine, setUpscaleEngine] = useState<UpscaleEngine>('stability');
  const [upscalePrompt, setUpscalePrompt] = useState<string>('');
  // Default 0.25 — sits inside both engines' valid ranges:
  //   • Stability conservative (HQ): requires 0.2..0.35
  //   • Stability creative (reimagine): accepts 0..0.35
  // Earlier we shipped 0.15 which was below conservative's 0.2 floor
  // and made every HQ call fail server-side.
  const [upscaleCreativity, setUpscaleCreativity] = useState<number>(0.25);
  const [upscaleFactor, setUpscaleFactor] = useState<UpscaleFactor>(4);
  const [upscaleSeed, setUpscaleSeed] = useState<number | null>(null);
  const [upscaleOutputMode, setUpscaleOutputMode] = useState<EditorOutputMode>('new-layer');
  // Vectorize keeps the most recent SVG result in memory so a
  // cancelled save dialog (or a deliberate "Save again" click) can
  // re-open the file picker without paying for the Recraft call
  // twice. `vectorizeStatus` drives the panel's idle / saved /
  // cancelled view; `vectorizeSavedFileName` is shown back to the
  // user as confirmation after a successful save.
  const [vectorizeStatus, setVectorizeStatus] = useState<'idle' | 'saved' | 'cancelled'>('idle');
  // SVG result kept in a ref so re-renders during the save flow
  // don't drop it. State + ref pair: state drives the panel view;
  // ref holds the raw bytes for the dialog and "Save again".
  const vectorizeSvgRef = useRef<string | null>(null);
  const [vectorizeSavedFileName, setVectorizeSavedFileName] = useState<string | null>(null);
  // The in-editor save-as dialog opens after a successful vectorize
  // and again whenever the user clicks "Save again". Local/Fias
  // destination is sticky across both opens — the user's last pick
  // stays selected.
  const [vectorizeSaveDialogOpen, setVectorizeSaveDialogOpen] = useState(false);
  const [vectorizeSaveDialogDestination, setVectorizeSaveDialogDestination] =
    useState<VectorizeSaveDestination>('local');
  // Control / Guided — four Stability v2beta `/control/*` endpoints
  // surfaced as modes. Default to `match-structure` (most general-
  // purpose: works on any image with a clear subject), `new-layer`
  // output for non-destructive A/B. Slider defaults match Stability's
  // documented sweet spots: 0.7 control strength, 0.5 fidelity for
  // match-style, 0.5/0.9/0.5 for the style-transfer trio.
  const [controlMode, setControlMode] = useState<ControlMode>('match-structure');
  const [controlPrompt, setControlPrompt] = useState<string>('');
  const [controlNegativePrompt, setControlNegativePrompt] = useState<string>('');
  const [controlStrength, setControlStrength] = useState<number>(0.7);
  const [controlFidelity, setControlFidelity] = useState<number>(0.5);
  const [controlStyleReferenceBlob, setControlStyleReferenceBlob] = useState<Blob | null>(null);
  const [controlStyleStrength, setControlStyleStrength] = useState<number>(0.5);
  const [controlCompositionFidelity, setControlCompositionFidelity] = useState<number>(0.9);
  const [controlChangeStrength, setControlChangeStrength] = useState<number>(0.5);
  const [controlSeed, setControlSeed] = useState<number | null>(null);
  const [controlOutputMode, setControlOutputMode] = useState<EditorOutputMode>('new-layer');
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [removeBackgroundError, setRemoveBackgroundError] = useState<string | null>(null);
  // Remove-background engine — defaults to the free on-device imgly path.
  // Stability / Recraft route through the paid backend (see
  // smart-remove-background.ts).
  const [removeBackgroundEngine, setRemoveBackgroundEngine] =
    useState<RemoveBackgroundEngine>('imgly');

  // Close-confirmation flow. When a dirty tab's X is clicked, the editor
  // first switches to that tab (so its content is on the canvas) and shows
  // the dialog. The Save / Save-As paths run through ExportDialog so the
  // close happens AFTER the export resolves.
  const [confirmCloseTabId, setConfirmCloseTabId] = useState<string | null>(null);
  const [isQuickSaving, setIsQuickSaving] = useState(false);
  const [exportDialogDestination, setExportDialogDestination] = useState<SaveDestination>('local');
  /** User-facing error message from the most recent save attempt, surfaced
   *  in the close-confirm dialog so the user can react to FSA permission
   *  revocations, network failures, quota exceeded, etc. instead of seeing
   *  the dialog hang. */
  const [saveError, setSaveError] = useState<string | null>(null);
  // When non-null, the currently-mounted ExportDialog should close this tab
  // after a successful export. Captured at the moment Save-As is clicked so
  // it survives the dialog closing on cancel without leaking into the next
  // unrelated export.
  const closeAfterExportTabIdRef = useRef<string | null>(null);

  const {
    state,
    setActiveTool,
    setSelectedAITool,
    setAIPickerOpen,
    setQuickMaskActive,
    setQuickMaskMode,
    beginTransform,
    setTransformMode,
    convertLayerToPaint,
    endTransform,
    updateBrushSettings,
    updateTextSettings,
    updateShapeSettings,
    updateMarqueeSettings,
    updateRetouchSettings,
    updatePenSettings,
    beginPathEdit,
    setPathEditAnchor,
    endPathEdit,
    setVectorPath,
    setZoom,
    setCanvasDimensions,
    setDpi,
    setSelection,
    setFileInfo,
    markDirty,
    markClean,
    togglePropertiesPanel,
    toggleLayersPanel,
    addLayer,
    addAdjustment,
    updateAdjustment,
    removeLayer,
    renameLayer,
    toggleLayerVisibility,
    setLayerOpacity,
    setLayerBlendMode,
    setLayerEffects,
    setLayerRasterModified,
    setLayerMask,
    setMaskVectorPath,
    convertMaskKind,
    setTextPath,
    setEditTarget,
    groupLayers,
    ungroup,
    moveNodeInTree,
    setActiveLayer,
    setAutoSelectLayers,
    reorderLayer,
    resetLayers,
    resetLayerDocument,
    updateViewSettings,
    addGuide,
    moveGuide,
    removeGuide,
    clearGuides,
    resetViewSettings,
    addSlice: addSliceRaw,
    updateSlice: updateSliceRaw,
    removeSlice: removeSliceRaw,
    clearSlices: clearSlicesRaw,
    resetSlices,
    shiftSlices,
  } = useImageEditorState();

  // Ref shadow of `state.document` for callbacks / rAF closures
  // that need the LATEST tree, not the one captured at their
  // declaration time. Two consumers today:
  //   1. `handleEditAdjustmentLayer` (passed to LayersPanel) reads
  //      it so its identity stays stable across renders — keeps the
  //      LayerRow `React.memo` from busting every slider tick.
  //   2. The rAF-coalesced slow-path effect reads it from the
  //      scheduled tick (not from effect-time closure) so rapid
  //      `state.document` changes all collapse into one composite
  //      pass against the freshest state.
  //
  // Synced via `useEffect` rather than a render-phase assignment so
  // the ref update obeys React's commit phase — important under
  // StrictMode's double-render and any future concurrent-rendering
  // codepath, where a render-time write could briefly point the ref
  // at a snapshot that React then discards.
  const documentRef = useRef(state.document);
  useEffect(() => {
    documentRef.current = state.document;
  }, [state.document]);

  // Tier 1: the history extra captures the full `document` so
  // group/mask metadata (parentId, childIds, mask) survives undo.
  // Older Tier 0 snapshots persist via `layers` only — the set
  // callback below tolerates either shape so legacy IDB sessions
  // keep working.
  interface LayersHistoryExtra {
    document?: EditorDocument;
    layers?: Layer[];
    activeLayerId: string | null;
    /** Tier 2 Group F sub-piece #5 — slice geometry mirrored into
     *  the history extra so add / move / resize / delete on the
     *  Slice tool participate in undo/redo. Optional for back-
     *  compat: history entries persisted before the Slice tool
     *  shipped will have `slices: undefined` and the restore path
     *  leaves the current array in place (reducer default `[]`). */
    slices?: SliceRect[];
  }
  const history = useCanvasHistory<LayersHistoryExtra>({
    maxHistory: 50,
    // Recurring "background gone after undo" diagnostic — fires at
    // snapshot time, not at undo time, so the offending handler is on
    // the call stack when the bad snapshot is created. The check is
    // simple and conservative: if the canvas has FabricImages but the
    // React-side layers list is empty, the snapshot is doomed (the next
    // undo would `resetLayers([])` and erase every layer entry from the
    // panel even though the canvas still draws the pixels). Returns
    // `null` on the happy path so this is silent in production.
    validateSnapshot: ({ canvasJSON, extra }) => {
      const objects = (canvasJSON as { objects?: Array<{ type?: string }> }).objects ?? [];
      // Fabric v7 serializes FabricImage as `type: "Image"`; tolerate the
      // legacy lower-case form too in case any older snapshot survives.
      const fabricImages = objects.filter((o) => o.type === 'Image' || o.type === 'image');
      const layerCount = extra?.document?.rootIds.length ?? extra?.layers?.length ?? 0;
      if (fabricImages.length > 0 && layerCount === 0) {
        return `canvas has ${fabricImages.length} FabricImage(s) but layers list is empty — undo from this snapshot would wipe the Background layer entry`;
      }
      return null;
    },
  });

  // Refs that always hold the latest layer state so the history hook's
  // get-extra callback can be registered once and read fresh values.
  const layersStateRef = useRef<LayersHistoryExtra>({
    document: state.document,
    activeLayerId: state.activeLayerId,
    slices: state.slices,
  });
  layersStateRef.current = {
    document: state.document,
    activeLayerId: state.activeLayerId,
    slices: state.slices,
  };

  // Tracks "the user just selected this group via the LayersPanel".
  // Set synchronously in `handleSelectLayer` for group rows; the
  // `onLayerSelected` callback (which Fabric fires synchronously
  // when we then multi-select the group's descendants on the canvas)
  // reads this ref to know it should ignore the descendant
  // selections and keep the group as the active layer. Cleared on
  // any non-group selection, when a canvas selection lands outside
  // the tracked group, or when activeLayerId drifts away from the
  // tracked group via any other path (group deleted, programmatic
  // setActiveLayer from elsewhere, etc.) via the effect below.
  const programmaticGroupSelectionRef = useRef<string | null>(null);

  useEffect(() => {
    history.setExtraStateAccess({
      get: () => layersStateRef.current,
      set: (extra) => {
        // Prefer the v2 (document) shape. Tier 0 sessions in IDB
        // stored only `layers`; the legacy branch promotes via
        // resetLayers (which routes through fromFlatLayers).
        if (extra.document) {
          resetLayerDocument(extra.document, extra.activeLayerId);
        } else if (extra.layers) {
          resetLayers(extra.layers, extra.activeLayerId);
        }
        // Tier 2 Group F sub-piece #5 — restore slice geometry
        // alongside the layer tree so undo/redo covers slice-tool
        // edits too. Legacy history entries without `slices` field
        // leave the current slice array in place (the entry pre-
        // dated the Slice tool); honor the explicit `[]` from
        // newer entries (a user undo to a state where they had no
        // slices yet should clear the panel).
        if (extra.slices !== undefined) {
          resetSlices(extra.slices);
        }
      },
    });
    return () => history.setExtraStateAccess(null);
  }, [history, resetLayers, resetLayerDocument, resetSlices]);

  // Set when a user-action handler changes layer state — useEffect below
  // captures the post-commit snapshot so the saved entry reflects the new state.
  const layerSnapshotPendingRef = useRef(false);
  // Optional per-action label for the deferred snapshot. Handlers
  // that know what they did (Add Layer, Delete Layer, Toggle
  // Visibility, etc.) set this alongside the pending ref so the
  // History panel reads the operation's name instead of falling
  // back to the active-tool resolver. Null = use the resolver
  // chain. Cleared by the snapshot effect after each snapshot.
  const layerSnapshotLabelRef = useRef<string | null>(null);
  // Sibling ref for slider-style "continuous" mutations (opacity, effect
  // params, filter sliders). Each tick of a slider drag would otherwise
  // produce its own history snapshot, exhausting the 50-step undo cap in
  // a single drag. The snapshot effect debounces this ref's commits by
  // SLIDER_SNAPSHOT_DEBOUNCE_MS so a slider drag becomes one history
  // entry. `markDirty()` still fires immediately so the tab `*` indicator
  // stays responsive. Discrete operations (toggles, delete, reorder)
  // continue to use the regular ref above and snapshot immediately.
  const layerSnapshotContinuousRef = useRef(false);
  const layerSnapshotContinuousLabelRef = useRef<string | null>(null);
  // Sibling ref for the post-loadFile snapshot — same commit-deferred
  // capture, but skips `markDirty()` so a freshly-opened file doesn't
  // appear modified. Without this, opening a file would unconditionally
  // flag the new tab as dirty (and prompt to save on close).
  const cleanSnapshotPendingRef = useRef(false);
  const cleanSnapshotLabelRef = useRef<string | null>(null);
  // Debounce timer for the continuous-ref path. Cleared on every new
  // continuous tick (keeps the slider drag in one entry), and flushed
  // ahead of any discrete op or undo so history order stays correct.
  const continuousDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Window the continuous debounce coalesces over. ~half a second feels
  // like the natural "I'm done dragging" pause to the user without
  // making undo wait noticeably after a slider release.
  const SLIDER_SNAPSHOT_DEBOUNCE_MS = 500;

  // Latched true between any pointerdown in the editor and the matching
  // pointerup / pointercancel. The debounced IDB session save reads this
  // and reschedules itself instead of firing while a stroke is in flight
  // (see the save effect for the full rationale).
  const interactionInFlightRef = useRef(false);

  // Tier 2 Group F sub-piece #5 — slice helpers wrap the raw
  // reducer dispatches with `layerSnapshotPendingRef.current = true`
  // so the post-commit snapshot effect fires and slice tool edits
  // participate in undo/redo. ADD/UPDATE/REMOVE/CLEAR each commit
  // as one discrete history entry; the panel + canvas already
  // coalesce continuous drags into single dispatches (Fabric fires
  // `object:modified` once per release, not per move tick), so the
  // pending-ref path (rather than the slider-continuous path) is
  // the right primitive.
  const addSlice = useCallback(
    (rect: { x: number; y: number; width: number; height: number; name?: string }) => {
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Add slice';
      addSliceRaw(rect);
    },
    [addSliceRaw],
  );
  const updateSlice = useCallback(
    (id: string, patch: Partial<Omit<SliceRect, 'id'>>) => {
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Update slice';
      updateSliceRaw(id, patch);
    },
    [updateSliceRaw],
  );
  const removeSlice = useCallback(
    (id: string) => {
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Remove slice';
      removeSliceRaw(id);
    },
    [removeSliceRaw],
  );
  const clearSlices = useCallback(() => {
    layerSnapshotPendingRef.current = true;
    layerSnapshotLabelRef.current = 'Clear slices';
    clearSlicesRaw();
  }, [clearSlicesRaw]);

  // While Free Transform is active, suppress the per-`object:modified`
  // snapshot save. The transform session is one "atomic" operation
  // from the user's perspective: their drag should end with EITHER
  // a single Apply-saved snapshot OR a clean Cancel that leaves no
  // history trace. Without this gate, each mouse:up during a drag
  // would saveSnapshot, and Cancel would have to roll back through
  // multiple history entries to get back to pre-transform.
  const transformSuppressSnapshotRef = useRef(false);

  // Throttle the "vector mask, can't paint" toast. The canvas hook
  // fires `onMaskPaintBlocked` once per blocked stroke; a rapid-
  // fire stroke burst would otherwise stack identical toasts.
  // Stored on a ref (not state) so the threshold check inside the
  // callback doesn't trigger a re-render. Tier 2 Group A PR-D.
  const maskPaintBlockedToastAtRef = useRef(0);

  // Same throttle pattern for the marquee-bound "no pixels affected"
  // toasts (brush/eraser/cut/copy/delete/fill). A rapid retry of the
  // same action would otherwise stack the queue. 2-second window —
  // long enough that two consecutive failures look like one toast,
  // short enough that the user gets timely feedback if they try a
  // different operation right after.
  const marqueeNoPixelsToastAtRef = useRef(0);

  // Last timestamp at which a pointer interaction touched the page.
  // Updated on pointerdown/up and on pointermove while a pointer is
  // down. The autosave gate uses this to keep the (potentially
  // 400ms-blocking) `canvas.toJSON()` from running while the user is
  // actively dragging — even when the current pointer flag is briefly
  // false because the previous gesture ended a few ms ago and the
  // next one hasn't started yet. Without this, the save's
  // serialization can land between a marquee mouse:up and the next
  // mouse:down, hogging the JS thread long enough to queue the next
  // pointerdown — the user-visible "pause every ~800ms" diagnosed
  // by recording console timing around the save path.
  const lastPointerActivityAtRef = useRef(0);

  // Tier 2 Group F — ref to the rasterModified dispatcher. The canvas
  // hook's `onLayerRasterModified` callback closes over an earlier
  // render's `setLayerRasterModified`; routing through this ref lets
  // it always see the current dispatcher. Mirrors the
  // `setExtraStateAccess` pattern used elsewhere in this file.
  const setLayerRasterModifiedRef = useRef<((id: string, modified: boolean) => void) | null>(null);
  setLayerRasterModifiedRef.current = setLayerRasterModified;

  // Same pattern: the canvas hook's `onLayerRasterModified` callback
  // routes through this ref so a future re-render's
  // `convertLayerToPaint` is always picked up, even though the current
  // useCallback is empty-deps-stable.
  const convertLayerToPaintRef = useRef<((id: string) => void) | null>(null);
  convertLayerToPaintRef.current = convertLayerToPaint;

  // Vector-mask sync (Tier 2 Group A PR-D) diff tracker. Maps
  // layerId → last-pushed VectorPath reference. The effect below
  // skips a re-rasterize when the current path is the same
  // reference. `handleAddVectorMaskToLayer` writes here on
  // creation so the post-create render tick doesn't queue a
  // redundant rasterize against a RAW that already matches.
  // Hoisted to component top so creation handlers (defined above
  // the effect) can access it without crossing the TDZ.
  const lastPushedMaskVectorPathRef = useRef<Map<string, VectorPath>>(new Map());

  const canvas = useFabricCanvas({
    canvasElRef,
    wrapperRef,
    onObjectModified: () => {
      markDirty();
      if (transformSuppressSnapshotRef.current) return;
      // Pick a label from the active layer's type so direct Fabric
      // mutations (drag, resize, text-commit, path-anchor edits)
      // read as "Text edit" / "Path edit" instead of falling back to
      // the active-tool resolver — which would say "Move" when a
      // user double-clicks a text layer with the Move tool active.
      const activeId = state.activeLayerId;
      const node = activeId ? state.document.layers[activeId] : null;
      let label: string | undefined;
      if (node?.type === 'text') label = 'Text edit';
      else if (node?.type === 'vector') label = 'Path edit';
      history.saveSnapshot(label ? { label } : undefined);
    },
    onSelectionChanged: setSelection,
    onLayerSelected: (layerId) => {
      // Sync the panel highlight to the canvas selection. We don't clear the
      // active layer when canvas-selection clears (matches Photoshop).
      if (!layerId) return;

      // Group-selection guard: when the user clicks a group row in
      // LayersPanel, `handleSelectLayer` programmatically multi-selects
      // every descendant's Fabric object so drag moves the whole group
      // together. Fabric's selection event then fires this callback
      // synchronously (before React commits the parent's setActiveLayer)
      // with the first descendant's id. We need a synchronous signal —
      // not the stale React state — to know "the user just selected
      // group G; ignore subsequent descendant selections".
      //
      // `programmaticGroupSelectionRef` is set inside `handleSelectLayer`
      // for groups and cleared on any non-group selection path. When set,
      // we ignore selections that are descendants of the tracked group.
      const programmaticGroupId = programmaticGroupSelectionRef.current;
      const doc = layersStateRef.current.document;
      if (programmaticGroupId && doc) {
        let parentId: string | null = doc.layers[layerId]?.parentId ?? null;
        while (parentId) {
          if (parentId === programmaticGroupId) return; // descendant — keep the group
          parentId = doc.layers[parentId]?.parentId ?? null;
        }
        // Not a descendant — the user has navigated away from the
        // group selection, so the guard no longer applies. Fall
        // through and let the regular flow update activeLayer.
        programmaticGroupSelectionRef.current = null;
      }
      setActiveLayer(layerId);
    },
    onShapeToolClick: (point) => {
      // User clicked empty canvas with the Shape tool active.
      // Vector primitives (polygon / star / arrow) create a real
      // vector layer using `lib/shape-generators`; legacy
      // primitives (rect / circle / line / triangle) continue
      // through the Fabric-primitive `addShape` path.
      const s = state.shapeSettings;
      if (s.type === 'polygon' || s.type === 'star' || s.type === 'arrow') {
        const base = makeLayer('vector');
        const path = generateVectorShapePath(s, point);
        const layer = {
          ...base,
          path,
          fabricObjectId: base.id,
          vectorSource: { kind: s.type },
        };
        addLayer(layer, true);
        canvas.addVectorObject(layer.id, path);
        layerSnapshotPendingRef.current = true;
        layerSnapshotLabelRef.current = `Add ${s.type}`;
        return;
      }
      const layer = makeLayer('shape');
      addLayer(layer, true);
      canvas.addShape(state.shapeSettings.type, state.shapeSettings, layer.id, point);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = `Add ${state.shapeSettings.type}`;
    },
    onTextToolClick: (point) => {
      // Same behavior for the Text tool.
      const layer = makeLayer('text');
      addLayer(layer, true);
      canvas.addText('add text', state.textSettings, layer.id, point);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Add text';
    },
    onPenToolFinalize: (path) => {
      // Pen tool fired Enter / double-click / close-on-first-anchor.
      // Create a vector LayerNode carrying the path, dispatch
      // ADD_LAYER, and mount the persistent Fabric.Path tagged with
      // the new layer's id. The hook's preview is already torn down
      // by the time this fires.
      const base = makeLayer('vector');
      const layer = { ...base, path, fabricObjectId: base.id };
      addLayer(layer, true);
      canvas.addVectorObject(layer.id, path);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Add path';
    },
    onPathEditSelectionChanged: (anchorIndex) => {
      // Mirror canvas-side selection changes (anchor click) into
      // the reducer + into the type-mirror state so the
      // EditPathPanel re-renders with the new anchor's type
      // pre-selected on the radio.
      setPathEditAnchor(anchorIndex);
      // `canvas` was assigned just above this hook call; reading
      // its `getPathEditSelectedAnchorType` is safe inside this
      // callback because it only fires after the user clicks.
      setPathEditSelectedAnchorTypeState(canvas.getPathEditSelectedAnchorType());
    },
    onCanvasDimensions: setCanvasDimensions,
    onPathCreated: () => {
      markDirty();
      history.saveSnapshot();
      // A destructive paint stroke mutated the source layer's
      // pixels. If there are non-destructive adjustment layers
      // above this one, their backings are now stale (they hold
      // pre-stroke adjusted pixels). Without re-deriving them, the
      // user sees the new stroke covered by the old adjustment —
      // i.e., the stroke "disappears." Run the slow-path
      // synchronously so the next paint reflects the new stroke.
      // No-ops when there are no adjustment layers
      // (`requiresCustomComposite` returns false) so the
      // common-case Tier-0 paint flow pays nothing.
      runSlowPathCompositeSync();
    },
    onZoomChanged: setZoom,
    onCloneSourceChanged: (point) => {
      setCloneSourcePoint(point);
      // The canvas auto-clears armed when a source is set via tap;
      // mirror that here so the panel button reverts.
      if (point) setCloneArmed(false);
    },
    onCloneStrokeRejected: () => {
      // The RetouchPanel's "Source: not set" text is the primary
      // signal to the user; no additional UI noise needed here.
    },
    onMaskPaintBlocked: () => {
      // Throttle to one toast per ~3 seconds so a rapid-fire
      // stroke burst doesn't stack the queue. The user only needs
      // to be told once — they'll either convert to raster or
      // give up.
      const now = Date.now();
      if (now - maskPaintBlockedToastAtRef.current < 3000) return;
      maskPaintBlockedToastAtRef.current = now;
      toast({
        title: 'Vector mask',
        description: 'Paint and erase strokes are blocked. Convert to a raster mask first.',
      });
    },
    onMarqueeActionNoPixels: (action) => {
      // 2-second throttle (see marqueeNoPixelsToastAtRef declaration).
      const now = Date.now();
      if (now - marqueeNoPixelsToastAtRef.current < 2000) return;
      marqueeNoPixelsToastAtRef.current = now;
      const description = (() => {
        switch (action) {
          case 'brush':
            return 'Brush stroke fell outside the selection.';
          case 'eraser':
            return 'Eraser stroke fell outside the selection.';
          case 'cut':
            return 'Nothing to cut — the selection is empty on this layer.';
          case 'copy':
            return 'Nothing to copy — the selection is empty on this layer.';
          case 'delete':
            return 'Nothing to delete — the selection is empty on this layer.';
          case 'fill':
            return 'Nothing to fill — click landed outside the selection.';
        }
      })();
      toast({ title: 'No pixels affected', description });
    },
    // Tier 2 Group F — paint / eraser strokes that bake onto the
    // layer's pixel surface flip the rasterModified flag. The
    // reducer is a no-op when the flag is already true, so a long
    // stroke burst doesn't spam history snapshots.
    //
    // Also flips a still-`empty` layer node to `paint` on the first
    // bake so the Adjust / Filter pickers enable. Shape / text /
    // vector layers do NOT auto-convert here — they require the
    // rasterize-before-paint dialog (wired via
    // `onRasterizeBeforePaintNeeded` below) so the user explicitly
    // consents to losing the layer's editable structure.
    onLayerRasterModified: (layerId) => {
      setLayerRasterModifiedRef.current?.(layerId, true);
      const node = documentRef.current.layers[layerId];
      if (node?.type === 'empty') {
        convertLayerToPaintRef.current?.(layerId);
      }
    },
    getLayerType: (layerId) => documentRef.current.layers[layerId]?.type,
    onRasterizeBeforePaintNeeded: (info) => {
      setPendingRasterizeBeforePaint(info);
    },
    onLayerConvertedToPaint: (layerId) => {
      convertLayerToPaintRef.current?.(layerId);
    },
  });

  // Connect canvas to history.
  //
  // Critical: we wire `loadFromJSON` to the *wrapper's* version
  // (`canvas.loadFromJSON`), NOT Fabric's bare `canvas.canvasRef.current.loadFromJSON`.
  //
  // Why: Fabric's `loadFromJSON` rebuilds every Fabric object on the
  // canvas with FRESH instances. The wrapper holds a `paintSurfacesRef`
  // map keyed by layer ID that stores `{ surface canvas, fabricImg }`
  // pairs — those entries point at the OLD Fabric objects after a bare
  // `loadFromJSON`, so any subsequent AI tool would write to a surface
  // canvas no longer attached to the live Fabric tree, and the user
  // sees no change.
  //
  // The wrapper's `loadFromJSON` calls Fabric's underneath, then
  // rebuilds `paintSurfacesRef` from the freshly-created objects.
  // Routing the history hook through it keeps the surface map in sync
  // across every undo / redo / restore.
  useEffect(() => {
    const fabricCanvas = canvas.canvasRef.current;
    if (!fabricCanvas) return;
    const wrapperLoadFromJSON = canvas.loadFromJSON;
    history.setCanvas({
      // toJSON / toObject / renderAll forward to the bare Fabric
      // canvas — these don't touch the surface map, so the underlying
      // method is correct.
      toJSON: () => fabricCanvas.toJSON(),
      toObject: (props?: string[]) => fabricCanvas.toObject(props),
      renderAll: () => fabricCanvas.renderAll(),
      // loadFromJSON goes through the wrapper so paintSurfacesRef gets
      // rebuilt against the fresh Fabric objects.
      loadFromJSON: wrapperLoadFromJSON,
    });
  }, [canvas.canvasRef, canvas.loadFromJSON, history]);

  const { isExporting, exportToLocal, exportToFias } = useImageExport({
    exportToDataURL: canvas.exportToDataURL,
    exportToBlob: canvas.exportToBlob,
  });

  // Multi-tab document state. Each tab represents one open file. Active tab
  // = live editor state; inactive tabs hold serialized PersistedSession
  // snapshots that get re-applied to the editor on tab switch.
  const tabs = useDocumentTabs();

  // Snapshot the current editor state into a PersistedSession-shaped blob,
  // keyed by the caller-supplied `documentId`. Callers MUST pass the
  // documentId of the tab whose state the canvas currently reflects —
  // not `tabs.activeTabId`, because the tab-switch effect captures the
  // OUTGOING tab's canvas AFTER React has already advanced `activeTabId`
  // to the INCOMING tab. Reading from `activeTabId` here would silently
  // save the outgoing canvas under the incoming tab's IDB key.
  // Returns null if there's nothing to snapshot (canvas not ready).
  const buildSnapshot = useCallback(
    (documentId: string): PersistedSession | null => {
      const json = canvas.toJSON(SERIALIZED_PROPS);
      if (!json) return null;
      const { entries, index } = history.getFullHistory();
      // sourceFileName lives on the TabDoc (one-of: the original
      // file-with-extension at open time, or the most recent Save-As
      // refresh). Snapshot the value off the matching tab so the
      // Export dialog's format-default survives a session reload.
      const matchingTab = tabs.tabs.find((t) => t.documentId === documentId);
      return {
        // Tier 2 Group F — documentId is the stable IDB key for this
        // session record AND for any associated PSD source-stash entry.
        // Lives on the tab; the snapshot mirrors it so reload can
        // re-associate a restored tab with its IDB record.
        documentId,
        canvasJSON: json,
        fileName: state.fileName,
        fileId: state.fileId,
        sourceFileName: matchingTab?.sourceFileName ?? null,
        viewportTransform: canvas.getViewportTransform() ?? undefined,
        // Tier 1: persist the layer document, not the derived flat
        // list. The reverse promotion (legacy v1 records) happens
        // in `session-store.migrateSession`.
        document: state.document,
        activeLayerId: state.activeLayerId,
        // Rich entries preserve per-step metadata (label, commandId,
        // isManual, timestamp) across reload. `historyStates` is the
        // older state-only field; we no longer write it for new
        // sessions but the reader still accepts it for back-compat.
        historyEntries: entries.length > 0 ? entries : undefined,
        historyIndex: entries.length > 0 ? index : undefined,
        // Tier 2 Group D — persist workspace overlay state alongside
        // the document so a reload restores rulers / grid / guides
        // / snap settings exactly as the user left them.
        viewSettings: state.viewSettings,
        // Tier 2 Group F sub-piece #5 — persist named slice regions.
        // Empty array is the common case (most documents have no
        // slices); we still write it so a reload doesn't need to
        // distinguish "never had slices" from "had slices, cleared".
        slices: state.slices,
        // Tier 3 Group D Slice 1d — round-trip the document DPI
        // through the session blob so reload + tab switch preserve
        // the metadata the user set in Document Settings.
        dpi: state.dpi,
        // Persist the unsaved-indicator so the tab's `*` survives a
        // refresh — pre-field records reload as `isDirty: false` and
        // the indicator simply doesn't show until the next edit.
        isDirty: state.isDirty,
        savedAt: Date.now(),
      };
    },
    [
      canvas,
      history,
      state.fileName,
      state.fileId,
      state.document,
      state.activeLayerId,
      state.viewSettings,
      state.slices,
      state.dpi,
      state.isDirty,
      tabs.tabs,
    ],
  );

  // Restore a PersistedSession into the editor — inverse of buildSnapshot.
  // Used both by the IndexedDB restore-on-mount flow and by tab switching.
  // Reset transient Smart Create state. The error banner, in-flight
  // prompt, and mode/model selections are per-edit-session — a tab
  // switch or fresh editor wipe should not carry them over to the
  // next document.
  const resetAiState = useCallback(() => {
    setAiStatus({ kind: 'idle' });
    setSmartCreatePrompt('');
  }, []);

  const applySnapshot = useCallback(
    async (session: PersistedSession) => {
      try {
        resetAiState();
        await canvas.loadFromJSON(session.canvasJSON);
        const restoredZoom =
          session.viewportTransform && session.viewportTransform.length === 6
            ? canvas.setViewportTransform(session.viewportTransform)
            : (canvas.fitToContainer() ?? 1);
        setFileInfo(session.fileName, session.fileId);
        // Old sessions persist a `filters` field (Fabric brightness/
        // contrast/saturation filters from before the Adjustments
        // dialog landed) — silently ignore it. The Adjustments
        // dialog supersedes this surface entirely; no UI to set
        // those values anymore.
        setZoom(restoredZoom);
        // Restore the layer tree. v2 sessions ship `document`
        // directly; legacy v1 sessions are promoted by
        // `migrateSession` before they reach this point, so the
        // `document` branch covers both.
        if (session.document) {
          resetLayerDocument(session.document, session.activeLayerId ?? null);
        } else if (session.layers) {
          resetLayers(session.layers, session.activeLayerId ?? null);
        }
        // Tier 2 Group D — restore workspace overlay state. Absent
        // on legacy sessions; the reducer's initial defaults stay
        // in place when undefined (rulers / grid hidden, no
        // guides). Bulk-replace is correct here because the
        // session is the authoritative snapshot.
        if (session.viewSettings) {
          resetViewSettings(session.viewSettings);
        }
        // Tier 2 Group F sub-piece #5 — restore named slice regions.
        // Sessions persisted before the Slice tool shipped will have
        // `slices: undefined`; the reducer's empty-array default
        // stays. Explicit `[]` from a newer session resets cleanly
        // (the user previously cleared all slices and we honor that).
        resetSlices(session.slices ?? []);
        // Tier 3 Group D Slice 1d — restore the document's DPI
        // metadata. `null` and `undefined` both mean "DPI unset" so
        // the legacy path that omits the field stays correct.
        setDpi(session.dpi ?? null);
        // Drop any compositor-cache entries from the prior document.
        // Adjustment-layer ids from a different session could
        // theoretically collide with ids in the loaded one; even
        // without collision the entries are dead weight. Fingerprint
        // checks would catch staleness, but clearing here makes the
        // intent explicit.
        clearRenderCache(compositorCacheRef.current);
        setHasImage(true);
        // Preserve the unsaved-indicator across reload. Pre-field
        // sessions load with `isDirty: undefined` → markClean (the
        // original behavior). New sessions round-trip the flag so a
        // user who closes the tab with `*` still sees `*` after
        // refresh.
        if (session.isDirty) markDirty();
        else markClean();
        // Prefer the rich entries (Tier 3 — preserves per-step labels
        // across refresh) and fall back to the older state-only field
        // for sessions written before that landed. The engine's
        // restore method accepts either shape; only the legacy shape
        // gets synthesized "Step N" placeholders.
        if (session.historyEntries && session.historyEntries.length > 0) {
          await history.restoreHistory(
            session.historyEntries as unknown as Parameters<typeof history.restoreHistory>[0],
            session.historyIndex ?? session.historyEntries.length - 1,
          );
          // Adjustment-layer FabricImages are `excludeFromExport: true`,
          // so neither `session.canvasJSON` nor any history entry's
          // `canvasJSON` carries them. The first `resetLayerDocument`
          // above triggers the reconciliation effect which creates
          // a fresh backing canvas per adjustment. But
          // `restoreHistory`'s `applyEntry` then calls a second
          // `canvas.loadFromJSON` which CLEARS every adjustment
          // backing just created (Fabric's clear+add cycle drops
          // every object on the canvas). The follow-up `extra.set`
          // dispatches another `resetLayerDocument`, but if React
          // batches that with the prior commit (same render lane),
          // the reconciliation effect doesn't re-fire and the
          // backings stay dropped — Fabric paints just bg + chrome
          // with no adjustment effects visible. Any later state
          // change (visibility toggle, draw, shape) re-fires
          // reconciliation and the adjustments come back.
          //
          // Force the post-restore reconciliation by re-dispatching
          // the document right here — guarantees a fresh state
          // reference (and therefore a fresh effect run) after the
          // canvas has settled on its final state. Also clears the
          // compositor cache so the re-fired slow-path treats every
          // adjustment as a miss against the new backings.
          clearRenderCache(compositorCacheRef.current);
          if (session.document) {
            // Spread to force a new document reference — passing the
            // same object that was dispatched 70 lines above is a
            // no-op for `useEffect` dep comparison.
            resetLayerDocument({ ...session.document }, session.activeLayerId ?? null);
          }
        } else if (session.historyStates && session.historyStates.length > 0) {
          await history.restoreHistory(
            session.historyStates as { canvasJSON: object; extra?: LayersHistoryExtra }[],
            session.historyIndex ?? session.historyStates.length - 1,
          );
          clearRenderCache(compositorCacheRef.current);
          if (session.document) {
            // Spread to force a new document reference — passing the
            // same object that was dispatched 70 lines above is a
            // no-op for `useEffect` dep comparison.
            resetLayerDocument({ ...session.document }, session.activeLayerId ?? null);
          }
        } else {
          // Same race as `loadFile` — the resetLayers dispatch above
          // hasn't committed yet, so an inline `history.saveSnapshot()`
          // captures `layers: []` and the first undo wipes the
          // Background layer. Defer to the post-commit useEffect via
          // the clean-pending flag so the snapshot reflects the just-
          // restored layers list. This branch only runs for sessions
          // that lack a serialized history stack (older sessions and
          // freshly-loaded files where IndexedDB saved before the first
          // AI action), but it's the path users hit most often after a
          // page reload.
          history.clearHistory();
          cleanSnapshotPendingRef.current = true;
          cleanSnapshotLabelRef.current = 'Open';
        }
      } catch (err) {
        // If apply fails (corrupt snapshot), leave the editor in whatever
        // state it managed to reach — better than crashing. Log so a
        // silent failure shows up in the dev console — earlier this
        // catch was bare-`{}`, which masked any failure mid-way
        // through `canvas.loadFromJSON` / reducer dispatch and made
        // the "empty editor after refresh" symptom hard to diagnose
        // (the UI looked identical whether the snapshot was missing
        // or whether apply silently threw).
        console.warn('[image-editor] applySnapshot failed for', session.documentId, err);
      }
    },
    [
      canvas,
      history,
      setFileInfo,
      setZoom,
      setDpi,
      resetLayers,
      resetLayerDocument,
      resetViewSettings,
      resetSlices,
      markClean,
      markDirty,
      resetAiState,
    ],
  );

  // Reset the editor to its empty/no-image state. Used when switching to a
  // freshly-created tab that hasn't had a file loaded into it yet, or when
  // the last tab is closed.
  const resetEditorToEmpty = useCallback(() => {
    resetAiState();
    canvas.clearCanvas();
    resetLayers([], null);
    // Tier 2 Group F sub-piece #5 — clear named slice regions when
    // the editor resets to empty. Without this, a slice list from a
    // previously-loaded image would persist into a freshly-opened
    // empty tab.
    resetSlices([]);
    // Tier 3 Group D Slice 1d — clear the previous document's DPI
    // when the tab-switch effect routes to an empty new tab. The
    // FileReader.onload path will set the right DPI once a
    // template / file finishes loading; in the meantime the
    // dialog should reflect "no DPI declared", not the previous
    // document's value.
    setDpi(null);
    setFileInfo('Untitled', null);
    history.clearHistory();
    setHasImage(false);
    markClean();
  }, [canvas, resetLayers, resetSlices, setDpi, setFileInfo, history, markClean, resetAiState]);

  const nextLayerId = useCallback(() => {
    layerIdSeqRef.current += 1;
    return `layer_${Date.now()}_${layerIdSeqRef.current}`;
  }, []);

  const makeLayer = useCallback(
    (type: LayerType, customName?: string): Layer => {
      const typeName: Record<LayerType, string> = {
        image: 'Image',
        text: 'Text',
        shape: 'Shape',
        paint: 'Drawing',
        empty: 'Layer',
        group: 'Group',
        adjustment: 'Adjustment',
        vector: 'Path',
      };
      const count = state.layers.filter((l) => l.type === type).length + 1;
      return {
        id: nextLayerId(),
        name: customName ?? `${typeName[type]} ${count}`,
        type,
        visible: true,
        // Tier 1: every layer carries an explicit `parentId`. Tier 0
        // makers always produced root layers, so `null` is the right
        // default; group-aware UI will pass an explicit parent id.
        parentId: null,
      };
    },
    [state.layers, nextLayerId],
  );

  // Handle file loading. Always opens the file in a NEW tab — both the
  // toolbar Open button and the tab bar's + button funnel through here, so
  // the user's existing work is never silently overwritten. When `handle`
  // is provided (browsers with the File System Access API), the tab keeps
  // a reference so a later Save can overwrite the original file in place.
  const loadFile = useCallback(
    (
      file: File,
      handle?: FileSystemFileHandle | null,
      /** Tier 3 Group D Slice 1d — optional DPI for the new document.
       *  Set inside FileReader.onload (which runs AFTER the
       *  tab-switch effect saves the outgoing tab's snapshot) so the
       *  value lands on the incoming tab's state, never the outgoing
       *  tab's. A synchronous setDpi here would race the tab-switch
       *  effect's `buildSnapshot(outgoingId)` — that call closes over
       *  the latest `state.dpi`, so the outgoing tab's IDB record
       *  would silently overwrite with the new tab's DPI. */
      dpiForNewTab?: number | null,
    ) => {
      const displayName = file.name.replace(/\.[^.]+$/, '');
      tabs.addTab({
        fileName: displayName,
        fileId: null,
        fileHandle: handle ?? null,
        sourceFileName: file.name,
      });
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        const fitZoom = await canvas.loadImage(dataUrl);
        const bgLayer: Layer = {
          id: nextLayerId(),
          name: 'Background',
          type: 'image',
          visible: true,
          parentId: null,
        };
        canvas.setBackgroundLayerId(bgLayer.id);
        resetLayers([bgLayer], bgLayer.id);
        setZoom(fitZoom);
        setFileInfo(displayName, null);
        if (dpiForNewTab !== undefined) {
          setDpi(dpiForNewTab);
        }
        setHasImage(true);
        markClean();
        // Defer the initial snapshot until React commits the new layers
        // list. `saveSnapshot` reads `layersStateRef.current`, which only
        // updates on the next render — calling it inline here captures
        // an empty layers array, and the first undo (after any subsequent
        // change) restores that empty state, wiping the Background layer.
        // The clean-pending flag routes through the layers useEffect
        // after the commit has flushed; using the "clean" variant skips
        // markDirty so the freshly-opened file stays clean.
        history.clearHistory();
        cleanSnapshotPendingRef.current = true;
        cleanSnapshotLabelRef.current = 'Open';
      };
      reader.readAsDataURL(file);
    },
    [canvas, setFileInfo, markClean, history, nextLayerId, resetLayers, setZoom, setDpi, tabs],
  );

  /** PSD-specific load path. Mirrors `loadFile`'s "open in new tab" model
   *  but parses the bytes via ag-psd, builds an `EditorDocument` from the
   *  PSD's layer tree, and stashes the original bytes in IDB so the
   *  export path (Tier 2 Group F PR 2b) can restore everything we
   *  flattened or didn't yet natively support.
   *
   *  Unsupported PSD features (smart objects, layer effects, adjustment
   *  layers + text + vector for now) flatten to raster on import. The
   *  per-layer `layerOriginById` map persists so export can later
   *  restore them losslessly. Color-mode / bit-depth rejections surface
   *  as toast errors before any tab state mutates. */
  const loadPsdFile = useCallback(
    async (file: File, handle?: FileSystemFileHandle | null) => {
      // Hard size limit BEFORE reading bytes into memory. `file.arrayBuffer()`
      // allocates the entire file in JS heap; without this gate a 1GB PSD
      // would OOM-kill the tab (and on WSL, can tear down the whole VM
      // per CLAUDE.md's resource-constraints note). 500MB is the
      // ceiling — covers the vast majority of PSDs while leaving headroom
      // for the parser's own working set (~3x the byte size for materialized
      // layer canvases). Tune via MAX_PSD_BYTES if real-world workflows
      // demand bigger files.
      if (file.size > MAX_PSD_BYTES) {
        toast({
          title: 'PSD too large',
          description: `This file is ${(file.size / 1024 / 1024).toFixed(0)} MB; the editor caps PSD imports at ${MAX_PSD_BYTES / 1024 / 1024} MB. Flatten layers or split the file in your source tool first.`,
          variant: 'destructive',
        });
        return;
      }
      // Read bytes BEFORE creating the tab. If parsing fails (e.g.
      // unsupported color mode), we don't want a half-created tab
      // sitting empty in the UI.
      const arrayBuffer = await file.arrayBuffer();

      let parsed: PsdImportResult;
      try {
        parsed = await parsePsdToDocument(arrayBuffer, nextLayerId);
      } catch (err) {
        const message =
          err instanceof PsdColorModeError || err instanceof PsdBitDepthError
            ? err.message
            : `Failed to parse PSD: ${err instanceof Error ? err.message : 'unknown error'}`;
        toast({
          title: 'Unable to open PSD',
          description: message,
          variant: 'destructive',
        });
        return;
      }

      const displayName = file.name.replace(/\.[^.]+$/, '');
      // `addTab` mints the documentId — capture it so the stash entry
      // is keyed identically to the session record this tab will save.
      tabs.addTab({
        fileName: displayName,
        fileId: null,
        fileHandle: handle ?? null,
        sourceFileName: file.name,
      });
      const tabIdAfter = tabs.activeTabId; // Set synchronously by addTab.
      // We need the tab's documentId — but `tabs.tabs` won't reflect
      // the just-added tab until the next render. Walk via the
      // tabsRef-equivalent: grab the activeTabId we just set and
      // search the tabs array on the next microtask.
      // Workaround: just use the activeTabId path via `tabs.tabs` —
      // we're inside an async function so React has flushed by now.
      const activeTab = tabs.tabs.find((t) => t.id === tabIdAfter);

      await canvas.initEmptyCanvas(parsed.originalWidth, parsed.originalHeight);

      // Suppress history snapshots while we materialize the PSD layer
      // tree. Every `addImageLayer` / `addVectorObject` / `addTextLayer`
      // fires `onObjectModified` → `history.saveSnapshot`, but
      // `state.document.layers` doesn't update until the
      // `resetLayerDocument` call further down — so each intermediate
      // snapshot captures a canvas-with-objects + empty-layers-list
      // state. The snapshot validator (`useCanvasHistory.runValidator`)
      // logs an invariant violation for that exact mismatch on every
      // load. We clear all those snapshots a few lines later via
      // `history.clearHistory()`, so the only effect of letting them
      // fire is console noise. Reuse the existing `isRestoring` flag
      // — same semantics: "we're rebuilding canvas state from a known
      // source; don't snapshot any of it."
      history.isRestoring.current = true;
      try {
        // Materialize each PSD leaf layer as a Fabric image. Phase 1 —
        // canvas.toBlob conversions run in PARALLEL via Promise.all
        // (these are independent encoder calls that the browser can
        // parallelize across worker threads). Phase 2 — Fabric `add`
        // mutations stay serial because Fabric's canvas mutation API
        // isn't safe to call concurrently. On a 50-layer 4K PSD this
        // cuts wall-clock import time roughly in half versus the prior
        // serial-toBlob loop.
        const entries = Array.from(parsed.layerImages.entries());
        const blobs = await Promise.all(
          entries.map(
            ([, img]) =>
              new Promise<Blob | null>((resolve) => {
                img.canvas.toBlob((b) => resolve(b), 'image/png');
              }),
          ),
        );
        for (let i = 0; i < entries.length; i++) {
          const [layerId, layerImage] = entries[i];
          const blob = blobs[i];
          if (!blob) continue;
          await canvas.addImageLayer(blob, {
            layerId,
            fitTo: {
              left: layerImage.left,
              top: layerImage.top,
              width: layerImage.canvas.width,
              height: layerImage.canvas.height,
            },
          });
        }

        // Materialize native vector layers (PSD natives follow-up
        // item 2a-iv). For layers whose `vectorMask` parsed natively,
        // `parsed.vectorPaths` carries the editor `VectorPath`;
        // `addVectorObject` mounts the Fabric Path with the layer's
        // fill / stroke.
        for (const [layerId, vectorPath] of parsed.vectorPaths) {
          canvas.addVectorObject(layerId, vectorPath);
        }

        // Materialize native text layers (PSD natives follow-up item
        // 2a-iv). PSD `LayerTextData` parsed into ParsedSvgTextLayer-
        // shaped descriptors (the existing `addTextLayer` accepts
        // both PSD- and SVG-sourced payloads since the editor's text
        // layer model is format-agnostic).
        for (const [layerId, textDesc] of parsed.textDescriptors) {
          canvas.addTextLayer(layerId, textDesc);
        }

        resetLayerDocument(parsed.document, parsed.document.rootIds[0] ?? null);
        setFileInfo(displayName, null);
        setHasImage(true);
        markClean();
        history.clearHistory();
        cleanSnapshotPendingRef.current = true;
        cleanSnapshotLabelRef.current = 'Open';

        // Defensive re-dispatch with a fresh document reference so the
        // adjustment-layer reconciliation effect (which mounts the
        // backing `FabricImage` per `type: 'adjustment'` node) and the
        // slow-path compositor both re-fire against the just-published
        // layer tree. The same workaround on the session-restore path
        // (~line 1366) traces the issue to React batching the
        // resetLayerDocument dispatches into one commit — when the
        // doc reference doesn't change, useEffect dep comparison
        // short-circuits and the reconciliation never sees the new
        // adjustment layers. Symptom user reported 2026-05-25: open a
        // PSD with B/C / H/S / Color Balance adjustment layers, none
        // of them visually apply, toggling visibility or moving sliders
        // does nothing. Also clear the compositor cache so cached
        // fingerprints against stale backings can't short-circuit the
        // new pass.
        clearRenderCache(compositorCacheRef.current);
        resetLayerDocument({ ...parsed.document }, parsed.document.rootIds[0] ?? null);
      } catch (err) {
        // A layer-mount or dispatch threw mid-load. Fabric is now
        // holding partial state (some objects added, document tree
        // still empty or stale). Surface the error to the user and
        // wipe the half-built canvas so the next Open starts clean.
        console.warn('[image-editor] PSD layer materialization failed:', err);
        toast({
          title: 'Unable to open PSD',
          description:
            err instanceof Error
              ? `Layer materialization failed: ${err.message}`
              : 'Layer materialization failed.',
          variant: 'destructive',
        });
        const fc = canvasRef.current;
        if (fc) {
          fc.clear();
          fc.requestRenderAll();
        }
        // Drop the half-formed document so the panel doesn't list
        // ghost layers that the canvas no longer holds.
        resetLayerDocument({ rootIds: [], layers: {} }, null);
        setHasImage(false);
        return;
      } finally {
        // Always release suppression — even if a layer mount throws.
        // The post-commit effect will request a fresh 'Open' snapshot
        // via `cleanSnapshotPendingRef`; that path is the one place
        // we WANT a snapshot during load.
        history.isRestoring.current = false;
      }

      // Persist the original bytes + origin map so the next phase's
      // export path can restore everything the editor flattened.
      // Quota error is surfaced as a toast; the import has already
      // completed in-memory, but reload won't restore the stash.
      if (activeTab) {
        try {
          await savePsdStash({
            documentId: activeTab.documentId,
            psdBytes: arrayBuffer,
            layerOriginById: parsed.layerOriginById,
            originalWidth: parsed.originalWidth,
            originalHeight: parsed.originalHeight,
          });
        } catch (err) {
          // BOTH quota and generic stash-write failures surface a toast.
          // Generic failures (QuotaExceededError that slipped past the
          // pre-check, IDB closed, transaction aborted, gzip stream
          // error) silently breaking the lossless round-trip story is
          // the exact regression this review block defends against —
          // user thinks the file imported successfully, edits, saves,
          // loses original adjustment-layer data on the export. The
          // toast tells them up-front so they can re-import after
          // freeing storage / closing other tabs.
          const isQuota = err instanceof PsdStashQuotaError;
          const description = isQuota
            ? `Storage budget too small for the original PSD bytes. The editor will save as a flattened PSD; consider freeing IDB space, shrinking the file, or — if you're in a private / incognito window — opening the file in a regular window where the browser allocates a larger storage budget. (${err.message})`
            : `Could not store the original PSD for lossless export — the editor will save as a flattened PSD. (${err instanceof Error ? err.message : 'unknown error'})`;
          toast({
            title: 'PSD opened, but lossless export unavailable',
            description,
            variant: 'destructive',
          });
          if (!isQuota) {
            console.warn('[image-editor] savePsdStash failed:', err);
          }
        }
      }

      // Surface a grouped-by-reason flatten-warnings toast. The
      // summary is built by `summarizePsdWarnings` (pure function,
      // testable) so the toast text stays consistent across surfaces.
      const summary = summarizePsdWarnings(parsed.warnings);
      if (summary) {
        toast({
          title: summary.title,
          description: `${summary.description}. Original PSD data preserved for export.`,
        });
      }
    },
    [canvas, tabs, nextLayerId, resetLayerDocument, setFileInfo, markClean, history],
  );

  /** SVG-specific load path. Mirrors `loadPsdFile`'s "open in new tab"
   *  model but parses XML rather than ag-psd bytes, builds an
   *  `EditorDocument` mixing vector layers (paths + primitives) with
   *  raster layers (for text / filters / masks / gradients / etc.),
   *  and persists the original SVG text in the source-stash for the
   *  round-trip story when sub-piece #4 (SVG export) lands. */
  const loadSvgFile = useCallback(
    async (file: File, handle?: FileSystemFileHandle | null) => {
      if (file.size > MAX_SVG_BYTES) {
        toast({
          title: 'SVG too large',
          description: `This file is ${(file.size / 1024 / 1024).toFixed(0)} MB; the editor caps SVG imports at ${MAX_SVG_BYTES / 1024 / 1024} MB. Embedded base64 images often dominate large SVGs — extract them to separate files in your source tool first.`,
          variant: 'destructive',
        });
        return;
      }
      const svgText = await file.text();
      let parsed: SvgImportResult;
      try {
        parsed = parseSvgToDocument(svgText, nextLayerId);
      } catch (err) {
        // Every documented rejection class in svg-import has a clear
        // user-facing message — pass it through. Anything else is a
        // generic parse failure.
        const isKnownRejection =
          err instanceof SvgScriptError ||
          err instanceof SvgMalformedError ||
          err instanceof SvgEntityDeclarationError ||
          err instanceof SvgLayerCountError ||
          err instanceof SvgNestingDepthError;
        const message = isKnownRejection
          ? (err as Error).message
          : `Failed to parse SVG: ${err instanceof Error ? err.message : 'unknown error'}`;
        toast({
          title: 'Unable to open SVG',
          description: message,
          variant: 'destructive',
        });
        return;
      }

      const displayName = file.name.replace(/\.[^.]+$/, '');
      tabs.addTab({
        fileName: displayName,
        fileId: null,
        fileHandle: handle ?? null,
        sourceFileName: file.name,
      });
      const tabIdAfter = tabs.activeTabId;
      const activeTab = tabs.tabs.find((t) => t.id === tabIdAfter);

      await canvas.initEmptyCanvas(parsed.originalWidth, parsed.originalHeight);

      // Materialize each layer.
      //   - Vector layers: add via `canvas.addVectorObject(layerId, path)`.
      //   - Raster layers: rasterize the fragment via the offscreen-
      //     <img> blob trick, convert to a Blob, and add via the
      //     existing `addImageLayer` pipeline. The rasterizer can fail
      //     (CORS taint, too-large dims, etc.) — failures degrade to
      //     a skipped layer + warning rather than aborting the whole
      //     import, matching the PSD path's resilience.
      const rasterFailures: { layerId: string; reason: string }[] = [];
      let approximateTransformCount = 0;
      for (const layerId of Object.keys(parsed.document.layers)) {
        const node = parsed.document.layers[layerId];
        // Groups have no Fabric objects of their own — childIds is
        // enough. Explicit `continue` rather than fall-through so a
        // future refactor that swaps the type-check order can't
        // accidentally materialize a phantom Fabric path/image for
        // the group (R1#1 pre-PR review note).
        if (node.type === 'group') continue;
        if (node.type === 'vector') {
          const path = parsed.vectorPaths.get(layerId);
          if (path) canvas.addVectorObject(layerId, path);
        } else if (node.type === 'text') {
          // Tier 2 Group F SVG round-trip polish (PR-2 phase D)
          // — `<text>` elements with no `<tspan>` / `<textPath>`
          // / gradient-fill / non-trivial-transform features
          // were parsed natively into a text LayerNode + a
          // ParsedSvgTextLayer entry. Materialize via the new
          // canvas helper which mirrors `addVectorObject`'s
          // pattern.
          const desc = parsed.textDescriptors.get(layerId);
          if (desc) canvas.addTextLayer(layerId, desc);
        } else if (node.type === 'image') {
          const desc = parsed.rasterDescriptors.get(layerId);
          if (!desc) continue;
          if (desc.approximateTransform) approximateTransformCount += 1;
          try {
            const rasterCanvas = await rasterizeSvgFragment({
              fragment: desc.fragment,
              width: Math.max(1, Math.round(desc.width)),
              height: Math.max(1, Math.round(desc.height)),
              viewBox: `0 0 ${parsed.originalWidth} ${parsed.originalHeight}`,
              defs: parsed.defsBlock,
            });
            const blob = await new Promise<Blob | null>((resolve) =>
              rasterCanvas.toBlob((b) => resolve(b), 'image/png'),
            );
            if (!blob) {
              rasterFailures.push({ layerId, reason: 'canvas.toBlob returned null' });
              continue;
            }
            await canvas.addImageLayer(blob, {
              layerId,
              fitTo: {
                left: desc.left,
                top: desc.top,
                width: rasterCanvas.width,
                height: rasterCanvas.height,
              },
            });
          } catch (err) {
            const msg =
              err instanceof SvgRasterizeError
                ? `${err.kind}: ${err.message}`
                : `unknown rasterizer error: ${err instanceof Error ? err.message : err}`;
            rasterFailures.push({ layerId, reason: msg });
          }
        }
      }

      // Apply text-on-path bindings AFTER all vector + text layers
      // are materialized. The binding lives on `LayerNode.textPath`;
      // wire it through `canvas.setTextPathBinding` so Fabric IText
      // renders glyphs along the bound vector layer's path. The
      // bound path's `VectorPath` lives in `parsed.vectorPaths`;
      // we re-serialize it to a d-string for setTextPathBinding's
      // signature. (PSD+SVG natives follow-up phase E.)
      for (const [layerId, desc] of parsed.textDescriptors) {
        if (!desc.textPathLayerId) continue;
        const boundPath = parsed.vectorPaths.get(desc.textPathLayerId);
        if (!boundPath) continue;
        const boundData = pathToSvgData(boundPath);
        if (boundData) canvas.setTextPathBinding(layerId, boundData);
      }

      resetLayerDocument(parsed.document, parsed.document.rootIds[0] ?? null);
      setFileInfo(displayName, null);
      setHasImage(true);
      markClean();
      history.clearHistory();
      cleanSnapshotPendingRef.current = true;
      cleanSnapshotLabelRef.current = 'Open';

      // Persist the original SVG text so the export path (sub-piece #4)
      // can restore flattened features. Same toast-on-failure semantics
      // as the PSD path — silent failure here would silently break the
      // future round-trip story.
      if (activeTab) {
        try {
          await saveSvgStash({
            documentId: activeTab.documentId,
            svgText,
            // Walker emits per-layer svgNodeIndex during the pre-order
            // DFS so the export path (sub-piece #4) can locate each
            // editor LayerNode's original SVG element in the re-parsed
            // stash. Populated by `buildEditorDocumentFromSvgDoc` in
            // svg-import.ts — pre-PR review finding (R1#2 / R3#3).
            layerOriginById: parsed.layerOriginById,
            originalWidth: parsed.originalWidth,
            originalHeight: parsed.originalHeight,
          });
        } catch (err) {
          const isQuota = err instanceof SvgStashQuotaError;
          const description = isQuota
            ? `Storage budget too small for the original SVG. The editor will save as a flattened image; consider freeing IDB space or — in a private window — opening in a regular window. (${err.message})`
            : `Could not store the original SVG for lossless export. The editor will save as a flattened image. (${err instanceof Error ? err.message : 'unknown error'})`;
          toast({
            title: 'SVG opened, but lossless export unavailable',
            description,
            variant: 'destructive',
          });
          if (!isQuota) {
            console.warn('[image-editor] saveSvgStash failed:', err);
          }
        }
      }

      // Surface flatten-warnings toast (rasterizer failures included).
      const allWarnings = [...parsed.warnings];
      for (const fail of rasterFailures) {
        allWarnings.push({
          layerId: fail.layerId,
          nodeDescription: '<rasterizer>',
          reason: 'unsupported-element',
          message: `Layer skipped — ${fail.reason}`,
        });
      }
      const summary = summarizeSvgWarnings(allWarnings);
      if (summary) {
        toast({
          title: summary.title,
          description: `${summary.description}. Original SVG preserved for export.`,
        });
      }
      // Distinct toast for layers whose rotated/skewed transforms
      // were approximated by axis-aligned bbox capture. Users should
      // know about lost fidelity even when the rasterization itself
      // succeeded. Pre-PR review finding (R3#10).
      if (approximateTransformCount > 0) {
        toast({
          title: 'Some transforms approximated',
          description: `${approximateTransformCount} rotated/skewed ${approximateTransformCount === 1 ? 'layer was' : 'layers were'} captured as an axis-aligned bounding box rather than full-fidelity rotated geometry. Native transform support is a follow-up.`,
        });
      }
    },
    [canvas, tabs, nextLayerId, resetLayerDocument, setFileInfo, markClean, history],
  );

  // Layer action handlers. Each mutates state/canvas and flags the snapshot
  // useEffect (above) to capture once the React commit has flushed.
  const handleAddLayer = useCallback(() => {
    const layer = makeLayer('empty');
    addLayer(layer, true);
    layerSnapshotPendingRef.current = true;
    layerSnapshotLabelRef.current = 'Add layer';
  }, [addLayer, makeLayer]);

  const handleSelectLayer = useCallback(
    (id: string) => {
      setActiveLayer(id);
      // Group rows have no Fabric objects of their own — clicking
      // a group should select every descendant's Fabric object so
      // a single drag moves the whole group together (Fabric's
      // built-in ActiveSelection drag handles the synchronized
      // movement). Walk the document subtree to collect the leaf
      // ids; non-group rows fall back to the single-layer path.
      const node = state.document.layers[id];
      if (node?.type === 'group') {
        // Tell `onLayerSelected` (fired synchronously by the
        // upcoming `selectLayersObjects` call) to ignore the
        // descendant selections that would otherwise override
        // `activeLayerId` with the first child's id.
        programmaticGroupSelectionRef.current = id;
        const descendantIds: string[] = [];
        const walk = (gid: string) => {
          const g = state.document.layers[gid];
          if (!g) return;
          for (const cid of g.childIds ?? []) {
            const child = state.document.layers[cid];
            if (!child) continue;
            if (child.type === 'group') walk(cid);
            else descendantIds.push(cid);
          }
        };
        walk(id);
        canvas.selectLayersObjects(descendantIds);
      } else {
        programmaticGroupSelectionRef.current = null;
        canvas.selectLayerObjects(id);
      }
      // Selection changes are not snapshotted — matches Photoshop behavior.
    },
    [setActiveLayer, canvas, state.document],
  );

  // Row-area click in the Layers panel — equivalent of clicking the
  // layer's name. Differs from the icon-click flow (`handleSelectLayer`):
  // the icon click ALWAYS shows the layer's bounding box so the user
  // can immediately see what they targeted, but the name click only
  // shows the bbox in the Move tool. In every other tool the row
  // click sets the active layer (panel highlight, brush target) but
  // explicitly clears Fabric's active object so the transform handles
  // — which are really a Move-tool affordance — don't render.
  const handleSelectLayerFromName = useCallback(
    (id: string) => {
      handleSelectLayer(id);
      if (state.activeTool !== 'move') {
        canvas.clearActiveSelection();
      }
    },
    [handleSelectLayer, canvas, state.activeTool],
  );

  const handleToggleLayerVisibility = useCallback(
    (id: string) => {
      const layer = state.layers.find((l) => l.id === id);
      if (!layer) return;
      canvas.setLayerVisibility(id, !layer.visible);
      toggleLayerVisibility(id);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Toggle visibility';
    },
    [canvas, state.layers, toggleLayerVisibility],
  );

  const handleRenameLayer = useCallback(
    (id: string, name: string) => {
      renameLayer(id, name);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Rename layer';
    },
    [renameLayer],
  );

  const handleDeleteLayer = useCallback(
    (id: string) => {
      canvas.removeLayerObjects(id);
      removeLayer(id);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Delete layer';
    },
    [canvas, removeLayer],
  );

  const handleReorderLayer = useCallback(
    (fromIndex: number, toIndex: number) => {
      reorderLayer(fromIndex, toIndex);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Reorder layer';
    },
    [reorderLayer],
  );

  const handleSetLayerOpacity = useCallback(
    (id: string, opacity: number) => {
      // Continuous: opacity is a slider; coalesce a drag into a single
      // history entry via the debounced snapshot path.
      canvas.setLayerOpacity(id, opacity);
      setLayerOpacity(id, opacity);
      layerSnapshotContinuousRef.current = true;
      layerSnapshotContinuousLabelRef.current = 'Opacity';
    },
    [canvas, setLayerOpacity],
  );

  const handleSetLayerBlendMode = useCallback(
    (id: string, blendMode: BlendMode) => {
      // Discrete: one selection from the dropdown — snapshot immediately.
      canvas.setLayerBlendMode(id, blendMode);
      setLayerBlendMode(id, blendMode);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Blend mode';
    },
    [canvas, setLayerBlendMode],
  );

  const handleSetLayerEffects = useCallback(
    (id: string, effects: LayerEffects) => {
      // Continuous: effect controls are dominated by sliders (shadow
      // blur/X/Y, stroke width). Toggling an effect on/off via its
      // checkbox lands here too and absorbs the same 500ms debounce —
      // a barely-perceptible delay before the entry shows in history,
      // and the right tradeoff to keep the slider drags coalesced.
      canvas.setLayerEffects(id, effects);
      setLayerEffects(id, effects);
      layerSnapshotContinuousRef.current = true;
      layerSnapshotContinuousLabelRef.current = 'Layer effects';
    },
    [canvas, setLayerEffects],
  );

  // Tier 1 layer-tree affordances surfaced to LayersPanel.

  // Per-row depth derived from the document tree. The flat
  // `state.layers` list is depth-first (toOrderedList), but the
  // depth itself isn't carried — we compute it once per document
  // change. Single-pass: walk the tree and stash each id's depth.
  const layerDepths = useMemo(() => {
    const out: Record<string, number> = {};
    const walk = (id: string, depth: number) => {
      const node = state.document.layers[id];
      if (!node) return;
      out[id] = depth;
      if (node.type === 'group' && node.childIds) {
        for (const cid of node.childIds) walk(cid, depth + 1);
      }
    };
    for (const rid of state.document.rootIds) walk(rid, 0);
    return out;
  }, [state.document]);

  // Local collapse state per group id. Not persisted — UI-only.
  // A future polish chunk can persist these in state.editTargets-
  // style if users complain about losing collapse state across
  // refreshes.
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set());

  // Clone Stamp state (mirrored from canvas via callbacks).
  // `cloneSourcePoint` shows in the RetouchPanel as feedback;
  // `cloneArmed` flips the panel button into "Tap canvas to set
  // source" mode and switches the canvas cursor to a crosshair
  // until the user taps. Both reset on tool change.
  const [cloneSourcePoint, setCloneSourcePoint] = useState<{ x: number; y: number } | null>(null);
  const [cloneArmed, setCloneArmed] = useState(false);
  // Track the Alt key globally so the cursor flips to a crosshair
  // while Alt is held — same effect as the armed state, but
  // transient. Held + retouch + clone variant = source-set mode.
  // (Fired at the window level rather than on the canvas because
  // the user might lift Alt off-canvas; we still want the next
  // mouse:down event to know whether Alt was held when it
  // arrived. Fabric's mouse:down handler reads `opt.e.altKey`
  // directly so the actual source-set behavior doesn't depend on
  // this state — only the cursor flip does.)
  const [altHeld, setAltHeld] = useState(false);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt' || e.altKey) setAltHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt' || !e.altKey) setAltHeld(false);
    };
    // Reset on blur — if the user alt-tabs out of the window,
    // the keyup might not fire and the cursor would stick.
    const onBlur = () => setAltHeld(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Track Space-held globally for the space-drag-pan override
  // (Photoshop convention — hold Space + drag = pan regardless of
  // active tool). Mirrors the Alt pattern above. Skip while typing
  // in form fields / contenteditable so Space stays usable as the
  // word separator. The canvas hook's `setSpaceHeld` snapshots /
  // restores the prior tool state so releasing Space returns
  // cleanly to whatever the user was doing.
  const setSpaceHeldOnCanvas = canvas.setSpaceHeld;
  useEffect(() => {
    const isTypingInto = (target: EventTarget | null): boolean =>
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      (target instanceof HTMLElement && target.isContentEditable);
    let active = false;
    const enter = () => {
      if (active) return;
      active = true;
      setSpaceHeldOnCanvas(true);
    };
    const exit = () => {
      if (!active) return;
      active = false;
      setSpaceHeldOnCanvas(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return;
      if (e.repeat) return;
      if (isTypingInto(e.target)) return;
      e.preventDefault();
      enter();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return;
      exit();
    };
    const onBlur = () => exit();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      // If the editor unmounts mid-pan, restore canvas state.
      exit();
    };
  }, [setSpaceHeldOnCanvas]);
  // Source-set mode: any of Set Source button armed, Alt held, or
  // both — but only meaningful while a stamp-style Retouch
  // variant (Clone or Heal) is active. Both share the same
  // source-pick state machine on the canvas. Pulled out so both
  // the canvas cursor override and the CanvasArea wrapper-level
  // CSS cursor stay in sync.
  const isStampVariant =
    state.retouchSettings.variant === 'clone' || state.retouchSettings.variant === 'heal';
  const cloneSourceSetMode =
    state.activeTool === 'retouch' && isStampVariant && (cloneArmed || altHeld);
  useEffect(() => {
    canvas.setCursorOverride(cloneSourceSetMode ? 'crosshair' : null);
  }, [cloneSourceSetMode, canvas]);
  // Push armed state into the canvas hook so the next mouse:down
  // knows to set the source.
  useEffect(() => {
    canvas.setCloneArmed(cloneArmed);
  }, [cloneArmed, canvas]);
  // Push the latest RetouchSettings so the canvas Clone handler
  // reads variant + per-variant config.
  useEffect(() => {
    canvas.setRetouchSettings(state.retouchSettings);
  }, [state.retouchSettings, canvas]);
  // Mirror the pen panel settings into the canvas (Tier 2 Group A).
  // The pen tool's mouse handlers read variant + stroke + fill via
  // a ref so live panel edits take effect on the next dab.
  useEffect(() => {
    canvas.setPenSettings(state.penSettings);
  }, [state.penSettings, canvas]);
  // Cancel in-progress pen path when the user switches away from
  // the pen tool. Without this, the preview keeps hanging around
  // and the next pen session appends to it.
  useEffect(() => {
    if (state.activeTool !== 'pen') canvas.cancelPenPath();
  }, [state.activeTool, canvas]);
  // Path Edit overlay (anchors + handles) renders at a constant
  // SCREEN size across zoom levels — the build helpers divide
  // their SCENE-pixel sizes by the current zoom. We rebuild on
  // every zoom change while a session is active so the visual
  // size stays put as the user wheels / pinches.
  useEffect(() => {
    if (state.pathEditActive) canvas.refreshPathEditOverlay();
  }, [state.zoom, state.pathEditActive, canvas]);
  // Cancel armed-mode when leaving the Retouch tool, when the
  // user switches away from a stamp-style variant (Clone / Heal),
  // or via Esc.
  useEffect(() => {
    if (state.activeTool !== 'retouch' || !isStampVariant) {
      if (cloneArmed) setCloneArmed(false);
      // Also drop the source-point reticle. Without this, the
      // crosshair lingers on the canvas (and the panel's "Source:
      // (x, y)" readout stays) after the user switches to a
      // non-stamp retouch variant (blur / sharpen / etc.) or away
      // from Retouch entirely — visually misleading since no
      // variant other than clone / heal consumes the source.
      // Clone ↔ Heal switches preserve the source: `isStampVariant`
      // is true for both, so this branch doesn't fire between them.
      if (cloneSourcePoint) canvas.setCloneSource(null);
    }
  }, [state.activeTool, isStampVariant, cloneArmed, cloneSourcePoint, canvas]);
  const handleSetCloneSourceFromPanel = useCallback(() => {
    setCloneArmed(true);
  }, []);
  const handleClearCloneSource = useCallback(() => {
    canvas.setCloneSource(null);
    setCloneArmed(false);
  }, [canvas]);
  const handleToggleCollapse = useCallback((id: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Shift-click range selection — only the explicit extension
  // beyond the active layer. Plain clicks clear it. The same
  // additional-selection set is also used by Ctrl/Cmd+click toggle
  // and by the Multi-Select-mode tap-to-toggle path, so the chip
  // row / future multi-layer ops see one unified "additional ids"
  // regardless of how the user built up the selection.
  const [shiftRangeIds, setShiftRangeIds] = useState<Set<string>>(() => new Set());

  // Multi-select mode (mobile-friendly): when on, taps on layer
  // rows TOGGLE membership in shiftRangeIds rather than activating
  // a different layer. The active layer stays put; the user can
  // build up a selection by tapping additional rows. Toggling the
  // mode off doesn't clear the selection — the chip row stays as
  // long as ≥2 vectors are selected. Off by default; the
  // LayersPanel header surfaces a 'Multi' toggle button.
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const toggleMultiSelectMode = useCallback(() => {
    setMultiSelectMode((v) => !v);
  }, []);

  // Toggle a layer's membership in the additional-selection set.
  // Routes from Ctrl/Cmd+click and from Multi-mode tap.
  //
  // Toggling the *active* layer means "deselect it." If there are
  // other multi-selected peers, promote the first peer to active and
  // drop the originally-active layer from the selection. If there
  // are no peers, clear the active layer entirely (null). This
  // matches the user's intuition that "the active layer's highlight
  // means it's selected; clicking again should unselect it" — the
  // previous behavior silently no-op'd, leaving the user unable to
  // remove their initial layer from a multi-select.
  const handleToggleSelectLayer = useCallback(
    (id: string) => {
      if (id === state.activeLayerId) {
        const peers = Array.from(shiftRangeIds);
        if (peers.length > 0) {
          const promoted = peers[0];
          const remaining = new Set(peers);
          remaining.delete(promoted);
          // Order matters: set the new range first so the
          // active-layer-changed effect below sees multi-select
          // still on and skips its clear (instead of wiping the
          // peers we just promoted around).
          setShiftRangeIds(remaining);
          setActiveLayer(promoted);
        } else {
          setActiveLayer(null);
        }
        return;
      }
      setShiftRangeIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [state.activeLayerId, shiftRangeIds, setActiveLayer],
  );
  useEffect(() => {
    // A new active layer (via plain click or another path) drops
    // the shift extension. Skip the clear when multi-select mode is
    // on so the deselect-active flow above (which promotes a peer
    // and keeps the remaining peers in the range) doesn't lose
    // them to this effect firing right after.
    if (multiSelectMode) return;
    setShiftRangeIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [state.activeLayerId, multiSelectMode]);

  // Clear the programmatic-group-selection ref whenever the active
  // layer drifts away from the tracked group via any path (group
  // deleted, programmatic setActiveLayer from outside the canvas
  // event flow, etc.). Without this the ref can stay set forever
  // pointing at a group that's no longer the active layer, and the
  // next selection on what would have been a descendant would be
  // silently swallowed.
  useEffect(() => {
    if (
      programmaticGroupSelectionRef.current !== null &&
      state.activeLayerId !== programmaticGroupSelectionRef.current
    ) {
      programmaticGroupSelectionRef.current = null;
    }
  }, [state.activeLayerId]);
  const handleShiftSelectLayer = useCallback(
    (id: string) => {
      const ordered = state.layers;
      const anchor = state.activeLayerId;
      if (!anchor || anchor === id) return;
      const a = ordered.findIndex((l) => l.id === anchor);
      const b = ordered.findIndex((l) => l.id === id);
      if (a === -1 || b === -1) return;
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const range = new Set<string>();
      for (let i = lo; i <= hi; i++) range.add(ordered[i].id);
      range.delete(anchor);
      setShiftRangeIds(range);
    },
    [state.layers, state.activeLayerId],
  );

  // Effective panel selection — what gets the multi-select tint
  // and what the "+ Group" header button operates on. Either:
  //   - the active layer is a group → all descendant leaves
  //     (so the panel shows every Fabric-backed child as
  //     selected and Group nesting is visible at a glance), OR
  //   - the active layer is a leaf → the shift-extended range
  //     (zero entries when no shift-click is in flight).
  const selectedLayerIds = useMemo(() => {
    if (!state.activeLayerId) return shiftRangeIds;
    const node = state.document.layers[state.activeLayerId];
    if (node?.type === 'group') {
      const out = new Set<string>();
      const walk = (gid: string) => {
        const g = state.document.layers[gid];
        if (!g) return;
        for (const cid of g.childIds ?? []) {
          const child = state.document.layers[cid];
          if (!child) continue;
          if (child.type === 'group') walk(cid);
          else out.add(cid);
        }
      };
      walk(state.activeLayerId);
      return out;
    }
    return shiftRangeIds;
  }, [state.activeLayerId, state.document, shiftRangeIds]);

  // Count of distinct vector layers in the current selection
  // (active + shift-extended). Drives the PropertiesPanel boolean-
  // ops chip row: hidden unless ≥2, since a single shape has
  // nothing to combine with. Re-derives whenever selection /
  // doc state changes so the row appears/disappears live as the
  // user shift-clicks.
  const eligibleVectorLayerCount = useMemo(() => {
    const ids = new Set<string>();
    if (state.activeLayerId) ids.add(state.activeLayerId);
    for (const id of selectedLayerIds) ids.add(id);
    let count = 0;
    for (const id of ids) {
      const node = state.document.layers[id];
      if (node?.type === 'vector' && node.path) count++;
    }
    return count;
  }, [state.activeLayerId, selectedLayerIds, state.document]);

  // Text-on-path selection (Tier 2 Group A PR-E). Computed from the
  // UNION {activeLayerId, ...selectedLayerIds} — covers both
  // shift-click (desktop) and Multi-mode tap (mobile; the LayersPanel
  // header's "Multi" toggle routes taps through
  // `handleToggleSelectLayer` into the same `shiftRangeIds` set
  // shift-click writes to). The candidate / bound info don't care
  // which layer is active — only that the selection contains the
  // right shapes.
  const textPathSelectionUnion = useMemo(() => {
    const ids = new Set<string>();
    if (state.activeLayerId) ids.add(state.activeLayerId);
    for (const id of selectedLayerIds) ids.add(id);
    return ids;
  }, [state.activeLayerId, selectedLayerIds]);

  // Tier 2 Group D — Align & Distribute. Selection set = active
  // layer ∪ multi-select set (same union used by text-on-path and
  // boolean-ops). Panel is gated on 2+ unique layers in the
  // selection.
  const alignSelectionIds = useMemo(() => {
    const ids = new Set<string>();
    if (state.activeLayerId) ids.add(state.activeLayerId);
    for (const id of selectedLayerIds) ids.add(id);
    return Array.from(ids);
  }, [state.activeLayerId, selectedLayerIds]);

  const handleAlign = useCallback(
    (anchor: AlignAnchor, target: AlignTarget) => {
      if (alignSelectionIds.length < 2) return;
      const bboxes = canvas.getLayerBBoxes(alignSelectionIds);
      if (bboxes.length < 2) return;
      const deltas = computeAlignDeltas({
        layers: bboxes,
        anchor,
        target,
        canvasWidth: state.canvasWidth,
        canvasHeight: state.canvasHeight,
      });
      canvas.applyLayerOffsets(deltas);
    },
    [alignSelectionIds, canvas, state.canvasWidth, state.canvasHeight],
  );
  const handleDistribute = useCallback(
    (axis: DistributeAxis) => {
      if (alignSelectionIds.length < 3) return;
      const bboxes = canvas.getLayerBBoxes(alignSelectionIds);
      if (bboxes.length < 3) return;
      const deltas = computeDistributeDeltas(bboxes, axis);
      canvas.applyLayerOffsets(deltas);
    },
    [alignSelectionIds, canvas],
  );

  // "Unbind" row — surfaces when the selection contains exactly one
  // text layer that's already bound. Multiple bound texts → no UI
  // (ambiguous; user narrows the selection to disambiguate).
  const textPathBoundInfo = useMemo(() => {
    let boundTextLayerId: string | null = null;
    for (const id of textPathSelectionUnion) {
      const n = state.document.layers[id];
      if (n?.type === 'text' && n.textPath) {
        if (boundTextLayerId) return null;
        boundTextLayerId = id;
      }
    }
    if (!boundTextLayerId) return null;
    const textNode = state.document.layers[boundTextLayerId];
    const bindingId = textNode?.textPath?.pathLayerId;
    if (!bindingId) return null;
    const target = state.document.layers[bindingId];
    return {
      textLayerId: boundTextLayerId,
      pathLayerId: bindingId,
      pathLayerName: target?.name ?? 'Path',
    };
  }, [state.document, textPathSelectionUnion]);

  // "Bind to Path" row — surfaces when the selection contains
  // exactly one (text + vector) pair and no bound text is already
  // there (in which case the Unbind row takes the slot). Unrelated
  // layers in the selection are ignored — the user clearly wants
  // to bind these two, plus a stray paint layer in the multi
  // shouldn't suppress the button.
  const textPathBindCandidate = useMemo(() => {
    if (textPathBoundInfo) return null;
    let textLayerId: string | null = null;
    let vectorLayerId: string | null = null;
    for (const id of textPathSelectionUnion) {
      const n = state.document.layers[id];
      if (!n) continue;
      if (n.type === 'text') {
        if (textLayerId) return null;
        textLayerId = id;
      } else if (n.type === 'vector' && n.path) {
        if (vectorLayerId) return null;
        vectorLayerId = id;
      }
    }
    if (!textLayerId || !vectorLayerId) return null;
    return {
      textLayerId,
      pathLayerId: vectorLayerId,
      pathLayerName: state.document.layers[vectorLayerId]?.name ?? 'Path',
    };
  }, [state.document, textPathSelectionUnion, textPathBoundInfo]);

  // Arch slider gating — operates on the ACTIVE layer's text. If
  // the active layer happens to be a bound text, arch is moot
  // (the bound path overrides it). Otherwise leave arch editable
  // — multi-selection may include a bound text that isn't the
  // active layer, and the user might want to edit arch on a
  // different active text.
  const isActiveTextBoundToPath = useMemo(() => {
    if (!state.activeLayerId) return false;
    const active = state.document.layers[state.activeLayerId];
    return !!(active?.type === 'text' && active.textPath);
  }, [state.activeLayerId, state.document]);

  const handleBindTextToPath = useCallback(() => {
    if (!textPathBindCandidate) return;
    setTextPath(textPathBindCandidate.textLayerId, {
      pathLayerId: textPathBindCandidate.pathLayerId,
    });
    layerSnapshotPendingRef.current = true;
    layerSnapshotLabelRef.current = 'Bind text to path';
  }, [textPathBindCandidate, setTextPath]);

  const handleUnbindTextFromPath = useCallback(() => {
    if (!textPathBoundInfo) return;
    setTextPath(textPathBoundInfo.textLayerId, null);
    layerSnapshotPendingRef.current = true;
    layerSnapshotLabelRef.current = 'Unbind text from path';
  }, [textPathBoundInfo, setTextPath]);

  // Group / Ungroup. Header "+ Group" wraps the active layer plus
  // any shift-extended selection. groupNodes validates that all
  // ids share a parent; we filter to that subset before dispatch
  // so an out-of-range selection silently drops mismatched ids
  // rather than failing visibly.
  const handleGroupActiveLayer = useCallback(() => {
    if (!state.activeLayerId) return;
    const ids = [state.activeLayerId, ...Array.from(selectedLayerIds)];
    const activeParent = state.document.layers[state.activeLayerId]?.parentId ?? null;
    const sharedIds = ids.filter((id) => state.document.layers[id]?.parentId === activeParent);
    if (sharedIds.length === 0) return;
    const groupId = `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    groupLayers(sharedIds, groupId, 'Group');
    layerSnapshotPendingRef.current = true;
    layerSnapshotLabelRef.current = 'Group layers';
    setShiftRangeIds(new Set());
  }, [state.activeLayerId, selectedLayerIds, state.document, groupLayers]);

  // Boolean ops on 2+ selected vector layers (Tier 2 Group A
  // PR-C). The selected vector layers fold sequentially through
  // the chosen op: ((A op B) op C) op D … so an N-layer Union
  // becomes the multi-shape silhouette of all of them. Source
  // layers are HIDDEN (visible=false) rather than deleted —
  // preserves the user's recovery path without forcing an
  // Affinity-style "Source" group (deferred polish). The result
  // becomes the active layer.
  const handleVectorBooleanOp = useCallback(
    (op: BooleanOp) => {
      const candidateIds = [state.activeLayerId, ...Array.from(selectedLayerIds)].filter(
        (id): id is string => typeof id === 'string',
      );
      // Dedupe preserving order so the active layer ends up first.
      const seen = new Set<string>();
      const orderedIds: string[] = [];
      for (const id of candidateIds) {
        if (!seen.has(id)) {
          seen.add(id);
          orderedIds.push(id);
        }
      }
      const vectorLayers = orderedIds
        .map((id) => state.document.layers[id])
        .filter((n): n is Layer => !!n && n.type === 'vector' && !!n.path);
      if (vectorLayers.length < 2) return;

      // Bake each layer's Fabric transform into its path data so
      // paper.js operates in scene-space. Without this, two layers
      // with the same raw path-data but different translations
      // would look identical to paper, producing nonsense
      // intersect/subtract/exclude results placed at the path-data
      // origin instead of the user's visible center.
      const bakedPaths = vectorLayers.map(
        (layer) => canvas.getBakedVectorPath(layer.id, layer.path!) ?? layer.path!,
      );

      let result: VectorPath = bakedPaths[0];
      for (let i = 1; i < bakedPaths.length; i++) {
        result = pathBoolean(result, bakedPaths[i], op);
        if (result.subPaths.length === 0) {
          // Most commonly: Intersect on disjoint shapes. Tell the
          // user — otherwise the chip click reads as a hang. We
          // exit BEFORE hiding any source so the document state
          // stays consistent (no destructive change on no-result).
          toast({
            title: 'Boolean produced no geometry',
            description: 'The selected shapes have no overlap for this operation.',
          });
          return;
        }
      }
      // Inherit style from the first source layer so the result
      // visually echoes the user's intent.
      const firstPath = vectorLayers[0].path!;
      result = {
        ...result,
        stroke: firstPath.stroke,
        fill: firstPath.fill,
      };

      const base = makeLayer('vector');
      const resultLayer: Layer = {
        ...base,
        path: result,
        fabricObjectId: base.id,
      };
      addLayer(resultLayer, true);
      canvas.addVectorObject(resultLayer.id, result);

      // Hide every source layer so the result reads as the
      // canonical visual. User can re-show via the eye icon to
      // recover; nothing is destroyed. Skip already-hidden layers
      // so the toggle doesn't flip them visible again.
      for (const layer of vectorLayers) {
        if (layer.visible !== false) toggleLayerVisibility(layer.id);
      }
      // Drop the shift-extended selection — after the op the
      // sources are no longer the user's focal target, the result
      // is. Mirrors handleGroupActiveLayer's post-op cleanup.
      setShiftRangeIds(new Set());
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = `Boolean ${op}`;
    },
    [
      state.activeLayerId,
      selectedLayerIds,
      state.document,
      makeLayer,
      addLayer,
      canvas,
      toggleLayerVisibility,
    ],
  );
  const handleUngroup = useCallback(
    (id: string) => {
      ungroup(id);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Ungroup';
    },
    [ungroup],
  );
  const handleMoveNodeInTree = useCallback(
    (id: string, parentId: string | null, index: number) => {
      moveNodeInTree(id, parentId, index);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Move layer';
    },
    [moveNodeInTree],
  );

  // Mask add/delete: routes through the canvas helper (which
  // creates / detaches the Fabric clipPath) and mirrors the
  // resulting LayerMask config into the document via the reducer
  // so persistence + undo work. Use the pending-snapshot pattern
  // so history.saveSnapshot fires AFTER React flushes the
  // setLayerMask dispatch — calling saveSnapshot inline here would
  // capture stale `state.layers` from `layersStateRef.current`
  // (it's only updated in render), and that stale extra would
  // wipe the mask config from the document on any later restore
  // (page reload's history.restoreHistory or undo/redo). Discrete,
  // not continuous — masks are a single user gesture, not a slider
  // drag.
  const handleAddMaskToLayer = useCallback(
    (id: string, opts?: { fromMarquee?: boolean }) => {
      const mask = canvas.addLayerMask(id, opts);
      if (!mask) return;
      setLayerMask(id, mask);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Add mask';
    },
    [canvas, setLayerMask],
  );
  const handleDeleteMaskFromLayer = useCallback(
    (id: string) => {
      canvas.removeLayerMask(id);
      setLayerMask(id, null);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Delete mask';
    },
    [canvas, setLayerMask],
  );

  // Mask config toggles (Disable/Enable, Invert). The reducer
  // dispatch sets the new full mask config; the mask-sync effect
  // upstream picks up the diff and re-materializes the clipPath
  // effective alpha. Snapshot-pending so history captures the
  // toggle as a discrete entry. No-op when the layer doesn't have
  // a mask (overflow menu wouldn't surface the toggle in that
  // case anyway, but stays defensive against stale closures).
  const handleToggleMaskEnabled = useCallback(
    (id: string) => {
      const node = state.document.layers[id];
      const mask = node?.mask;
      if (!mask) return;
      setLayerMask(id, { ...mask, enabled: !mask.enabled });
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Toggle mask';
    },
    [state.document, setLayerMask],
  );
  const handleToggleMaskInverted = useCallback(
    (id: string) => {
      const node = state.document.layers[id];
      const mask = node?.mask;
      if (!mask) return;
      setLayerMask(id, { ...mask, inverted: !mask.inverted });
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Invert mask';
    },
    [state.document, setLayerMask],
  );

  // Add a vector mask. Tier 2 Group A PR-D. Single-pass:
  // `addLayerMask`'s `vectorPath` opt rasterizes the seed path
  // into RAW directly, so the materialize → EFFECTIVE pass at
  // creation time already produces the right alpha. We then
  // pre-seed `lastPushedMaskVectorPathRef` with the same path
  // reference so the vector-mask-sync effect's next tick sees
  // "already pushed" and skips the redundant rasterize that
  // would otherwise fire on the first state.document change.
  //
  // No marquee-from-selection shortcut for v1 — the marquee
  // is a raster shape; seeding a vector mask from a marquee
  // would require selection→path conversion (a future
  // enhancement).
  const handleAddVectorMaskToLayer = useCallback(
    (id: string) => {
      const seed = canvasRectPath(state.canvasWidth, state.canvasHeight);
      const baseMask = canvas.addLayerMask(id, { vectorPath: seed });
      if (!baseMask) return;
      setLayerMask(id, { ...baseMask, kind: 'vector', vectorPath: seed });
      lastPushedMaskVectorPathRef.current.set(id, seed);
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Add vector mask';
    },
    [canvas, state.canvasWidth, state.canvasHeight, setLayerMask],
  );

  // Convert an existing raster mask to vector. Tier 2 Group A
  // PR-D. Traces the RAW alpha channel into a paper.js path,
  // simplifies, and seeds the vectorPath. Three outcomes:
  //   1. Trace produces geometry → seed with traced path.
  //   2. RAW is uniformly above threshold (default reveal-all
  //      mask) → seed with canvas-rect (lossless).
  //   3. RAW is uniformly below threshold (fully-hidden mask)
  //      → seed with empty subPaths (vector mask renders
  //      hidden, user starts fresh).
  // The vector-mask-sync effect re-rasterizes RAW from the new
  // path next frame, which discards the original raster paint
  // — irreversible without undo. No confirm dialog: the user
  // explicitly picked "Convert to Vector Mask"; the toast +
  // undo entry is the safety net.
  const handleConvertMaskToVector = useCallback(
    (id: string) => {
      const node = state.document.layers[id];
      const mask = node?.mask;
      if (!mask || (mask.kind ?? 'raster') === 'vector') return;
      const raw = canvas.getRawMaskCanvas(id);
      const traced = raw
        ? traceMaskAlphaToVectorPath(raw)
        : canvasRectPath(state.canvasWidth, state.canvasHeight);
      convertMaskKind(id, 'vector', traced);
      // Reset edit target to 'layer' so a stale 'mask' value
      // (set by a prior raster-mask thumb tap) doesn't route
      // subsequent brush strokes into the vector-mask no-op
      // toast loop. The user's next interaction with the new
      // vector mask is the thumb tap → Path Edit, which doesn't
      // care about editTarget.
      setEditTarget(id, 'layer');
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Convert mask to vector';
      toast({
        title: 'Converted to vector mask',
        description:
          traced.subPaths.length > 0
            ? 'Tap the mask thumb to edit the path.'
            : 'Empty path — use the pen tool to add anchors.',
      });
    },
    [canvas, state.document, state.canvasWidth, state.canvasHeight, convertMaskKind, setEditTarget],
  );

  // Convert vector → raster. Tier 2 Group A PR-D. The current
  // rasterized RAW (which always matches the vector path
  // because the vector-mask-sync effect keeps them in lockstep)
  // becomes the new source of truth. Just drop vectorPath +
  // flip kind; no canvas-side mutation needed.
  const handleConvertMaskToRaster = useCallback(
    (id: string) => {
      const node = state.document.layers[id];
      const mask = node?.mask;
      if (!mask || (mask.kind ?? 'raster') !== 'vector') return;
      convertMaskKind(id, 'raster');
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Convert mask to raster';
      toast({
        title: 'Converted to raster mask',
        description: 'Brush in mask mode now paints alpha directly.',
      });
    },
    [state.document, convertMaskKind],
  );

  // Keep the canvas z-order in sync with the layer list. Front-of-list = top of canvas.
  useEffect(() => {
    canvas.applyLayerOrder(state.layers.map((l) => l.id));
  }, [state.layers, canvas]);

  // Cascade visibility through the tree: hiding a group hides
  // every descendant Fabric object. Runs on every document change
  // and is idempotent — only renders when something moved.
  useEffect(() => {
    canvas.applyEffectiveVisibility(state.document);
  }, [state.document, canvas]);

  // Flush any in-flight continuous (slider-debounced) snapshot. Called
  // by anything that needs the slider drag committed as a discrete
  // history entry before the next operation lands — discrete edits,
  // explicit undo / redo, and the snapshot effect itself when both a
  // continuous and discrete tick are pending in the same render. Safe
  // to call repeatedly; no-ops when no debounce is in flight.
  const flushContinuousSnapshot = useCallback(() => {
    if (continuousDebounceRef.current) {
      clearTimeout(continuousDebounceRef.current);
      continuousDebounceRef.current = null;
      const label = layerSnapshotContinuousLabelRef.current;
      layerSnapshotContinuousLabelRef.current = null;
      history.saveSnapshot(label ? { label } : undefined);
    }
  }, [history]);

  // Cancel the debounce on unmount so the timer can't fire after the
  // editor is gone (would call history.saveSnapshot against a stale
  // canvas ref). The flush-on-blur side is handled implicitly by the
  // existing IndexedDB session save — even if a continuous snapshot
  // never reaches history, the live canvas state survives a refresh.
  useEffect(() => {
    return () => {
      if (continuousDebounceRef.current) {
        clearTimeout(continuousDebounceRef.current);
        continuousDebounceRef.current = null;
      }
    };
  }, []);

  // Capture history snapshot after a user-action layer change has committed.
  // Three pending refs feed this:
  //   - `layerSnapshotPendingRef` — discrete user actions (toggles,
  //     reorder, delete, paint stroke). Snapshots immediately and
  //     marks dirty. Flushes any pending continuous first so slider-
  //     then-discrete sequences land in history in the right order.
  //   - `layerSnapshotContinuousRef` — continuous slider-style
  //     mutations. Marks dirty immediately for tab `*` responsiveness;
  //     debounces the snapshot itself so a slider drag becomes one
  //     history entry.
  //   - `cleanSnapshotPendingRef` — post-loadFile snapshot. Snapshots
  //     immediately and skips markDirty so freshly-opened files don't
  //     appear modified.
  useEffect(() => {
    if (layerSnapshotPendingRef.current) {
      layerSnapshotPendingRef.current = false;
      const label = layerSnapshotLabelRef.current;
      layerSnapshotLabelRef.current = null;
      flushContinuousSnapshot();
      history.saveSnapshot(label ? { label } : undefined);
      markDirty();
    } else if (layerSnapshotContinuousRef.current) {
      layerSnapshotContinuousRef.current = false;
      const label = layerSnapshotContinuousLabelRef.current;
      // Don't clear the continuous label yet — a follow-up tick of
      // the same drag should keep the same label. The debounce timer
      // clears it when the snapshot finally lands (and any later
      // continuous tick that wants a different label will overwrite
      // before the timer fires).
      markDirty();
      if (continuousDebounceRef.current) clearTimeout(continuousDebounceRef.current);
      continuousDebounceRef.current = setTimeout(() => {
        history.saveSnapshot(label ? { label } : undefined);
        layerSnapshotContinuousLabelRef.current = null;
        continuousDebounceRef.current = null;
      }, SLIDER_SNAPSHOT_DEBOUNCE_MS);
    } else if (cleanSnapshotPendingRef.current) {
      cleanSnapshotPendingRef.current = false;
      const label = cleanSnapshotLabelRef.current;
      cleanSnapshotLabelRef.current = null;
      history.saveSnapshot(label ? { label } : undefined);
    }
    // Tier 2 Group F sub-piece #5 — `state.slices` is in the dep
    // array so slice-tool dispatches (which set
    // `layerSnapshotPendingRef.current = true` before dispatching
    // ADD/UPDATE/REMOVE/CLEAR_SLICE) fire this effect post-commit
    // and snapshot the new state. `state.layers` covers layer-tree
    // mutations; both share the same pending-ref + saveSnapshot
    // machinery so an alternating layer / slice sequence produces
    // a coherent undo stack.
  }, [state.layers, state.slices, history, markDirty, flushContinuousSnapshot]);

  // Wrap a canvas mutation that fires through `onObjectModified` →
  // `history.saveSnapshot` so the History panel reads the operation's
  // name instead of the active-tool fallback ("Move"). Used for
  // toolbar actions that don't go through `dispatchCommand` (the
  // primary label channel) — see PropertiesPanel's transform row.
  // Try/finally ensures the pending label is cleared even if the
  // canvas op throws.
  const withHistoryLabel = useCallback(
    (label: string, fn: () => void): void => {
      history.setPendingLabel({ label });
      try {
        fn();
      } finally {
        history.setPendingLabel(null);
      }
    },
    [history],
  );

  const handleApplyCrop = useCallback(() => {
    if (canvas.applyActiveCrop()) {
      markDirty();
      history.saveSnapshot();
    }
  }, [canvas, markDirty, history]);

  // Invert turns any marquee shape into a wand-style mask selection
  // (the inverse of a rect is a frame, not a rect — see the
  // `invertMarquee` impl). Push React's marqueeSettings.shape to
  // match so the marquee panel's shape buttons highlight the right
  // tile (otherwise the user sees "rect" highlighted while looking
  // at a wand-style selection).
  const handleInvertSelection = useCallback(() => {
    if (state.marqueeSettings.shape !== 'wand') {
      updateMarqueeSettings({ shape: 'wand' });
    }
    canvas.invertMarquee();
  }, [canvas, state.marqueeSettings.shape, updateMarqueeSettings]);

  // Quick Mask toggle (Tier 1 Group D). The boolean lives in the
  // reducer; this effect bridges to the canvas side. Entering installs
  // the working alpha mask + red overlay; exiting converts the mask
  // back into a wand-style marquee. The effect runs whenever the
  // flag flips, regardless of what triggered it (StatusBar chip
  // popover, future command-palette entry, etc.).
  const handleToggleQuickMask = useCallback(() => {
    setQuickMaskActive(!state.quickMaskActive);
  }, [setQuickMaskActive, state.quickMaskActive]);

  // Apply Quick Mask via the chosen output target. Selection is the
  // default Photoshop semantic; Layer Mask attaches the painted
  // alpha to the active layer's clipPath via the same plumbing
  // `addLayerMask` uses.
  const handleApplyQuickMask = useCallback(
    (output: 'selection' | 'mask') => {
      const qmCanvas = canvas.getQuickMaskCanvas();
      if (output === 'mask' && state.activeLayerId && qmCanvas) {
        const maskConfig = canvas.applyLayerMaskFromCanvas(state.activeLayerId, qmCanvas);
        if (maskConfig) {
          setLayerMask(state.activeLayerId, maskConfig);
          // Same pending-snapshot pattern `handleAddMaskToLayer` uses
          // — saveSnapshot runs after React commits the setLayerMask
          // dispatch so the snapshot's `extra` captures the post-
          // dispatch layers state (an inline saveSnapshot here would
          // capture stale state and drop the mask config on later
          // restore — see the comment block on `handleAddMaskToLayer`).
          layerSnapshotPendingRef.current = true;
          layerSnapshotLabelRef.current = 'Apply Quick Mask';
        }
        // Skip the auto-selection install — we've already applied
        // the mask. The state flip below would otherwise trigger
        // exitQuickMask's default `setSelectionFromMask` path.
        canvas.discardQuickMask();
      }
      // Flip the React flag last so the active-flag effect's
      // exitQuickMask is a no-op (refs cleared by discard in the
      // mask path, or runs default selection-install in the
      // selection path).
      setQuickMaskActive(false);
    },
    [canvas, state.activeLayerId, setLayerMask, setQuickMaskActive],
  );

  const handleCancelQuickMask = useCallback(() => {
    // Cancel = throw away the painted mask AND don't install a
    // selection. The selection path would otherwise auto-install
    // via exitQuickMask's default behavior.
    canvas.discardQuickMask();
    setQuickMaskActive(false);
  }, [canvas, setQuickMaskActive]);

  // Mirror state.activeTool → canvas-side tool state for ANY path
  // that changes the active tool, not just ToolPalette clicks.
  // `handleToolSelect` calls `canvas.setTool` directly for an
  // immediate sync; this effect covers the rest — Quick Mask
  // toggling, future command-palette entries, etc. — so brush
  // handlers in `useFabricCanvas` (which gate on
  // `activeToolRef.current`) see the right tool.
  //
  // The `lastSyncedTool` ref guards against double-firing: when
  // `handleToolSelect` runs `canvas.setTool` directly, this effect
  // will also see the new `state.activeTool` and would otherwise
  // call `setTool` again. Some `setTool` cases (notably 'crop')
  // are non-idempotent — re-entering 'crop' adds a second crop
  // rect — so guarding is necessary.
  const brushSettingsRef = useRef(state.brushSettings);
  const lastSyncedToolRef = useRef<EditorTool>(state.activeTool);
  useEffect(() => {
    brushSettingsRef.current = state.brushSettings;
  }, [state.brushSettings]);
  useEffect(() => {
    if (lastSyncedToolRef.current === state.activeTool) return;
    lastSyncedToolRef.current = state.activeTool;
    canvas.setTool(state.activeTool, brushSettingsRef.current);
  }, [state.activeTool, canvas]);

  // Tier 2 Group F sub-piece #5 — Slice tool wiring.
  //
  // (1) Install the dispatch-into-React callbacks ONCE so the canvas
  //     event handlers (drag-create, object:modified-on-resize) can
  //     route into the reducer. The ref pattern inside useFabricCanvas
  //     means we don't have to re-register on every render.
  useEffect(() => {
    canvas.setSliceCallbacks({
      onCreate: (rect) => addSlice(rect),
      onUpdate: (id, patch) => updateSlice(id, patch),
    });
    return () => {
      canvas.setSliceCallbacks(null);
    };
  }, [canvas, addSlice, updateSlice]);
  // (2) Reconcile on-canvas slice overlays with `state.slices` whenever
  //     either side changes — or whenever the active tool flips on/off
  //     'slice'. When the tool isn't 'slice', call with `[]` so the
  //     canvas doesn't carry slice overlays during, e.g., the Move
  //     tool's view (those overlays would steal clicks even though
  //     the user has moved on to a different workflow).
  useEffect(() => {
    if (state.activeTool === 'slice') {
      canvas.syncSliceOverlays(state.slices);
    } else {
      canvas.syncSliceOverlays([]);
    }
  }, [state.slices, state.activeTool, canvas]);

  useEffect(() => {
    if (state.quickMaskActive) {
      // Active marquee shape lands on 'wand' on exit (the QM mask
      // becomes a wand selection), so prep the panel UI now so the
      // user doesn't see a momentary mismatch.
      if (state.marqueeSettings.shape !== 'wand') {
        updateMarqueeSettings({ shape: 'wand' });
      }
      const ok = canvas.enterQuickMask();
      if (!ok) {
        // Couldn't enter — likely no image loaded. Roll back the
        // state to keep the UI consistent.
        setQuickMaskActive(false);
      }
    } else {
      canvas.exitQuickMask();
    }
    // (Mode mirror sits in a separate effect — see below.)
    // The deps are intentionally `quickMaskActive` and `canvas` only:
    // depending on `state.marqueeSettings.shape` or
    // `updateMarqueeSettings` would re-fire the effect when we set
    // the shape ourselves, calling enter/exit a second time per
    // toggle. `enterQuickMask` is defensively idempotent (sweeps
    // any existing tagged overlays before adding a new one) so a
    // stray re-fire is harmless. The project's ESLint config
    // doesn't register the exhaustive-deps rule.
  }, [state.quickMaskActive, canvas]);

  // Mirror Quick Mask mode (Paint / Erase) into the canvas-side ref
  // read by the brush handler. Separate from the active-flag effect
  // so toggling mode mid-session doesn't re-enter QM.
  useEffect(() => {
    canvas.setQuickMaskMode(state.quickMaskMode);
  }, [state.quickMaskMode, canvas]);

  // Free Transform (Tier 1 Group E Phase 1). Mirrors the state-flag
  // pattern from Quick Mask: BEGIN_TRANSFORM in the reducer flips
  // `state.transformActive`, this effect bridges to the canvas.
  // The control config (which Fabric handles render, skew locks)
  // sits in `applyTransformControls` — fired on entry AND on every
  // mode change so the per-mode handle setup stays in lockstep.
  const handleBeginTransform = useCallback(
    (layerId: string) => {
      // Mute per-stroke snapshots for the duration of the session.
      // Apply will save ONE snapshot at the end; Cancel saves
      // none. Without the mute, dragging fires object:modified on
      // mouse:up, which saves a "post-drag" snapshot that Cancel
      // can't cleanly roll back.
      transformSuppressSnapshotRef.current = true;
      beginTransform(layerId);
    },
    [beginTransform],
  );
  const handleApplyTransform = useCallback(() => {
    canvas.exitTransformMode(true);
    transformSuppressSnapshotRef.current = false;
    // Apply marks the session's net effect as ONE history entry.
    // markDirty + saveSnapshot mirror what onObjectModified would
    // do for a normal user-initiated edit.
    markDirty();
    history.saveSnapshot();
    endTransform(true);
  }, [canvas, endTransform, history, markDirty]);
  // Intercepts transform-mode changes from the FreeTransformPanel
  // chip row. Distort / Perspective / Warp need a `FabricImage`
  // target (the underlying rasterizer in `commitNonAffineDeformation`
  // uses `setElement`, which is FabricImage-only). On a vector /
  // shape / text layer we open the rasterize-before-warp prompt
  // instead of switching mode immediately; the dialog's onConfirm
  // does the rasterize then proceeds. Free / Skew still pass
  // through directly — they only need affine matrix props that
  // every Fabric object supports.
  const handleSetTransformMode = useCallback(
    (mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp') => {
      if (mode !== 'distort' && mode !== 'perspective' && mode !== 'warp') {
        setTransformMode(mode);
        return;
      }
      const layerId = state.transformLayerId ?? state.activeLayerId;
      const node = layerId ? state.document.layers[layerId] : null;
      const needsRasterize =
        node?.type === 'vector' || node?.type === 'shape' || node?.type === 'text';
      if (!needsRasterize) {
        setTransformMode(mode);
        return;
      }
      // Defer the mode switch — the dialog's onConfirm will run
      // the rasterize swap and dispatch SET_TRANSFORM_MODE then.
      setPendingWarpMode(mode);
    },
    [state.transformLayerId, state.activeLayerId, state.document.layers, setTransformMode],
  );
  const handleConfirmRasterizeBeforeWarp = useCallback(() => {
    if (!pendingWarpMode) return;
    const layerId = state.transformLayerId ?? state.activeLayerId;
    if (!layerId) {
      setPendingWarpMode(null);
      return;
    }
    // Order matters: swap the Fabric object FIRST so the mode-mirror
    // effect (which calls applyTransformControls) sees a
    // FabricImage when it next fires. Then mutate the document so
    // type/path stay consistent with the on-canvas reality.
    const ok = canvas.rasterizeLayerToImage(layerId);
    if (!ok) {
      setPendingWarpMode(null);
      return;
    }
    convertLayerToPaint(layerId);
    setTransformMode(pendingWarpMode);
    setPendingWarpMode(null);
  }, [
    pendingWarpMode,
    state.transformLayerId,
    state.activeLayerId,
    canvas,
    convertLayerToPaint,
    setTransformMode,
  ]);
  const handleCancelRasterizeBeforeWarp = useCallback(() => {
    // Drop the dialog. We don't reset to 'free' because we never
    // dispatched a mode change — `state.transformMode` is still
    // whatever the panel showed before the chip click. The chip
    // row's active-state indicator will reflect that.
    setPendingWarpMode(null);
  }, []);
  const handleCancelTransform = useCallback(() => {
    canvas.exitTransformMode(false);
    transformSuppressSnapshotRef.current = false;
    // No saveSnapshot — Cancel leaves no history trace per the
    // "cancel means it never happened" user expectation.
    endTransform(false);
  }, [canvas, endTransform]);

  // Liquify — Tier 1 Group E final piece. Activates the target
  // layer (if it isn't already), pulls its rasterizable surface
  // via the same `getActiveAdjustmentTargets` gate the Adjust
  // button uses, and snapshots a fresh canvas as the workspace's
  // working source. Apply routes through `runOnActiveLayerFullImage`
  // which writes the deformed pixels back over the live surface
  // and fires `object:modified` (markDirty + history.saveSnapshot
  // run through the existing canvas-hook plumbing — no need to
  // mirror them here).
  const handleBeginLiquify = useCallback(
    (layerId: string) => {
      if (state.activeLayerId !== layerId) {
        setActiveLayer(layerId);
        canvas.setActiveLayerId(layerId);
      }
      const targets = canvas.getActiveAdjustmentTargets(state.document);
      if (targets.length === 0) return;
      const liveSurface = targets[0].canvas;
      const snapshot = document.createElement('canvas');
      snapshot.width = liveSurface.width;
      snapshot.height = liveSurface.height;
      // `willReadFrequently: true` on the FIRST getContext call —
      // the rasterizer reads back via getImageData every drag tick,
      // and the flag is locked on first context creation regardless
      // of later options. Without this Chrome logs a perf warning.
      const sctx = snapshot.getContext('2d', { willReadFrequently: true });
      if (!sctx) return;
      sctx.drawImage(liveSurface, 0, 0);
      setLiquifySource(snapshot);
      setLiquifyOpen(true);
    },
    [state.activeLayerId, state.document, canvas, setActiveLayer],
  );
  const handleApplyLiquify = useCallback(
    async (deformedCanvas: HTMLCanvasElement) => {
      try {
        await canvas.runOnActiveLayerFullImage(async () => deformedCanvas);
      } catch (err) {
        // Lazy-register fallback in `runOnActiveLayerFullImage`
        // covers the common "unpainted image layer" case, so a
        // throw here is unexpected — log and close the workspace
        // anyway so the user isn't stuck with a stale full-screen
        // takeover.
        console.error('[image-editor] liquify apply failed:', err);
      } finally {
        setLiquifyOpen(false);
        setLiquifySource(null);
      }
    },
    [canvas],
  );
  const handleCloseLiquify = useCallback(() => {
    setLiquifyOpen(false);
    setLiquifySource(null);
  }, []);

  // Path Edit mode — Tier 2 Group A PR-B. Mirrors the Free
  // Transform pattern: reducer flag + a canvas-side overlay that
  // owns the working state until Apply / Cancel.
  const handleBeginPathEdit = useCallback(
    (layerId: string) => {
      const node = state.document.layers[layerId];
      if (!node || node.type !== 'vector' || !node.path) return;
      if (state.activeLayerId !== layerId) {
        setActiveLayer(layerId);
        canvas.setActiveLayerId(layerId);
      }
      // Snapshot the path BEFORE dispatching BEGIN_PATH_EDIT — the
      // canvas-side overlay treats it as the source-of-truth for
      // the session. Reducer state.document stays at this snapshot
      // throughout; Apply commits via SET_VECTOR_PATH.
      const ok = canvas.enterPathEditMode(layerId, node.path);
      if (!ok) return;
      beginPathEdit(layerId);
    },
    [state.document, state.activeLayerId, canvas, beginPathEdit, setActiveLayer],
  );
  // Enter Path Edit on a vector mask's path. Tier 2 Group A PR-D.
  // Mirrors handleBeginPathEdit but reads from the layer's
  // mask.vectorPath instead of the layer's own path, and passes
  // `mode: 'mask'` to enterPathEditMode so the canvas overlay
  // skips the Fabric.Path swap. The reducer's pathEditTarget
  // flag drives handleApplyPathEdit's dispatch choice.
  const handleEditVectorMaskPath = useCallback(
    (layerId: string) => {
      const node = state.document.layers[layerId];
      const mask = node?.mask;
      if (!node || !mask || mask.kind !== 'vector' || !mask.vectorPath) return;
      if (state.activeLayerId !== layerId) {
        setActiveLayer(layerId);
        canvas.setActiveLayerId(layerId);
      }
      const ok = canvas.enterPathEditMode(layerId, mask.vectorPath, { mode: 'mask' });
      if (!ok) return;
      beginPathEdit(layerId, 'mask');
    },
    [state.document, state.activeLayerId, canvas, beginPathEdit, setActiveLayer],
  );
  const handleApplyPathEdit = useCallback(() => {
    if (!state.pathEditLayerId) return;
    const finalPath = canvas.exitPathEditMode(true);
    if (finalPath) {
      if (state.pathEditTarget === 'mask') {
        // Mask mode — commit to the layer's mask.vectorPath. The
        // vector-mask-sync effect picks up the new path next
        // frame and re-rasterizes RAW.
        setMaskVectorPath(state.pathEditLayerId, finalPath);
      } else {
        // Drop the `vectorSource` marker on Apply — the user has
        // committed their custom anchor edits, so the path is no
        // longer a "regular polygon / star / arrow." Without this,
        // a subsequent color / stroke / sides slider change would
        // trip the regenerate flow and overwrite the user's curve
        // edits with a freshly-regenerated primitive.
        setVectorPath(state.pathEditLayerId, finalPath, { clearVectorSource: true });
      }
    }
    endPathEdit(true);
    setPathEditSelectedAnchorTypeState(null);
    // Defer the history snapshot via `layerSnapshotPendingRef` so
    // it fires from the post-render useEffect — the
    // `setVectorPath` dispatch above queues a state change, and an
    // inline `history.saveSnapshot()` runs BEFORE React commits.
    // The pre-commit snapshot captures the canvas in its post-Apply
    // shape (canvas state is mutable + already updated) but the
    // React document in its pre-Apply shape. Reload via
    // `restoreHistory` then mismatches them: the canvas renders the
    // new path but `state.document.vector.path` reverts to the old
    // geometry, breaking anything that derives from the document
    // (text-on-path bindings, future vector mask paths, etc.).
    // markDirty is fired by the pending-snapshot effect alongside
    // saveSnapshot — no need to call it inline here.
    layerSnapshotPendingRef.current = true;
    layerSnapshotLabelRef.current = 'Edit path';
  }, [
    canvas,
    state.pathEditLayerId,
    state.pathEditTarget,
    endPathEdit,
    setVectorPath,
    setMaskVectorPath,
  ]);
  const handleCancelPathEdit = useCallback(() => {
    canvas.exitPathEditMode(false);
    endPathEdit(false);
    setPathEditSelectedAnchorTypeState(null);
  }, [canvas, endPathEdit]);
  // React state mirror of the selected anchor's type. The canvas
  // owns the authoritative working path; this mirror exists so
  // the EditPathPanel's radio reflects type changes immediately
  // (a direct read of `canvas.getPathEditSelectedAnchorType()`
  // per render doesn't re-trigger when `setPathEditAnchorType`
  // mutates the canvas-side state, because mutating a ref
  // doesn't schedule a React update).
  const [pathEditSelectedAnchorType, setPathEditSelectedAnchorTypeState] = useState<
    'corner' | 'smooth' | 'asymmetric' | null
  >(null);
  const handlePathEditAnchorType = useCallback(
    (type: 'corner' | 'smooth' | 'asymmetric') => {
      canvas.setPathEditAnchorType(type);
      setPathEditSelectedAnchorTypeState(type);
    },
    [canvas],
  );
  // The canvas-side selection-change callback (passed in
  // `useFabricCanvas` opts above) calls `setPathEditAnchor` AND
  // refreshes the type mirror so the radio reflects the new
  // anchor's existing type the moment it's selected.

  useEffect(() => {
    if (state.transformActive && state.transformLayerId) {
      const ok = canvas.enterTransformMode(state.transformLayerId);
      if (!ok) {
        // Target layer missing — likely deleted between dispatch
        // and effect fire. Roll back so the panel doesn't get
        // stuck.
        endTransform(false);
      } else {
        canvas.applyTransformControls(state.transformMode);
      }
    }
    // The end-transform handlers (apply / cancel) call
    // `canvas.exitTransformMode` themselves with the right
    // `apply` boolean, so the `else` branch is a deliberate
    // no-op.
    //
    // `state.transformMode` is intentionally omitted — the
    // second effect below mirrors mode changes separately, so
    // re-running this on every mode flip would re-enter the
    // session and clobber in-progress overlay state. The
    // project's ESLint config doesn't register
    // `react-hooks/exhaustive-deps` AND uses
    // `--report-unused-disable-directives`, so adding a disable
    // directive for it is itself a lint error — descriptive
    // comment is the only valid expression of intent.
  }, [state.transformActive, state.transformLayerId, canvas]);

  useEffect(() => {
    if (state.transformActive) {
      canvas.applyTransformControls(state.transformMode);
    }
  }, [state.transformMode, state.transformActive, canvas]);

  // Cycle through Retouch variants on Shift+R. Order is sourced
  // from `RETOUCH_VARIANT_ORDER` in RetouchPanel — derived from
  // the chip row, so cycle and visual order can't drift.
  const handleCycleRetouchVariant = useCallback(() => {
    const cur = state.retouchSettings.variant;
    const idx = RETOUCH_VARIANT_ORDER.indexOf(cur);
    const next = RETOUCH_VARIANT_ORDER[(idx + 1) % RETOUCH_VARIANT_ORDER.length];
    updateRetouchSettings({ variant: next });
  }, [state.retouchSettings.variant, updateRetouchSettings]);

  // Cut / Copy / Paste — toolbar buttons + Ctrl+X/C/V shortcuts. Cut and
  // Copy operate on the marqueed region of the active layer's paint
  // surface; Paste creates a new "Pasted" layer placed at the original
  // bbox so the pixels return to where they were copied from.
  const handleCopy = useCallback(() => {
    canvas.copySelection();
  }, [canvas]);

  const handleCut = useCallback(() => {
    if (canvas.cutSelection()) {
      markDirty();
      history.saveSnapshot();
    }
  }, [canvas, markDirty, history]);

  const handlePaste = useCallback(async () => {
    // Center the pasted image on the user's last canvas click. When no
    // click is recorded (Ctrl+V before any canvas interaction), pass
    // `undefined` so addImageLayer falls back to its synchronous
    // imageDimsRef-based "center of document" default. We deliberately
    // do NOT use `state.canvasWidth/Height` here — those are React
    // state and may be 0 if React hasn't committed the LOAD_IMAGE
    // dimensions yet, which would silently send the image to a
    // negative-coords position at the top-left corner.
    const centerAt = canvas.getLastClickPoint() ?? undefined;

    // 1. Fabric internal clipboard first — for in-editor cut/copy/paste of
    //    objects (text/shape/image already in the document). Lossless.
    const item = canvas.getClipboard();
    if (item) {
      const blob = await new Promise<Blob | null>((resolve) => {
        item.canvas.toBlob(resolve, 'image/png');
      });
      if (!blob) return;
      const layer = makeLayer('image', 'Pasted');
      addLayer(layer, true);
      await canvas.addImageLayer(blob, { layerId: layer.id, centerAt });
      // Flag the snapshot effect to capture once React commits the new layer
      // state. We don't call markDirty() / history.saveSnapshot() here — the
      // pending-flag effect handles both, so doing it twice was producing
      // duplicate history entries and requiring two Ctrl+Z to undo a paste.
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Paste';
      return;
    }

    // 2. OS clipboard fallback (e.g. screenshot from outside the editor).
    //    The native `paste` event listener below catches this for Ctrl+V
    //    in some browsers, but the toolbar Paste button — and mobile,
    //    where Ctrl+V isn't an option — depend on this path. Permission
    //    or feature unavailability is silent: failing to paste isn't
    //    surprising enough to warrant an error toast.
    if (!SUPPORTS_OS_CLIPBOARD_READ) return;
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const clipItem of clipboardItems) {
        const imageType = clipItem.types.find((t) => t.startsWith('image/'));
        if (!imageType) continue;
        const blob = await clipItem.getType(imageType);
        const layer = makeLayer('image', 'Pasted');
        addLayer(layer, true);
        await canvas.addImageLayer(blob, { layerId: layer.id, centerAt });
        layerSnapshotPendingRef.current = true;
        layerSnapshotLabelRef.current = 'Paste';
        return;
      }
      toast({
        title: 'Nothing to paste',
        description: 'No image found in the clipboard.',
      });
    } catch {
      // Permission denied (most common on first-use without a user
      // gesture) or transient read failure — no-op silently.
    }
  }, [canvas, addLayer, makeLayer]);

  // Tool switching
  const handleToolSelect = useCallback(
    (tool: EditorTool) => {
      setActiveTool(tool);
      canvas.setTool(tool, state.brushSettings);
      // Mark this tool as already-synced so the activeTool effect
      // skips a redundant `canvas.setTool` call after React commits.
      // See the lastSyncedToolRef declaration above for why this
      // matters (idempotency concerns in setTool's switch cases).
      lastSyncedToolRef.current = tool;

      // Text and Shape tools used to auto-create a placeholder
      // object on tool select. That behavior was removed because
      // it generated unwanted layers when the user picked Text /
      // Shape just to *edit* an existing object's properties (e.g.
      // selecting a shape layer, then clicking the Shape tool to
      // tweak its fill or stroke). Object creation now happens on
      // canvas click via `onShapeToolClick` / `onTextToolClick`
      // — one click selects the tool, the next click on empty
      // canvas drops the new object.

      // AI tool: open the capability picker. Always reopens on click so
      // users can switch capabilities without leaving the AI tool — the
      // sub-panel header has a "Change" button that does the same thing.
      if (tool === 'ai') {
        setAIPickerOpen(true);
      }
    },
    [setActiveTool, setAIPickerOpen, canvas, state.brushSettings],
  );

  useEffect(() => {
    canvas.setMarqueeShape(state.marqueeSettings.shape);
  }, [state.marqueeSettings.shape, canvas]);

  useEffect(() => {
    canvas.setForceSquare(state.marqueeSettings.forceSquare);
  }, [state.marqueeSettings.forceSquare, canvas]);

  useEffect(() => {
    canvas.setMarqueeColor(state.marqueeSettings.color);
  }, [state.marqueeSettings.color, canvas]);

  useEffect(() => {
    canvas.setWandSettings(state.marqueeSettings.tolerance, state.marqueeSettings.contiguous);
  }, [state.marqueeSettings.tolerance, state.marqueeSettings.contiguous, canvas]);

  // Push the active layer id into the canvas hook so new draw strokes get
  // tagged with it (and appear under that layer in the panel).
  useEffect(() => {
    canvas.setActiveLayerId(state.activeLayerId);
  }, [state.activeLayerId, canvas]);

  // Mirror the Move tool's Auto-Select preference into the canvas hook.
  // The Move-tool mouse:down branch reads this every gesture; effect is
  // immediate (a click after the toggle change picks up the new mode).
  useEffect(() => {
    canvas.setAutoSelectLayers(state.autoSelectLayers);
  }, [state.autoSelectLayers, canvas]);

  // Push the React-side edit-target map into the canvas hook. The
  // brush-stroke listener inside Fabric reads from a ref kept in
  // sync this way — without the push, mask painting silently
  // routes to the layer surface every time.
  useEffect(() => {
    canvas.setEditTargets(state.editTargets);
  }, [state.editTargets, canvas]);

  // Tier 1 render-pipeline integration. Phase 2 wires the
  // adjustment-layer slow path through here; mask / non-passthrough
  // group features stay deferred and surface as a dev-only warn.
  // Tier 0-shaped documents (no groups, masks, adjustments) skip
  // the compositor entirely via the `requiresCustomComposite` fast
  // path — zero perf cost for users not on Tier 1 features.
  //
  // rAF-coalesced: rapid `state.document` changes (e.g. dragging
  // a slider in non-destructive edit mode fires UPDATE_ADJUSTMENT
  // at every value change) all collapse into a single composite
  // pass per frame. The compositor walks the whole tree and does
  // ~2x full-canvas getImageData per adjustment, so synchronous
  // per-change would burn frames on any non-trivial canvas size.
  //
  // After the compositor runs, mark every adjustment layer's
  // FabricImage dirty so Fabric reads the freshly-written backing
  // canvas pixels on the next render. Without the dirty flag,
  // Fabric paints the cached version and the user doesn't see
  // the update.
  //
  // Dev-mode timing log: `[image-editor] composite` is printed with
  // the duration so perf regressions surface in devtools as you
  // iterate. Production builds drop the wrapping calls entirely.
  const slowPathRafRef = useRef<number | null>(null);
  // Per-adjustment "composite-below" cache. Keyed by adjustment
  // layer id; persists across renderDocument calls for the lifetime
  // of this editor instance. The cache short-circuits cache-hit
  // adjustments AND skips painting any leaves below them — see
  // `render-pipeline.ts::renderDocument` for the algorithm. Cleared
  // on document/session load so we never reuse stale entries
  // across an open-other-file event.
  const compositorCacheRef = useRef<CompositorCache>(createCompositorCache());
  // Drives `renderDocument`'s `interactiveMode` flag. True when an
  // edit-mode adjustments modal is open, so live-edit slider drags
  // run cache-miss adjustments at viewport-resolution proxy (cheap).
  // The synthetic `handleAdjustmentCommit` and the post-cancel
  // slow-path pass both run at full resolution: commit hardcodes
  // `interactiveMode: false` in `runSlowPathCompositeSync`; cancel
  // closes the modal (clears activeAdjustmentKind) BEFORE its rAF
  // fires, so the ref is back to false by the time `renderDocument`
  // reads it. Eye-toggle / opacity / reorder paths never enter
  // interactive mode — they want full-res cache hits.
  const interactiveCompositorRef = useRef<boolean>(false);
  // Persistent scratch holder for the interactive proxy's downsample
  // / shader-result canvas pair. The compositor reuses these across
  // every renderDocument call (size-tracked, reallocated only when
  // canvas dims change), avoiding ~1.5MB of GC churn per frame
  // during sustained slider drags at 60fps.
  const proxyScratchRef = useRef<ProxyScratchHolder>(createProxyScratchHolder());
  // `useFabricCanvas` returns a useMemo'd object whose identity is
  // gated on 100+ callback deps; in practice it re-memoizes more
  // often than the slow path actually needs. Destructure the three
  // stable pieces this effect uses (a useRef object and two empty-
  // dep useCallbacks) so the effect re-fires only on the things
  // that genuinely require a recomposite. Without this the effect
  // re-fires on every parent re-render, which on a doc with
  // adjustment layers means we schedule a composite every ~16 ms —
  // sustained main-thread block on tool-switch / hover / selection.
  const { canvasRef, paintLeafToCanvas, getAdjustmentBackingCanvas } = canvas;
  // Reconcile adjustment FabricImages against `state.document`. They
  // are `excludeFromExport: true`, so Fabric's `loadFromJSON` (used
  // by session-restore and history undo/redo) does NOT recreate
  // them — the document still lists the adjustment layer, but the
  // Fabric scene has no FabricImage with that layerId. This effect
  // walks the document and creates any missing FabricImage so the
  // slow-path compositor (next effect) has a backing canvas to
  // write into. No-op when every adjustment already has its
  // FabricImage, which is the steady state after fresh adds.
  //
  // Keep this before the slow-path effect — both fire on
  // `state.document` change, React runs them in declaration order,
  // and the compositor needs the FabricImage to exist before it
  // can find a backing canvas.
  const { addAdjustmentLayer } = canvas;
  useEffect(() => {
    const fc = canvasRef.current;
    if (!fc) return;
    // Pass `state.canvasWidth/Height` explicitly — the hook's
    // `imageDimsRef` is only populated by `initEmptyCanvas` / `loadImage`
    // call sites. Session restore and the React StrictMode double-mount
    // both publish `state.document` (with adjustment LayerNodes) BEFORE
    // the canvas-init paths fire — without explicit dims, the backing
    // FabricImage allocation bails on the `w<=0 || h<=0` check and the
    // adjustment never visually applies (2026-05-26 PSD-reopen bug).
    for (const id in state.document.layers) {
      const node = state.document.layers[id];
      if (node.type !== 'adjustment') continue;
      let hasFabricImage = false;
      for (const obj of fc.getObjects()) {
        const objLayerId = (obj as unknown as { layerId?: string }).layerId;
        if (objLayerId === id) {
          hasFabricImage = true;
          break;
        }
      }
      if (!hasFabricImage) {
        addAdjustmentLayer({
          layerId: id,
          width: state.canvasWidth,
          height: state.canvasHeight,
        });
      }
    }
  }, [state.document, state.canvasWidth, state.canvasHeight, canvasRef, addAdjustmentLayer]);

  // Mirror snap-relevant view-settings fields into the canvas
  // hook's snap config. The Fabric `object:moving` handler reads
  // from a ref so toggling a snap setting / adding a guide takes
  // effect on the next mouse-move tick without re-binding the
  // listener. Tier 2 Group D.
  useEffect(() => {
    canvas.setSnapConfig({
      snapToGuides: state.viewSettings.snapToGuides,
      snapToGrid: state.viewSettings.snapToGrid,
      gridSize: state.viewSettings.gridConfig.size,
      guides: state.viewSettings.guides,
    });
  }, [
    canvas,
    state.viewSettings.snapToGuides,
    state.viewSettings.snapToGrid,
    state.viewSettings.gridConfig.size,
    state.viewSettings.guides,
  ]);

  // Mirror the smart-guides toggle into the canvas hook's snap
  // config. Same ref-based pattern as the snap-config mirror
  // above. Tier 2 Group D Smart Guides.
  useEffect(() => {
    canvas.setSmartSnapEnabled(state.viewSettings.smartGuidesEnabled);
  }, [canvas, state.viewSettings.smartGuidesEnabled]);

  // Global Ctrl-held tracker — mirrors window key state into the
  // canvas hook so smart-snap can be temporarily disabled mid-
  // drag without taking effect on the next React render. Same
  // window-listener pattern as the existing altHeld tracker.
  // Holding Ctrl while dragging skips smart-snap (the layer-to-
  // layer pass); existing guide / grid snap continues to fire.
  const setSmartSnapCtrlDisabled = canvas.setSmartSnapCtrlDisabled;
  useEffect(() => {
    const isTypingInto = (target: EventTarget | null): boolean =>
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      (target instanceof HTMLElement && target.isContentEditable);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Control' && e.key !== 'Meta' && !e.ctrlKey && !e.metaKey) return;
      if (isTypingInto(e.target)) return;
      setSmartSnapCtrlDisabled(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      // ANY of these conditions means Ctrl is no longer held
      // (keyup of Control itself, or a keyup of some other key
      // while ctrlKey turned out false in the reported state).
      if (e.key === 'Control' || e.key === 'Meta' || (!e.ctrlKey && !e.metaKey)) {
        setSmartSnapCtrlDisabled(false);
      }
    };
    const onBlur = () => setSmartSnapCtrlDisabled(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      // Reset on unmount so a remount doesn't inherit a stuck
      // disabled state.
      setSmartSnapCtrlDisabled(false);
    };
  }, [setSmartSnapCtrlDisabled]);

  // Mirror "adjustments modal is open" into the interactive-
  // compositor ref. Covers BOTH edit-mode (clicking adjustment
  // thumb in LayersPanel) AND destructive-preview (picker drag) —
  // each tick of either mode invalidates one or more adjustments,
  // and proxy-resolution shader runs cut the per-adjustment cost
  // on 4K from ~90ms to ~10ms. Declared BEFORE the slow-path
  // effect so React runs it first on each commit; by the time the
  // slow-path's rAF fires (always one frame later) the ref reflects
  // the latest state. Modal close clears `activeAdjustmentKind`
  // synchronously, so the post-close slow path picks up
  // `interactiveMode: false` for the final full-resolution pass.
  useEffect(() => {
    interactiveCompositorRef.current = activeAdjustmentKind !== null;
  }, [activeAdjustmentKind]);

  useEffect(() => {
    if (!requiresCustomComposite(state.document)) return;
    if (hasUnimplementedSlowPath(state.document)) {
      if (import.meta.env.DEV) {
        console.warn(
          '[image-editor] document needs the custom compositor; slow-path masks / groups are not yet implemented.',
        );
      }
      return;
    }
    const fabricCanvas = canvasRef.current;
    if (!fabricCanvas) return;
    // Coalesce. If an rAF is already scheduled, drop this fire —
    // the scheduled tick reads the LATEST document via the
    // documentRef (not via closure of the effect that scheduled
    // it), so rapid state.document changes all collapse into a
    // single composite pass against the freshest state.
    if (slowPathRafRef.current !== null) return;
    slowPathRafRef.current = requestAnimationFrame(() => {
      slowPathRafRef.current = null;
      const fc = canvasRef.current;
      if (!fc) return;
      const doc = documentRef.current;
      if (!requiresCustomComposite(doc) || hasUnimplementedSlowPath(doc)) return;
      const devLabel = import.meta.env.DEV ? '[image-editor] composite' : null;
      if (devLabel) console.time(devLabel);
      const stats = renderDocument({
        document: doc,
        width: state.canvasWidth,
        height: state.canvasHeight,
        paintLeaf: paintLeafToCanvas,
        getAdjustmentBacking: getAdjustmentBackingCanvas,
        cache: compositorCacheRef.current,
        proxyScratch: proxyScratchRef.current,
        interactiveMode: interactiveCompositorRef.current,
      });
      if (devLabel) {
        console.timeEnd(devLabel);
        // Hit/miss counts make it obvious at-a-glance whether the
        // cache is doing its job during a session. Production builds
        // tree-shake this entire block via `import.meta.env.DEV`.
        console.log(
          '[image-editor] cache',
          'hits:',
          stats.cacheHits,
          'misses:',
          stats.cacheMisses,
          'interactive:',
          interactiveCompositorRef.current,
        );
      }
      let anyDirty = false;
      for (const id in doc.layers) {
        const node = doc.layers[id];
        if (node.type !== 'adjustment') continue;
        for (const obj of fc.getObjects()) {
          const objLayerId = (obj as unknown as { layerId?: string }).layerId;
          if (objLayerId !== id) continue;
          obj.dirty = true;
          anyDirty = true;
        }
      }
      if (anyDirty) fc.requestRenderAll();
    });
  }, [
    state.document,
    state.canvasWidth,
    state.canvasHeight,
    canvasRef,
    paintLeafToCanvas,
    getAdjustmentBackingCanvas,
  ]);

  // Cancel any pending slow-path rAF on unmount. Without this, a
  // scheduled tick can fire against a torn-down canvas after
  // navigation away from the editor.
  useEffect(
    () => () => {
      if (slowPathRafRef.current !== null) {
        cancelAnimationFrame(slowPathRafRef.current);
        slowPathRafRef.current = null;
      }
    },
    [],
  );

  // Mirror mask configs into the canvas-side hook + re-materialize
  // each layer's clipPath effective alpha whenever its mask config
  // changes. Without this, mask Disable / Invert / Density / Feather
  // updates in `state.document` would never reach the Fabric
  // clipPath — Fabric's render loop honors clipPath alpha, but only
  // after `materializeMaskClipPath` has baked the config into the
  // clipPath element. Cheap when nothing changed (a stable map
  // compare per layer); identity materialize is one drawImage.
  //
  // rAF-coalesced for the same reason the slow-path effect is: a
  // future Density / Feather slider drag will fire UPDATE-style
  // dispatches on every pointer-move, and materialization with
  // non-default config does a getImageData/per-pixel/putImageData
  // round-trip (~30-80ms on 4K). Without coalescing, the per-frame
  // work would stack and jank the drag. The rAF callback reads
  // `documentRef.current` so the latest config wins when rapid
  // changes collapse into one tick.
  //
  // Tracked against `lastPushedMaskConfigsRef` so unrelated
  // `state.document` mutations (brush strokes, layer reorders,
  // etc.) don't trigger materialization for masks whose config
  // didn't change. Diff compares every LayerMask field via Object
  // .keys so a future field addition (e.g. linked semantics,
  // colorOverlay) is picked up automatically without editing the
  // effect.
  const lastPushedMaskConfigsRef = useRef<Map<string, LayerMask>>(new Map());
  const maskSyncRafRef = useRef<number | null>(null);
  useEffect(() => {
    if (maskSyncRafRef.current !== null) return;
    maskSyncRafRef.current = requestAnimationFrame(() => {
      maskSyncRafRef.current = null;
      const doc = documentRef.current;
      const liveIds = new Set<string>();
      for (const id in doc.layers) {
        const node = doc.layers[id];
        const mask = node.mask;
        if (!mask) continue;
        liveIds.add(id);
        const prev = lastPushedMaskConfigsRef.current.get(id);
        const unchanged =
          prev !== undefined &&
          (Object.keys(mask) as (keyof LayerMask)[]).every((k) => prev[k] === mask[k]);
        if (unchanged) continue;
        canvas.setMaskConfig(id, mask);
        canvas.materializeMaskClipPath(id, mask);
        lastPushedMaskConfigsRef.current.set(id, { ...mask });
      }
      // Drop entries for layers whose mask was removed since last
      // push. The canvas-side `removeLayerMask` already clears its
      // mirror; this keeps our diff-tracking accurate so a future
      // re-add starts from a clean compare.
      for (const id of Array.from(lastPushedMaskConfigsRef.current.keys())) {
        if (!liveIds.has(id)) {
          lastPushedMaskConfigsRef.current.delete(id);
          canvas.setMaskConfig(id, null);
        }
      }
    });
  }, [state.document, canvas]);

  // Cancel pending mask-sync rAF on unmount so it can't fire against
  // a torn-down canvas. Mirrors the slow-path effect's cleanup.
  useEffect(
    () => () => {
      if (maskSyncRafRef.current !== null) {
        cancelAnimationFrame(maskSyncRafRef.current);
        maskSyncRafRef.current = null;
      }
    },
    [],
  );

  // Vector-mask sync (Tier 2 Group A PR-D). Walks layers with a
  // vector mask; whenever the mask's vectorPath changes, re-
  // rasterizes into the mask's RAW canvas and re-materializes
  // EFFECTIVE. The raster-mask sync above handles config-only
  // changes (feather/density/invert/enabled); this effect handles
  // path geometry changes specifically. Both run in tandem — a
  // single state.document mutation that flips kind AND changes
  // config flows through both.
  //
  // Identity check: stored reference equality on the vectorPath.
  // The reducer creates a fresh `LayerMask` object on every
  // `SET_MASK_VECTOR_PATH` dispatch (and `CONVERT_MASK_KIND` when
  // entering vector kind), so the ref comparison catches every
  // path change without deep-comparing the anchor list.
  // `lastPushedMaskVectorPathRef` is declared at component top
  // so creation handlers (which fire above this effect's line
  // number) can pre-seed it.
  const vectorMaskSyncRafRef = useRef<number | null>(null);
  useEffect(() => {
    if (vectorMaskSyncRafRef.current !== null) return;
    vectorMaskSyncRafRef.current = requestAnimationFrame(() => {
      vectorMaskSyncRafRef.current = null;
      const doc = documentRef.current;
      const liveIds = new Set<string>();
      for (const id in doc.layers) {
        const node = doc.layers[id];
        const mask = node.mask;
        if (!mask || mask.kind !== 'vector' || !mask.vectorPath) continue;
        liveIds.add(id);
        const prev = lastPushedMaskVectorPathRef.current.get(id);
        if (prev === mask.vectorPath) continue;
        canvas.setMaskVectorPath(id, mask.vectorPath, mask);
        lastPushedMaskVectorPathRef.current.set(id, mask.vectorPath);
      }
      // Drop entries for layers whose mask is gone OR whose mask
      // is no longer vector — keeps the diff cache accurate
      // across kind switches.
      for (const id of Array.from(lastPushedMaskVectorPathRef.current.keys())) {
        if (!liveIds.has(id)) lastPushedMaskVectorPathRef.current.delete(id);
      }
    });
  }, [state.document, canvas]);

  useEffect(
    () => () => {
      if (vectorMaskSyncRafRef.current !== null) {
        cancelAnimationFrame(vectorMaskSyncRafRef.current);
        vectorMaskSyncRafRef.current = null;
      }
    },
    [],
  );

  // Text-path binding sync (Tier 2 Group A PR-E). Walks text layers
  // with `textPath` set; rebuilds the `fabric.IText.path` from the
  // referenced vector layer's geometry whenever either the binding
  // OR the underlying vector path changes. rAF-coalesced so a Path
  // Edit drag that fires many SET_VECTOR_PATH dispatches per second
  // collapses to one rebuild per frame.
  //
  // Auto-unbind on missing target: the reducer's REMOVE_LAYER cascade
  // already clears `textPath` when its referenced vector layer is
  // deleted, so this loop just rechecks defensively (the vector may
  // also be deleted via paths the cascade doesn't cover, e.g. a
  // future full-document replace). Missing vector → unbind via
  // `setTextPathBinding(textId, null)`.
  //
  // Track via the path-data string (NOT the VectorPath object) so
  // anchor-handle edits inside Path Edit retrigger the sync — the
  // VectorPath object identity changes on every reducer mutation,
  // but the SVG-data string captures the actual geometry, letting
  // us skip a fabric.Path rebuild when only metadata changed.
  const lastPushedTextPathRef = useRef<Map<string, { pathLayerId: string; svgData: string }>>(
    new Map(),
  );
  const textPathSyncRafRef = useRef<number | null>(null);
  useEffect(() => {
    if (textPathSyncRafRef.current !== null) return;
    textPathSyncRafRef.current = requestAnimationFrame(() => {
      textPathSyncRafRef.current = null;
      const doc = documentRef.current;
      const liveTextIds = new Set<string>();
      for (const id in doc.layers) {
        const node = doc.layers[id];
        if (node.type !== 'text') continue;
        const binding = node.textPath;
        if (!binding) continue;
        liveTextIds.add(id);
        const vectorNode = doc.layers[binding.pathLayerId];
        if (!vectorNode || vectorNode.type !== 'vector' || !vectorNode.path) {
          // Defensive unbind — reducer cascade should have handled
          // the common path, but be safe against partial state.
          canvas.setTextPathBinding(id, null);
          lastPushedTextPathRef.current.delete(id);
          continue;
        }
        const svgData = pathToSvgData(vectorNode.path);
        const prev = lastPushedTextPathRef.current.get(id);
        if (prev && prev.pathLayerId === binding.pathLayerId && prev.svgData === svgData) {
          continue;
        }
        if (canvas.setTextPathBinding(id, svgData)) {
          lastPushedTextPathRef.current.set(id, {
            pathLayerId: binding.pathLayerId,
            svgData,
          });
          if (import.meta.env.DEV) {
            console.debug(
              `[image-editor] text-path-sync: rebound ${id} → ${binding.pathLayerId}` +
                ` (${svgData.length} chars, prev=${prev ? 'set' : 'none'})`,
            );
          }
        }
      }
      // Drop entries for layers that no longer have a binding (text
      // layer removed, or textPath was set to null via Unbind).
      for (const id of Array.from(lastPushedTextPathRef.current.keys())) {
        if (!liveTextIds.has(id)) {
          canvas.setTextPathBinding(id, null);
          lastPushedTextPathRef.current.delete(id);
        }
      }
    });
  }, [state.document, canvas]);

  // Cancel pending text-path-sync rAF on unmount.
  useEffect(
    () => () => {
      if (textPathSyncRafRef.current !== null) {
        cancelAnimationFrame(textPathSyncRafRef.current);
        textPathSyncRafRef.current = null;
      }
    },
    [],
  );

  // Push the latest brush color so the Fill tool can use it on click.
  useEffect(() => {
    canvas.setBrushColor(state.brushSettings.color);
  }, [state.brushSettings.color, canvas]);

  // Live-update the active drawing brush whenever any brush setting changes,
  // so the Draw / Eraser tool's next stroke uses the current values without
  // requiring a tool re-pick.
  useEffect(() => {
    canvas.updateBrush(state.brushSettings);
  }, [state.brushSettings, canvas]);

  // Bidirectional shape-settings sync:
  //   1) When `state.activeLayerId` changes, read the active shape's actual
  //      properties and mirror them into `state.shapeSettings`. The Shape
  //      panel always shows the selected shape's properties.
  //   2) When `state.shapeSettings` changes (e.g., user moved a slider),
  //      apply those settings to the active shape.
  // Both effects use prev-refs to guard against the `canvas` dep changing
  // identity on every render (it's a fresh object literal from
  // `useFabricCanvas`). Without these guards, the sync effect dispatches on
  // every render → infinite loop. The `syncingFromCanvasRef` flag prevents
  // the apply-effect from re-applying settings just read from the canvas.
  const prevShapeSettingsRef = useRef(state.shapeSettings);
  const prevActiveLayerIdForShapeRef = useRef<string | null | undefined>(undefined);
  const syncingShapeFromCanvasRef = useRef(false);

  useEffect(() => {
    if (prevActiveLayerIdForShapeRef.current === state.activeLayerId) return;
    prevActiveLayerIdForShapeRef.current = state.activeLayerId;
    const fromCanvas = canvas.getActiveShapeSettings();
    if (!fromCanvas) return;
    syncingShapeFromCanvasRef.current = true;
    updateShapeSettings(fromCanvas);
  }, [state.activeLayerId, canvas, updateShapeSettings]);

  useEffect(() => {
    if (prevShapeSettingsRef.current === state.shapeSettings) return;
    prevShapeSettingsRef.current = state.shapeSettings;
    if (syncingShapeFromCanvasRef.current) {
      // The latest shapeSettings update came from reading the active shape;
      // skip the round-trip apply.
      syncingShapeFromCanvasRef.current = false;
      return;
    }
    canvas.updateActiveShape(state.shapeSettings);
    // Vector primitives (polygon / star / arrow) regenerate live
    // when their parameters change. The marker `vectorSource` on
    // the layer prevents this from clobbering pen-drawn paths —
    // those have no source kind and are left untouched. We only
    // regenerate when the slider's primitive kind matches the
    // layer's source kind (so switching the chip from polygon to
    // rect doesn't turn a polygon into a degenerate rect; the
    // user deletes + recreates for cross-primitive type changes).
    if (state.activeLayerId) {
      const node = state.document.layers[state.activeLayerId];
      if (node?.type === 'vector' && node.path) {
        if (shouldRegenerateVectorPrimitive(node, state.shapeSettings.type)) {
          // Geometry regenerate (and style — generateVectorShapePath
          // bakes stroke + fill from settings into the new path).
          // Only fires for vector primitives whose marker matches
          // the active chip; pen-drawn paths and custom-edited
          // primitives skip this branch.
          const old = canvas.getActiveVectorBounds(state.activeLayerId);
          if (old) {
            // Use the min half-axis so a non-uniformly-scaled
            // primitive doesn't get stretched further on regen;
            // the visual size matches the layer's current shorter
            // axis. Width/height in `bbox` are already in
            // scene-space coords thanks to `getBoundingRect`.
            const halfExtent = Math.max(1, Math.min(old.bbox.width, old.bbox.height) / 2);
            const path = generateVectorShapePath(state.shapeSettings, old.center, halfExtent);
            setVectorPath(state.activeLayerId, path);
            canvas.replaceVectorObject(state.activeLayerId, path);
            layerSnapshotContinuousRef.current = true;
          }
        } else {
          // Style-only sync — applies to ANY vector layer (pen-
          // drawn paths, custom-edited primitives, and primitives
          // whose chip doesn't match the layer's source kind).
          // Geometry stays put; stroke / fill / width update on
          // both the canvas object and the document path.
          const stroke = {
            color: state.shapeSettings.stroke,
            width: state.shapeSettings.strokeWidth,
          };
          const fill = state.shapeSettings.fill;
          const styleChanged =
            node.path.stroke?.color !== stroke.color ||
            node.path.stroke?.width !== stroke.width ||
            node.path.fill !== fill;
          if (styleChanged) {
            canvas.updateVectorStyle(state.activeLayerId, {
              stroke: stroke.color,
              strokeWidth: stroke.width,
              fill,
            });
            setVectorPath(state.activeLayerId, { ...node.path, stroke, fill });
            layerSnapshotContinuousRef.current = true;
          }
        }
      }
    }
  }, [state.shapeSettings, state.activeLayerId, state.document, canvas, setVectorPath]);

  // Same bidirectional sync for the Text tool's settings.
  const prevTextSettingsRef = useRef(state.textSettings);
  const prevActiveLayerIdForTextRef = useRef<string | null | undefined>(undefined);
  const syncingTextFromCanvasRef = useRef(false);
  // Tier 2 Group B (2026-05-14) — debounce timer for the
  // post-text-change history snapshot. See the comment in the
  // text-sync effect for why it can't fire inline.
  const textSettingsSnapshotTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevActiveLayerIdForTextRef.current === state.activeLayerId) return;
    prevActiveLayerIdForTextRef.current = state.activeLayerId;
    // Tier 2 Group B (2026-05-14) — pass `state.activeLayerId` so
    // the lookup falls back to searching by layer id when no
    // Fabric active object is set (the case immediately after
    // `loadFromJSON` on session restore). Without this, the
    // PropertiesPanel showed defaults on reload until the user
    // clicked off and back onto the text layer.
    const fromCanvas = canvas.getActiveTextSettings(state.activeLayerId);
    if (!fromCanvas) return;
    syncingTextFromCanvasRef.current = true;
    updateTextSettings(fromCanvas);
  }, [state.activeLayerId, canvas, updateTextSettings]);

  useEffect(() => {
    if (prevTextSettingsRef.current === state.textSettings) return;
    prevTextSettingsRef.current = state.textSettings;
    if (syncingTextFromCanvasRef.current) {
      syncingTextFromCanvasRef.current = false;
      return;
    }
    canvas.updateActiveText(state.textSettings);
    // Tier 2 Group B (2026-05-14) — programmatic `.set()` doesn't
    // fire Fabric's `object:modified`, so neither the dirty flag
    // nor the history snapshot get updated here. Without this,
    // on reload `restoreSession` runs `restoreHistory` which calls
    // `loadFromJSON` on the latest history entry — and that entry
    // doesn't have the text-styling changes (since no
    // `saveSnapshot` ran). The result is the canvas reverts to
    // pre-change state regardless of what's in the IDB session.
    //
    // Inline debounce (not via the shared continuous-snapshot
    // ref) — that ref is consumed by an effect declared BEFORE
    // this one, so on the same render the consumer would run
    // before we could set the ref. Keeping the debounce local
    // sidesteps the effect-ordering trap. ~250ms feels like
    // "slider drag pause = commit".
    markDirty();
    if (textSettingsSnapshotTimerRef.current !== null) {
      window.clearTimeout(textSettingsSnapshotTimerRef.current);
    }
    textSettingsSnapshotTimerRef.current = window.setTimeout(() => {
      textSettingsSnapshotTimerRef.current = null;
      history.saveSnapshot();
    }, 250);
  }, [state.textSettings, canvas, history, markDirty]);

  // Bidirectional pen-settings ↔ active vector layer sync.
  //
  // Without this, PenPanel's stroke / fill / width controls only set
  // defaults for the NEXT path drawn — a user editing them while a
  // pen-drawn vector layer is selected sees no effect on that layer.
  // (`getActiveShapeSettings` only handles the four primitive shape
  // classes — `Rect`, `Circle`, `Triangle`, `Line` — so the shape
  // sync above bypasses `fabric.Path` entirely. Pen-drawn paths fall
  // through to this dedicated sync.)
  //
  // The pull-from-canvas effect ONLY sets `syncingPenFromCanvasRef`
  // when the values actually differ — otherwise `updatePenSettings`
  // would no-op the state update, the apply-effect would never run,
  // and the flag would still be `true` on the user's next genuine
  // edit, silently swallowing it. The shape sync has this same
  // latent quirk but rarely trips it because `shapeSettings`
  // typically differs between layers; pen-drawn paths created via
  // the current panel start out byte-identical so it would trip
  // every time.
  const prevPenSettingsRef = useRef(state.penSettings);
  const prevActiveLayerIdForPenRef = useRef<string | null | undefined>(undefined);
  const syncingPenFromCanvasRef = useRef(false);

  useEffect(() => {
    if (prevActiveLayerIdForPenRef.current === state.activeLayerId) return;
    prevActiveLayerIdForPenRef.current = state.activeLayerId;
    if (!state.activeLayerId) return;
    const node = state.document.layers[state.activeLayerId];
    if (node?.type !== 'vector') return;
    const fromCanvas = canvas.getVectorLayerStyle(state.activeLayerId);
    if (!fromCanvas) return;
    const needsUpdate =
      fromCanvas.stroke !== state.penSettings.strokeColor ||
      fromCanvas.strokeWidth !== state.penSettings.strokeWidth ||
      fromCanvas.fill !== state.penSettings.fill;
    if (!needsUpdate) return;
    syncingPenFromCanvasRef.current = true;
    updatePenSettings({
      strokeColor: fromCanvas.stroke,
      strokeWidth: fromCanvas.strokeWidth,
      fill: fromCanvas.fill,
    });
  }, [state.activeLayerId, state.document, state.penSettings, canvas, updatePenSettings]);

  useEffect(() => {
    if (prevPenSettingsRef.current === state.penSettings) return;
    prevPenSettingsRef.current = state.penSettings;
    if (syncingPenFromCanvasRef.current) {
      syncingPenFromCanvasRef.current = false;
      return;
    }
    if (!state.activeLayerId) return;
    const node = state.document.layers[state.activeLayerId];
    if (node?.type !== 'vector' || !node.path) return;
    const stroke = {
      color: state.penSettings.strokeColor,
      width: state.penSettings.strokeWidth,
    };
    const fill = state.penSettings.fill;
    const styleChanged =
      node.path.stroke?.color !== stroke.color ||
      node.path.stroke?.width !== stroke.width ||
      node.path.fill !== fill;
    if (!styleChanged) return;
    canvas.updateVectorStyle(state.activeLayerId, {
      stroke: stroke.color,
      strokeWidth: stroke.width,
      fill,
    });
    setVectorPath(state.activeLayerId, { ...node.path, stroke, fill });
    layerSnapshotContinuousRef.current = true;
  }, [state.penSettings, state.activeLayerId, state.document, canvas, setVectorPath]);

  // Tier 2 Group B (2026-05-14) — flush pending text-settings
  // snapshots + IDB save on tab hide / page unload. Without this,
  // a user who changes a text setting and refreshes within 250ms
  // loses the change: the 250ms history-snapshot timer never
  // fires, no `saveSnapshot` runs, the IDB session-save timer
  // (800ms) also doesn't fire, and the latest history entry
  // (which `restoreHistory` replays on reload) doesn't have the
  // change. `visibilitychange` runs before `beforeunload` in
  // Chromium and is reliable across tab-close + refresh paths;
  // `pagehide` is the Safari fallback.
  //
  // The IDB write itself is async — modern browsers attempt to
  // flush queued IDB writes during unload but don't guarantee it.
  // We call `saveSession(...)` synchronously which kicks off the
  // write request; the browser usually completes it before the
  // page actually unloads.
  useEffect(() => {
    const flush = () => {
      if (textSettingsSnapshotTimerRef.current !== null) {
        window.clearTimeout(textSettingsSnapshotTimerRef.current);
        textSettingsSnapshotTimerRef.current = null;
        history.saveSnapshot();
      }
      // Flush the debounced last-active-id hint so reload picks the
      // right tab even if the user reloads within the 200ms debounce
      // window after the last tab switch.
      if (lastActiveHintTimerRef.current !== null) {
        window.clearTimeout(lastActiveHintTimerRef.current);
        lastActiveHintTimerRef.current = null;
        const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
        if (activeTab) void saveLastActiveDocumentId(activeTab.documentId);
      }
      if (!hasImage) return;
      // Visibility flush / page-hide: canvas reflects the active tab,
      // so the active tab's documentId is the right key for the IDB
      // record this snapshot writes. (Contrast with the tab-switch
      // effect below, which must pass the OUTGOING tab's id.)
      const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
      if (!activeTab) return;
      const snapshot = buildSnapshot(activeTab.documentId);
      if (snapshot) void saveSession(snapshot);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, [history, hasImage, buildSnapshot, tabs.tabs, tabs.activeTabId]);

  // Clipboard paste — extracts an image from the OS clipboard and creates a new image layer.
  useEffect(() => {
    const onPaste = async (e: ClipboardEvent) => {
      // Skip if focus is in an input/textarea (the user is pasting text into a form)
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      if (!e.clipboardData) return;
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (!blob) continue;
          e.preventDefault();
          const layer = makeLayer('image');
          addLayer(layer, true);
          // See handlePaste for the rationale on `centerAt` vs.
          // `state.canvasWidth/Height` — we let addImageLayer's
          // synchronous imageDimsRef-based default take over when
          // there's no recorded click.
          const centerAt = canvas.getLastClickPoint() ?? undefined;
          await canvas.addImageLayer(blob, { layerId: layer.id, centerAt });
          layerSnapshotPendingRef.current = true;
          layerSnapshotLabelRef.current = 'Paste';
          break;
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [canvas, addLayer, makeLayer]);

  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    // CRITICAL: `restoredRef.current = true` MUST stay AFTER the async
    // work completes, not here. Setting it eagerly used to break the
    // entire session-restore path under React 18 StrictMode dev
    // double-mount:
    //   1. First mount setup sets ref → kicks off async A.
    //   2. First mount cleanup runs → abort.abort() aborts A.
    //   3. Second mount setup sees ref already true → returns early.
    //   4. A's `await` resumes → `abort.signal.aborted === true` → A
    //      exits without adding any tabs.
    //   Net: NO tabs restored. Empty-state UI shown despite a valid
    //   session record sitting in IDB. (Production wasn't affected —
    //   no StrictMode there — so the regression slipped through.)
    // Setting the ref only AFTER addTab completes turns it into a
    // "completed" gate: aborted attempts leave it false, the second
    // mount re-attempts cleanly, and a successfully-completed restore
    // blocks subsequent re-runs of the effect.
    const abort = new AbortController();
    void (async () => {
      // Tier 2 Group F — multi-tab restore. Each tab persists its own
      // session record keyed by documentId; `loadAllSessions` returns
      // them in savedAt order. The `lastActiveDocumentId` meta hint
      // tells us which tab to make active (the one the user was on
      // when the page last unloaded). Tabs that aren't last-active
      // get seeded with `activate: false` so they stay inactive until
      // the user switches.
      const [sessions, lastActiveId] = await Promise.all([
        loadAllSessions(),
        loadLastActiveDocumentId(),
      ]);
      if (abort.signal.aborted) return;

      // Tier 2 Group F — best-effort cleanup of PSD-stash entries
      // whose document was lost to a crash (no matching session
      // record). Runs once at startup; tab-close + last-tab-close
      // paths run their own atomic deletes. Fire-and-forget — a
      // failed GC isn't user-visible, just a slow IDB leak.
      void gcOrphanedStashes(sessions.map((s) => s.documentId));

      if (sessions.length === 0) {
        restoredRef.current = true;
        return;
      }

      // Resolve which session becomes active. Default to the most-
      // recently-saved if the hint is missing or points at a deleted
      // tab — `sessions` is savedAt-ascending, so last() is newest.
      const activeSession =
        sessions.find((s) => s.documentId === lastActiveId) ?? sessions[sessions.length - 1];
      for (const session of sessions) {
        if (abort.signal.aborted) return;
        // Seed the session as the tab's initial snapshot. The tab-switch
        // effect (below) will pick up the active one on the next render
        // and call applySnapshot — this single source of truth avoids
        // racing resetEditorToEmpty when the effect fires concurrently
        // with a direct applySnapshot call here.
        tabs.addTab({
          fileName: session.fileName,
          fileId: session.fileId,
          documentId: session.documentId,
          // Optional in PersistedSession (added later); back-compat
          // for older records is `undefined` → null on the tab,
          // matching the pre-field code path's Export-dialog fallback.
          sourceFileName: session.sourceFileName ?? null,
          snapshot: session,
          activate: session === activeSession,
        });
      }
      // Mark complete only when we actually got here — every prior
      // early-return was either "no work to do" (sessions.length === 0
      // path above sets it before returning) or an abort (caller will
      // re-run on next mount).
      restoredRef.current = true;
    })();
    return () => {
      abort.abort();
    };
  }, []);

  // Tab switching. When the active tab id changes, save the outgoing tab's
  // editor state and apply the incoming tab's snapshot (or reset the editor
  // for a freshly-created tab with no snapshot yet).
  //
  // Tier 2 Group F — persist the outgoing tab's snapshot to IDB too, not
  // just into React state. Without this, an inactive tab's state lives
  // only in React; a crash / accidental browser close between tab switches
  // would lose it. Per-tab IDB records (keyed by documentId) make every
  // tab survivable across reload.
  //
  // We list every closed-over value as a dep, but the body short-circuits
  // on `prevId === newId` so non-id changes (e.g. setTabSnapshot updating
  // tabs.tabs) cost only the dep comparison.
  const prevActiveTabIdRef = useRef<string | null>(null);
  // Debounce timer for the `lastActiveDocumentId` IDB write. Rapid
  // tab-switch clicks coalesce to one write; the previous-pending
  // write is cancelled on each new switch. 200ms is long enough to
  // batch a typical click-through but short enough that a quick
  // reload doesn't lose the hint.
  const lastActiveHintTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const prevId = prevActiveTabIdRef.current;
    const newId = tabs.activeTabId;
    if (prevId === newId) return;
    prevActiveTabIdRef.current = newId;

    // Capture the OUTGOING tab synchronously, BEFORE the async block.
    // By the time the async block runs `buildSnapshot`, React has
    // already advanced `tabs.activeTabId` to the incoming tab, so
    // looking up the outgoing tab via activeTabId would be wrong.
    // Resolving the outgoing TabDoc here closes over the correct
    // reference for the whole async lifecycle.
    const outgoingTab = prevId !== null ? (tabs.tabs.find((t) => t.id === prevId) ?? null) : null;

    void (async () => {
      // 1. Capture outgoing tab's state. Mirror to BOTH React state
      // (so the next switch-back is instant — no IDB round trip) AND
      // IDB (so a reload restores it). `outgoingTab.documentId` is
      // the OUTGOING tab's IDB key — passing it explicitly avoids
      // the activeTabId-already-advanced race.
      if (outgoingTab && hasImage) {
        const outgoing = buildSnapshot(outgoingTab.documentId);
        if (outgoing) {
          tabs.setTabSnapshot(outgoingTab.id, outgoing);
          void saveSession(outgoing);
        }
      }

      // 2. Restore incoming tab.
      if (newId === null) {
        // No active tab — last tab was closed. Wipe ALL persisted sessions
        // so a refresh starts empty instead of restoring an image the
        // user just closed (and possibly inheriting any half-baked Fabric
        // state from a stale snapshot, which has been observed to break
        // the marquee + move tools on the next open).
        void clearAllSessions();
        resetEditorToEmpty();
        return;
      }
      // Record the new active id as the last-active hint so reload
      // restores the same tab as active. Debounced — every click
      // through 5 tabs would otherwise queue 5 IDB writes; we only
      // need the LAST one to persist. 200ms covers rapid-fire clicks
      // without delaying the hint past the next plausible reload.
      const incoming = tabs.tabs.find((t) => t.id === newId);
      if (incoming) {
        if (lastActiveHintTimerRef.current !== null) {
          window.clearTimeout(lastActiveHintTimerRef.current);
        }
        const docId = incoming.documentId;
        lastActiveHintTimerRef.current = window.setTimeout(() => {
          lastActiveHintTimerRef.current = null;
          void saveLastActiveDocumentId(docId);
        }, 200);
      }
      if (incoming?.snapshot) {
        await applySnapshot(incoming.snapshot);
        // Snapshot is now live in the editor — clear it so the inactive
        // copy doesn't drift.
        tabs.setTabSnapshot(newId, null);
      } else {
        // Freshly-created tab with no content yet. Reset and wait for
        // loadFile / handleFiasImageSelected to populate the editor.
        resetEditorToEmpty();
      }
    })();
  }, [
    tabs.activeTabId,
    tabs.tabs,
    tabs.setTabSnapshot,
    hasImage,
    buildSnapshot,
    applySnapshot,
    resetEditorToEmpty,
  ]);

  // Mirror file-related editor state onto the active tab so the tab label
  // reflects renames + dirty status without the user opening the tab.
  useEffect(() => {
    if (!tabs.activeTabId) return;
    tabs.updateActiveTabMetadata({
      fileName: state.fileName,
      fileId: state.fileId,
      isDirty: state.isDirty,
    });
  }, [state.fileName, state.fileId, state.isDirty, tabs.activeTabId]);

  // Debounced session save. Keyed on canvas mutations (history counts), file
  // metadata, filters, and zoom — so the user's last view (zoom + pan) is
  // preserved when paired with any other change. Persists the ACTIVE tab's
  // state to its own IDB record (keyed by `documentId`). Inactive tabs are
  // persisted on tab-switch — their React-state snapshot rides along to IDB
  // via the tab-switch effect above (Tier 2 Group F).
  //
  // Window-level pointer tracking — feeds the in-flight gate on the IDB
  // save below. Window (not the canvas wrapper) so a pointerup released
  // outside the canvas still clears the flag; `pointercancel` catches
  // the touch-cancel / lost-pointer-capture edge cases. The browser
  // guarantees a pointer-down is followed by exactly one of pointerup or
  // pointercancel, so the flag can't get stuck.
  useEffect(() => {
    const bump = () => {
      lastPointerActivityAtRef.current = Date.now();
    };
    const onDown = () => {
      interactionInFlightRef.current = true;
      bump();
    };
    const onMove = () => {
      // Only meaningful while a pointer is down — the autosave gate
      // looks at "user idle from interaction"; idle mouse cursors
      // moving around the chrome shouldn't postpone saves.
      if (interactionInFlightRef.current) bump();
    };
    const onUp = () => {
      interactionInFlightRef.current = false;
      bump();
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  // Tier 2 Group B (2026-05-14) — `state.textSettings` added to the dep
  // list. Text styling changes mutate the active IText via the text-sync
  // effect's `canvas.updateActiveText()`, but a programmatic `.set()` does
  // NOT fire Fabric's `object:modified` event, so `history.saveSnapshot()`
  // never runs and the save was never triggered. Slider drags coalesce
  // through the 800 ms debounce below. Same applies to the arch / font
  // changes that go through the same effect — they were silently failing
  // to persist before this fix.
  //
  // Pointer-in-flight gate: `buildSnapshot` calls `canvas.toJSON(...)` which
  // serializes every FabricImage (paint surfaces, masks). With the
  // dataURL cache from earlier commits, unchanged surfaces serve cached
  // bytes — but the active surface still re-encodes, and on a huge canvas
  // that's a tens-of-ms hitch. If that hitch lands mid-stroke, the
  // browser queues pointer events and they flush as a burst on unblock —
  // the brush line "jumps". Defense-in-depth: the timer callback below
  // re-checks `interactionInFlightRef` and reschedules itself instead of
  // firing while a pointer is down. Cache miss for one surface is short
  // enough that this is theoretical for typical sizes, but it's free
  // insurance for the extremes.
  useEffect(() => {
    if (!hasImage) return;
    let timer: ReturnType<typeof setTimeout>;
    // Max time we'll defer the save while waiting for the pointer to
    // come up. Pathological cases (stuck stylus, accessibility tool
    // holding click, a frozen tab where pointerup never fires) would
    // otherwise reschedule forever and the autosave would silently
    // never land — a tab crash mid-defer means lost work. 30s strikes
    // the balance: long enough that normal long strokes don't hit
    // the cap, short enough that pathological cases still get saved
    // before a typical "did you mean to close this tab?" timeout.
    const startedAt = Date.now();
    const MAX_DEFER_MS = 30_000;
    // Don't fire the save unless the user has been pointer-idle for
    // this long. Eyeballed against `buildSnapshot`'s ~400ms first-run
    // cost (paint-surface dataURL encode) — by the time we cross this
    // threshold we're confident the user isn't mid-gesture. Diagnostic
    // logs showed the prior pointer-state-only gate firing the save
    // between mouse:up and the next mouse:down, with the 400ms hitch
    // landing right on top of the user's next press and getting
    // perceived as "pause every ~800ms during marquee drag."
    const POINTER_IDLE_MS = 800;
    const tryFire = () => {
      const inFlight = interactionInFlightRef.current || canvas.getIsCanvasInteracting();
      const sinceLastInteraction = Date.now() - lastPointerActivityAtRef.current;
      const idleEnough = !inFlight && sinceLastInteraction >= POINTER_IDLE_MS;
      if (!idleEnough && Date.now() - startedAt < MAX_DEFER_MS) {
        // Either a pointer is currently down OR the user was just
        // interacting and might be about to press again. Defer.
        // Short poll interval so the save fires shortly after the
        // user goes quiet without burning cycles.
        timer = setTimeout(tryFire, 200);
        return;
      }
      // Debounced save: canvas reflects the currently-active tab, so
      // its documentId is the correct IDB key. Tab-switching has its
      // own save path that handles the outgoing-tab case.
      const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
      if (!activeTab) return;
      const snapshot = buildSnapshot(activeTab.documentId);
      if (snapshot) void saveSession(snapshot);
    };
    timer = setTimeout(tryFire, 800);
    return () => clearTimeout(timer);
  }, [
    buildSnapshot,
    hasImage,
    tabs.tabs,
    tabs.activeTabId,
    state.fileName,
    state.fileId,
    state.zoom,
    state.layers,
    state.activeLayerId,
    state.textSettings,
    history.undoCount,
    history.redoCount,
  ]);

  // Refund toast on AI failure. invokeEntity refunds reserved credits
  // server-side whenever the executor errors, so any aiStatus error
  // means the user has been made whole. We surface that explicitly so
  // a failed generation doesn't feel like a lost charge. Fires once
  // per error: each setAiStatus({ kind: 'error', ... }) creates a new
  // object reference, so the effect runs on every fresh failure even
  // if the message string is identical.
  useEffect(() => {
    if (aiStatus.kind === 'error') {
      toast({
        title: 'Generation failed',
        description: `${aiStatus.message} — any credits reserved have been refunded.`,
        variant: 'destructive',
      });
    }
  }, [aiStatus]);

  // ─── Tier 3 Group C Phase 1 — Command Registry ────────────────
  // The registry holds every editor command (keyboard shortcuts,
  // toolbar entries, command-palette actions). Single dispatch path
  // means no parallel keydown switch and no risk of toolbar /
  // keyboard / palette getting out of sync.
  //
  // Handlers ref pattern: every command's `run` body closes over
  // `commandHandlersRef.current`, which is reassigned every render
  // with fresh closures. The register-all effect runs once with `[]`
  // deps, so adding state to a handler does NOT churn the registry.
  //
  // Trade-off: 35+ arrow allocations per render. Acceptable because
  // (a) the closures live in a ref — they never become props or
  // context values, so they can't cause downstream re-renders; (b)
  // the alternative (useMemo with explicit deps) would need ~10
  // deps and re-allocate whenever any of them changed anyway, with
  // worse readability. If profiling later shows this in a hot path,
  // migrate to useMemo — the win is purely allocation count, not
  // render count.
  const commandHandlersRef = useRef<EditorCommandHandlers>(
    null as unknown as EditorCommandHandlers,
  );
  commandHandlersRef.current = {
    // edit
    undo: () => {
      flushContinuousSnapshot();
      history.undoCanvas();
    },
    redo: () => {
      flushContinuousSnapshot();
      history.redoCanvas();
    },
    cut: handleCut,
    copy: handleCopy,
    paste: () => void handlePaste(),
    deleteSelected: () => canvas.deleteSelected(),
    // select
    selectAll: () => canvas.selectAll(),
    invertSelection: handleInvertSelection,
    // file — wrapped in arrows because the handler consts are
    // declared later in this function body; direct references at
    // this line are TDZ violations even though the arrows only run
    // when dispatched.
    newDocument: () => handleNewBlankFile(),
    openNewFromTemplate: () => setTemplatePickerOpen(true),
    openLocal: () => handleOpenFile(),
    openFias: () => handleOpenFias(),
    save: () => void handleToolbarSave(),
    // handleSaveAs picks the export destination based on the active
    // tab's source (Fias vs local) before opening the dialog.
    // Calling setExportDialogOpen(true) directly skips that.
    openSaveAs: () => handleSaveAs(),
    openSaveAsTemplate: () => setSaveTemplateOpen(true),
    closeFile: () => handleCloseActiveFile(),
    hasActiveFile: tabs.activeTabId !== null,
    openDocumentSettings: () => setDocumentSettingsOpen(true),
    openCanvasSize: () => setCanvasSizeOpen(true),
    // view
    zoomIn: () => canvas.zoomIn(),
    zoomOut: () => canvas.zoomOut(),
    fitToScreen: () => {
      canvas.fitToContainer();
    },
    actualSize: () => canvas.zoomToActual(),
    toggleRulers: () => updateViewSettings({ rulersVisible: !state.viewSettings.rulersVisible }),
    toggleMeasurementGrid: () =>
      updateViewSettings({ gridVisible: !state.viewSettings.gridVisible }),
    toggleGuidesVisible: () =>
      updateViewSettings({ guidesVisible: !state.viewSettings.guidesVisible }),
    toggleTransparencyChecker: () => setTransparencyCheckerVisible((v) => !v),
    toggleSnapToGrid: () => updateViewSettings({ snapToGrid: !state.viewSettings.snapToGrid }),
    toggleSnapToGuides: () =>
      updateViewSettings({ snapToGuides: !state.viewSettings.snapToGuides }),
    toggleSmartGuides: () =>
      updateViewSettings({ smartGuidesEnabled: !state.viewSettings.smartGuidesEnabled }),
    toggleLockGuides: () => updateViewSettings({ lockGuides: !state.viewSettings.lockGuides }),
    clearGuides: () => clearGuides(),
    openNewGuide: () => setNewGuideOpen(true),
    openGridSettings: () => setGridSettingsOpen(true),
    // tools
    selectTool: (tool) => handleToolSelect(tool as EditorTool),
    cycleRetouchVariant: handleCycleRetouchVariant,
    // transform / modal-mode
    beginTransform: () => {
      if (state.activeLayerId) handleBeginTransform(state.activeLayerId);
    },
    applyTransform: handleApplyTransform,
    cancelTransform: handleCancelTransform,
    beginLiquify: () => {
      if (state.activeLayerId) handleBeginLiquify(state.activeLayerId);
    },
    applyCrop: handleApplyCrop,
    finalizePenPath: () => canvas.finalizePenPath(false),
    cancelPenPath: () => canvas.cancelPenPath(),
    applyPathEdit: handleApplyPathEdit,
    cancelPathEdit: handleCancelPathEdit,
    // history
    historyManualSnapshot: () => {
      // Auto-name with timestamp; future panel polish can offer
      // rename. Pinned / cap-exempt by virtue of going through
      // manualSnapshot rather than saveSnapshot.
      const dt = new Date();
      const label = `Snapshot ${dt.toLocaleTimeString()}`;
      history.manualSnapshot(label);
    },
    toggleHistoryPanel: () => setHistoryPanelOpen((v) => !v),
    // help / meta
    openHelp: () => setHelpDialogOpen(true),
    openPalette: () => setPaletteOpen(true),
    openKeyboardSettings: () => setKeyboardSettingsOpen(true),
  };

  // Register every command once at mount, then immediately apply
  // any persisted keyboard overrides (Tier 3 Group C Phase 2). The
  // cleanup unregisters each spec — important under React
  // StrictMode (double-mount) and HMR so the registry doesn't
  // accumulate duplicates. Overrides survive cleanup because they
  // live in the registry instance, but the registry instance is
  // itself stable across renders (it's in a ref), so this is fine.
  useEffect(() => {
    const registry = commandRegistryRef.current;
    const specs = buildEditorCommands(() => commandHandlersRef.current);
    const cleanups = specs.map((s) => registry.register(s));
    // Apply persisted overrides AFTER registration — unknown ids in
    // the stored map are silently skipped by setAllShortcutOverrides.
    registry.setAllShortcutOverrides(loadKeyboardOverrides());
    return () => cleanups.forEach((c) => c());
  }, []);

  // Default-label resolver — feeds the History panel a useful label
  // for saveSnapshot calls that don't flow through the dispatch
  // wrapper (canvas-event-driven actions like paint strokes,
  // marquee drags, erase passes). Reads `activeTool` + the retouch
  // sub-variant from a sidecar ref so the panel reads "Clone" /
  // "Heal" / "Smudge" when the user paints with those variants
  // rather than the generic "Retouch". Registered once at mount —
  // the resolver closes over refs only, so it always sees current
  // state without needing to re-bind.
  const historyLabelContextRef = useRef({
    activeTool: state.activeTool as string,
    retouchVariant: state.retouchSettings.variant as string,
    pathEditActive: state.pathEditActive,
    transformActive: state.transformActive,
    quickMaskActive: state.quickMaskActive,
  });
  historyLabelContextRef.current = {
    activeTool: state.activeTool,
    retouchVariant: state.retouchSettings.variant,
    pathEditActive: state.pathEditActive,
    transformActive: state.transformActive,
    quickMaskActive: state.quickMaskActive,
  };
  useEffect(() => {
    history.setDefaultLabelResolver(() => {
      const ctx = historyLabelContextRef.current;
      // Mode-active states win over the bare active tool — a drag
      // during path-edit / transform / quick-mask should label by
      // the mode, not by whatever tool the user had selected before
      // entering the mode.
      if (ctx.pathEditActive) return 'Path edit';
      if (ctx.transformActive) return 'Transform';
      if (ctx.quickMaskActive) return 'Quick mask';
      if (ctx.activeTool === 'retouch' && ctx.retouchVariant) {
        const chip = VARIANT_CHIPS.find((c) => c.id === ctx.retouchVariant);
        if (chip) return chip.label;
      }
      return TOOL_HISTORY_LABELS[ctx.activeTool] ?? null;
    });
    return () => history.setDefaultLabelResolver(null);
  }, [history]);

  // Persist override changes from the Settings dialog. Stable
  // callback ref so the dialog doesn't re-render on every parent
  // render — dialog rerenders only matter when `keyboardSettingsOpen`
  // flips.
  const handleKeyboardOverridesChanged = useCallback((overrides: KeyboardOverrides) => {
    saveKeyboardOverrides(overrides);
  }, []);

  const commandContext = useMemo<CommandContext>(
    () => ({
      hasActiveLayer: state.activeLayerId !== null,
      hasSelection: canvas.hasMarqueeSelection,
      activeTool: state.activeTool,
      transformActive: state.transformActive,
      pathEditActive: state.pathEditActive,
      // The palette's input-focus check covers HTML input typing,
      // and fabric IText editing handles its own keys on the canvas.
      // Set false here until a future migration needs a finer signal.
      isEditingText: false,
      paletteOpen,
    }),
    [
      state.activeLayerId,
      state.activeTool,
      state.transformActive,
      state.pathEditActive,
      canvas.hasMarqueeSelection,
      paletteOpen,
    ],
  );
  const commandContextRef = useRef(commandContext);
  commandContextRef.current = commandContext;

  // Stable dispatch wrapper — every toolbar button, menu item, and
  // future palette / recorder callsite routes through this so the
  // registry is the single chokepoint for command firing.
  //
  // Pending-label channel: the wrapper sets the command's display
  // label on the history hook before invoking `run`, then clears it.
  // Any `history.saveSnapshot()` the command body triggers picks up
  // the label automatically, so the History panel reads "Brush" /
  // "Move" / etc. instead of generic "Edit". Closures-over-refs-only,
  // so identity never changes across renders.
  const dispatchCommand = useCallback(
    (id: string) => {
      const cmd = commandRegistryRef.current.get(id);
      if (cmd) history.setPendingLabel({ label: cmd.label, commandId: cmd.id });
      const promise = commandRegistryRef.current.dispatch(id, commandContextRef.current);
      void promise.finally(() => {
        history.setPendingLabel(null);
      });
    },
    [history],
  );

  // Central keydown listener — single dispatch point for every
  // editor shortcut. The legacy if-else switch this replaced ran
  // ~25 manually-ordered if-else cases; the registry handles
  // priority via insertion order and availability via per-spec
  // `isAvailable`.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      const ctx = commandContextRef.current;
      if (ctx.paletteOpen) return; // palette owns keys while open
      const match = commandRegistryRef.current.matchShortcut(e, ctx, isMac);
      if (!match) return;
      e.preventDefault();
      // Route through the same wrapper used by toolbar / palette
      // dispatches so the History panel sees consistent labels
      // regardless of how the command was fired.
      dispatchCommand(match.id);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isMac, dispatchCommand]);

  // Close the close-after-export tab if one was set when Save-As triggered
  // this export. Captured to a local var first so the ref can be cleared
  // immediately, preventing a re-trigger if the dialog is reopened later.
  const consumeCloseAfterExport = useCallback(() => {
    const id = closeAfterExportTabIdRef.current;
    closeAfterExportTabIdRef.current = null;
    if (id) void tabs.closeTab(id);
  }, [tabs]);

  const handleExportLocal = useCallback(
    async (
      fileName: string,
      format: CanvasNativeExportFormat,
      quality: number,
      target?: { width?: number; height?: number },
    ) => {
      const result = await exportToLocal(fileName, format, quality, target);
      // User cancelled the OS save picker — don't show success
      // feedback, don't mark clean, don't close the tab.
      if (result.status === 'cancelled') return;
      // Pin the FSA handle on the active tab so a follow-up File →
      // Save (or Cmd+S) routes through `quickSaveCurrent`'s in-place
      // write path with the right format dispatcher. The fallback
      // paths (iOS Share, `<a download>`) return `'saved'` without a
      // handle — Save falls back to Save-As for those browsers.
      // Always refresh `sourceFileName` so the next Save-As dialog's
      // format dropdown reflects this save's chosen format, even on
      // non-FSA browsers where the handle field stays null.
      tabs.updateActiveTabMetadata({
        fileName,
        sourceFileName: `${fileName}.${format}`,
        ...(result.status === 'saved-with-handle' ? { fileHandle: result.handle } : {}),
      });
      markClean();
      consumeCloseAfterExport();
    },
    [exportToLocal, markClean, consumeCloseAfterExport, tabs],
  );

  // Size estimate for the Export dialog. Encodes the canvas at the
  // currently-selected settings via the same encoder the export
  // path will use, so the readout reflects real compressor behavior.
  // The dialog debounces calls; here we just expose the encoder.
  const handleEstimateBlob = useCallback(
    (
      format: CanvasNativeExportFormat,
      quality: number,
      target?: { width?: number; height?: number },
    ) => canvas.exportToBlob(format, quality, target),
    [canvas],
  );

  /** PSD export — bypasses the canvas-toBlob path. Loads the source
   *  stash (if any), re-parses it, runs the merge algorithm, and
   *  either downloads the result or refuses non-local destinations
   *  (PSD-to-Fias / PSD-on-clipboard aren't implemented in v1; the
   *  dialog's UI already gates the dropdown, this is the defensive
   *  fallback). */
  const handleExportPsd = useCallback(
    async (fileName: string, destination: ExportDestination) => {
      const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
      if (!activeTab) {
        toast({
          title: 'No active tab',
          description: 'Open a file to export.',
          variant: 'destructive',
        });
        return;
      }
      if (destination !== 'local') {
        toast({
          title: 'PSD export — local download only',
          description:
            'Save to Fias and Copy to clipboard don’t support PSD in this version. Pick Local download or choose a different format.',
          variant: 'destructive',
        });
        return;
      }
      try {
        const result = await exportDocumentToPsd({
          documentId: activeTab.documentId,
          document: state.document,
          canvasWidth: state.canvasWidth,
          canvasHeight: state.canvasHeight,
          layerCanvasProvider: canvas.getLayerExportPayload,
          // PSD natives follow-up phase C — text layers emit as
          // native PSD text records via `canvas.getLayerTextPayload`
          // (the SVG-side payload shape is reused; both formats
          // consume the same provider shape).
          layerTextProvider: canvas.getLayerTextPayload,
          layerMaskProvider: canvas.getRawMaskCanvas,
          layerShapePathProvider: canvas.getShapeVectorPath,
        });
        // Skip `exportToLocal` — that path bakes the PSD bytes through
        // a canvas roundtrip (wrong format). Hand the raw PSD bytes to
        // `saveBlobLocally` which prefers the File System Access API
        // (and pins the handle on the tab) over `<a download>`. With
        // a handle captured, a follow-up Cmd+S writes PSD bytes
        // in-place via `quickSaveCurrent`'s existing PSD branch.
        const blob = new Blob([result.bytes], { type: 'image/vnd.adobe.photoshop' });
        const saveResult = await saveBlobLocally(blob, {
          fileName,
          extension: 'psd',
          description: 'Photoshop document',
        });
        // User cancelled the OS save picker — bail before any of the
        // post-save side effects (toast, markClean, tab-close).
        if (saveResult.status === 'cancelled') return;
        tabs.updateActiveTabMetadata({
          fileName,
          sourceFileName: `${fileName}.psd`,
          ...(saveResult.status === 'saved-with-handle' ? { fileHandle: saveResult.handle } : {}),
        });
        markClean();
        consumeCloseAfterExport();
        // Summary toast — informational, lets the user know what
        // round-tripped vs got flattened. Fires whenever a stash was
        // present (i.e. the file originated from PSD import) so the
        // user always has confirmation of the save outcome, even when
        // every layer was rasterized.
        const { summary, hadStash } = result;
        if (hadStash) {
          const preserved = summary.preservedFromStash;
          const raster = summary.emittedAsRaster;
          const description =
            preserved > 0
              ? `${preserved} layer${preserved === 1 ? '' : 's'} preserved from original PSD; ${raster} saved as raster.`
              : `All ${raster} layer${raster === 1 ? '' : 's'} saved as raster (every layer was modified since import).`;
          toast({ title: 'PSD saved', description });
        } else {
          toast({
            title: 'PSD saved',
            description: `${summary.emittedAsRaster} layer${summary.emittedAsRaster === 1 ? '' : 's'} saved as raster.`,
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error.';
        toast({
          title: 'PSD export failed',
          description: message,
          variant: 'destructive',
        });
        console.warn('[image-editor] PSD export failed:', err);
      }
    },
    [
      tabs,
      state.document,
      state.canvasWidth,
      state.canvasHeight,
      canvas.getLayerExportPayload,
      canvas.getLayerTextPayload,
      markClean,
      consumeCloseAfterExport,
    ],
  );

  /** SVG export — mirror of `handleExportPsd`. Local download only
   *  in v1 (no SVG-to-Fias upload, no SVG-on-clipboard). Routes
   *  through `exportDocumentToSvg` because the structured output
   *  can't roundtrip through `canvas.toBlob`. */
  const handleExportSvg = useCallback(
    async (fileName: string, destination: ExportDestination) => {
      const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
      if (!activeTab) {
        toast({
          title: 'No active tab',
          description: 'Open a file to export.',
          variant: 'destructive',
        });
        return;
      }
      if (destination !== 'local') {
        toast({
          title: 'SVG export — local download only',
          description:
            'Save to Fias and Copy to clipboard don’t support SVG in this version. Pick Local download or choose a different format.',
          variant: 'destructive',
        });
        return;
      }
      try {
        const result = await exportDocumentToSvg({
          documentId: activeTab.documentId,
          document: state.document,
          canvasWidth: state.canvasWidth,
          canvasHeight: state.canvasHeight,
          layerCanvasProvider: canvas.getLayerExportPayload,
          // Tier 2 Group F SVG round-trip polish (PR-2 phase E) —
          // editor-created text layers (and stash-restored single-
          // line text that imported natively in phase D) emit as
          // `<text>` instead of `<image>`. Arch / text-on-path
          // layers return null from the provider and fall back to
          // raster.
          layerTextProvider: canvas.getLayerTextPayload,
        });
        // Skip `exportToLocal` — that path runs through `canvas.toBlob`
        // which can't produce SVG. `saveBlobLocally` prefers the FSA
        // picker (handle pinned for in-place follow-up Save) and
        // falls back to `<a download>` elsewhere.
        const blob = new Blob([result.bytes], { type: 'image/svg+xml' });
        const saveResult = await saveBlobLocally(blob, {
          fileName,
          extension: 'svg',
          description: 'SVG image',
        });
        if (saveResult.status === 'cancelled') return;
        tabs.updateActiveTabMetadata({
          fileName,
          sourceFileName: `${fileName}.svg`,
          ...(saveResult.status === 'saved-with-handle' ? { fileHandle: saveResult.handle } : {}),
        });
        markClean();
        consumeCloseAfterExport();
        const { summary, hadStash, stashCorrupted, containsExternalRefs } = result;
        if (stashCorrupted) {
          // Hard warning — stash existed but couldn't be parsed.
          // Every layer fell back to editor-side. User expected
          // lossless and got reduced. Surface up-front. Pre-PR
          // review finding (R1#2 / R3#A.6).
          toast({
            title: 'SVG saved with reduced fidelity',
            description:
              'The original SVG data could not be restored (the saved record is corrupted). Layers were saved as raster / vector only. Re-import the original .svg to regain lossless export.',
            variant: 'destructive',
          });
        } else if (hadStash) {
          const preserved = summary.preservedFromStash;
          const vector = summary.emittedAsVector;
          const raster = summary.emittedAsRaster;
          const parts: string[] = [];
          if (preserved > 0) parts.push(`${preserved} preserved from original SVG`);
          if (vector > 0) parts.push(`${vector} as vector`);
          if (raster > 0) parts.push(`${raster} as raster`);
          toast({
            title: 'SVG saved',
            description: parts.length > 0 ? parts.join(', ') + '.' : 'No layers in document.',
          });
        } else {
          const vector = summary.emittedAsVector;
          const raster = summary.emittedAsRaster;
          const parts: string[] = [];
          if (vector > 0) parts.push(`${vector} layer${vector === 1 ? '' : 's'} as vector`);
          if (raster > 0) parts.push(`${raster} as raster`);
          toast({
            title: 'SVG saved',
            description: parts.length > 0 ? parts.join(', ') + '.' : 'No layers in document.',
          });
        }
        // Adjustment layers + oversized rasters surface as `skipped`
        // — call them out so a user who imported a PSD with
        // adjustment layers and saved as SVG knows what was lost.
        // Pre-PR review finding (R1#3).
        if (summary.skipped > 0) {
          toast({
            title: `${summary.skipped} layer${summary.skipped === 1 ? '' : 's'} not saved`,
            description:
              "Adjustment layers and oversized rasters can't be represented in SVG and were skipped. Flatten / remove them, or save as PSD instead.",
          });
        }
        // Privacy warning when the saved file carries external URL
        // references that would re-fetch on a recipient's machine.
        // Stash-restored elements preserve their original hrefs
        // (the import-side rasterizer's sanitizer applies only to
        // its in-memory view; the source stash is verbatim). Letting
        // the user know is the right trade-off vs. silently
        // rewriting their data. Pre-PR review finding (R3#B).
        if (containsExternalRefs) {
          toast({
            title: 'Saved SVG references external URLs',
            description:
              'Your file references images or stylesheets on other domains. Anyone who opens this file will trigger fetches to those URLs (revealing their IP to the host). Edit the file in a text editor to strip them if needed.',
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error.';
        toast({
          title: 'SVG export failed',
          description: message,
          variant: 'destructive',
        });
        console.warn('[image-editor] SVG export failed:', err);
      }
    },
    [
      tabs,
      state.document,
      state.canvasWidth,
      state.canvasHeight,
      canvas.getLayerExportPayload,
      canvas.getLayerTextPayload,
      markClean,
      consumeCloseAfterExport,
    ],
  );

  /** PDF export — single-page PDF sized to the canvas, image
   *  embedded as JPEG. Mirrors `handleExportSvg`'s structure. Local
   *  download only in v1 (no PDF-to-Fias upload, no PDF on
   *  clipboard). */
  const handleExportPdf = useCallback(
    async (fileName: string, destination: ExportDestination) => {
      const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
      if (!activeTab) {
        toast({
          title: 'No active tab',
          description: 'Open a file to export.',
          variant: 'destructive',
        });
        return;
      }
      if (destination !== 'local') {
        toast({
          title: 'PDF export — local download only',
          description:
            'Save to Fias and Copy to clipboard don’t support PDF in this version. Pick Local download or choose a different format.',
          variant: 'destructive',
        });
        return;
      }
      try {
        // JPEG-encode the canvas client-side so jsPDF can embed the
        // bytes verbatim without re-encoding. The PDF orchestrator
        // accepts any data URL but JPEG keeps the file size sane for
        // photo-heavy content.
        const canvasDataUrl = canvas.exportToDataURL('jpeg', PDF_IMAGE_JPEG_QUALITY);
        if (!canvasDataUrl) {
          toast({
            title: 'PDF export failed',
            description: 'Could not render the canvas.',
            variant: 'destructive',
          });
          return;
        }
        const result = await exportDocumentToPdf({
          documentId: activeTab.documentId,
          document: state.document,
          canvasWidth: state.canvasWidth,
          canvasHeight: state.canvasHeight,
          // Tier 3 Group D Slice 1d — when the user has declared a
          // DPI via Document Settings, size the PDF page in points
          // so the print physical dimensions match. `null` falls
          // back to the legacy pixel-page path.
          dpi: state.dpi,
          canvasDataUrl,
        });
        const blob = new Blob([result.bytes], { type: 'application/pdf' });
        const saveResult = await saveBlobLocally(blob, {
          fileName,
          extension: 'pdf',
          description: 'PDF document',
        });
        if (saveResult.status === 'cancelled') return;
        tabs.updateActiveTabMetadata({
          fileName,
          sourceFileName: `${fileName}.pdf`,
          ...(saveResult.status === 'saved-with-handle' ? { fileHandle: saveResult.handle } : {}),
        });
        markClean();
        consumeCloseAfterExport();
        toast({
          title: 'PDF saved',
          description: `${state.canvasWidth}×${state.canvasHeight} single-page PDF.`,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error.';
        toast({
          title: 'PDF export failed',
          description: message,
          variant: 'destructive',
        });
        console.warn('[image-editor] PDF export failed:', err);
      }
    },
    [
      tabs,
      state.document,
      state.canvasWidth,
      state.canvasHeight,
      state.dpi,
      canvas,
      markClean,
      consumeCloseAfterExport,
    ],
  );

  const handleExportFias = useCallback(
    async (settings: ExportSettings, fileName: string) => {
      // TIFF can't be uploaded to Fias in v1 — the `processImage`
      // endpoint pins encode to png/jpeg/webp/avif (see
      // `FiasUploadFormat`). The dialog's structured-format-or-tiff
      // destination effect forces destination=local for TIFF, so
      // this branch should never see TIFF; defensive guard mirrors
      // the dialog-side gate so a future code path that wires
      // around the effect can't silently degrade to PNG.
      if (settings.format === 'tiff') {
        toast({
          title: 'TIFF — local download only',
          description: 'Save to Fias does not accept TIFF in this version.',
          variant: 'destructive',
        });
        return;
      }
      const uploadFormat: FiasUploadFormat = settings.format;
      const dataUrl = canvas.exportToDataURL(uploadFormat, settings.quality / 100);
      const result = await exportToFias({
        sourceDataUrl: dataUrl,
        operations: [],
        output: {
          format: uploadFormat,
          quality: settings.quality,
          width: settings.width,
          height: settings.height,
          maintainAspectRatio: settings.maintainAspectRatio,
        },
        saveAs: { fileName },
      });
      if (result) {
        // Pin the Fias fileId + fileName back on the tab so a
        // follow-up File → Save (or Cmd+S) routes through
        // `quickSaveCurrent`'s in-place Fias path instead of
        // re-opening the Save-As dialog. Without this, Save would
        // keep falling back to Save-As for the entire session, which
        // is what made the Save menu item look redundant. Also
        // refresh `sourceFileName` so the next Save-As dialog's
        // format dropdown defaults to the format we just uploaded.
        tabs.updateActiveTabMetadata({
          fileName,
          sourceFileName: `${fileName}.${settings.format}`,
          ...(result.fileId ? { fileId: result.fileId } : {}),
        });
        markClean();
        consumeCloseAfterExport();
      }
    },
    [canvas, exportToFias, markClean, consumeCloseAfterExport, tabs],
  );

  /** Tier 2 Group F sub-piece #5 — Slice export handler.
   *
   *  Renders the full canvas to an offscreen HTMLCanvasElement at
   *  native pixel resolution, then asks `exportSlicesAsBlobs` to
   *  crop one image per slice + apply the Scale multiplier. For
   *  local destination, the resulting files are bundled into a
   *  single ZIP via JSZip and downloaded with the standard
   *  `<a download>` trick. For Fias destination, each slice is
   *  uploaded sequentially via `processImage` (no batch endpoint
   *  exists; we surface progress in the toast).
   *
   *  The slice tool's on-canvas overlay rectangles are excluded
   *  from export via the `excludeFromExport: true` flag set in
   *  `tagAsSliceOverlay` — they don't appear in the rendered
   *  source canvas even though they're visually present while the
   *  user has the Slice tool active. */
  const handleExportSlices = useCallback(
    async (opts: {
      destination: ExportDestination;
      format: SliceExportFormat;
      quality: number;
      scaleMultiplier: number;
      fileName: string;
    }) => {
      if (state.slices.length === 0) {
        toast({
          title: 'No slices defined',
          description: 'Define at least one slice with the Slice tool first.',
          variant: 'destructive',
        });
        return;
      }
      // Render the canvas at native resolution into an HTMLCanvasElement
      // we can read pixels from. `exportToDataURL` returns a PNG at
      // native dims (we always use PNG here regardless of the user's
      // chosen output format — PNG is lossless, so per-slice JPEG /
      // WebP re-encode below doesn't compound any source-side loss).
      const fullDataUrl = canvas.exportToDataURL('png', 1);
      if (!fullDataUrl) {
        toast({
          title: 'Slice export failed',
          description: 'Could not render the canvas.',
          variant: 'destructive',
        });
        return;
      }
      const img = await new Promise<HTMLImageElement | null>((resolve) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => resolve(null);
        el.src = fullDataUrl;
      });
      if (!img) {
        toast({
          title: 'Slice export failed',
          description: 'Could not decode the canvas snapshot.',
          variant: 'destructive',
        });
        return;
      }
      const sourceCanvas = document.createElement('canvas');
      sourceCanvas.width = state.canvasWidth;
      sourceCanvas.height = state.canvasHeight;
      const sCtx = sourceCanvas.getContext('2d');
      if (!sCtx) {
        toast({
          title: 'Slice export failed',
          description: 'Could not create source canvas.',
          variant: 'destructive',
        });
        return;
      }
      sCtx.drawImage(img, 0, 0, state.canvasWidth, state.canvasHeight);

      const result = await exportSlicesAsBlobs(
        state.slices,
        sourceCanvas,
        state.canvasWidth,
        state.canvasHeight,
        {
          format: opts.format,
          quality: opts.quality,
          scaleMultiplier: opts.scaleMultiplier,
        },
      );
      if (result.files.length === 0) {
        toast({
          title: 'No slices exported',
          description:
            result.total > 0
              ? `All ${result.total} slices were outside the canvas or failed to encode.`
              : 'Define at least one slice first.',
          variant: 'destructive',
        });
        return;
      }

      if (opts.destination === 'local') {
        try {
          if (result.files.length === 1) {
            // Single-slice shortcut: skip the ZIP and download the
            // image directly under its slice name. ZIP overhead is
            // pure friction when there's only one file (the user
            // would unzip immediately).
            const only = result.files[0];
            const url = URL.createObjectURL(only.blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = only.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 0);
            markClean();
            consumeCloseAfterExport();
            toast({
              title: 'Slice saved',
              description: only.name,
            });
            return;
          }
          // 2+ slices: bundle into a single ZIP so the user doesn't
          // hit per-file download prompts (which browsers throttle
          // or block as popups).
          const zipBlob = await bundleSlicesIntoZip(result.files);
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${opts.fileName}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Defer revoke so the browser finishes reading the blob
          // before the URL becomes invalid (mirrors the SVG export
          // path's R1#7 fix).
          setTimeout(() => URL.revokeObjectURL(url), 0);
        } catch (err) {
          toast({
            title: 'Slice export failed',
            description: err instanceof Error ? err.message : 'Unknown error.',
            variant: 'destructive',
          });
          return;
        }
        markClean();
        consumeCloseAfterExport();
        const msg =
          result.failed === 0
            ? `${result.files.length} slices bundled.`
            : `${result.files.length} of ${result.total} slices bundled; ${result.failed} skipped.`;
        toast({ title: 'Slices saved as ZIP', description: msg });
        return;
      }

      // Fias destination: loop per-slice through processImage. No
      // batch endpoint exists; each slice is one upload. Progress
      // is surfaced in a final summary toast (one toast per upload
      // would spam the system; the loop runs sequentially so a
      // mid-failure leaves earlier successes in vault).
      //
      // PDF + TIFF slices are local-only — the dialog's
      // local-only-format destination effect forces
      // destination='local' for both, so this branch should never
      // see either. Defensive guards mirror the dialog-layer gate
      // so a future code path that wires around the effect can't
      // silently hit the canvas-native upload path.
      if (opts.format === 'pdf' || opts.format === 'tiff') {
        // Hitting this branch indicates the dialog's
        // local-only-format destination effect failed to fire before
        // the user clicked Save — a state-machine bug rather than a
        // routine code path. Surface to the console so a regression
        // in the gating logic shows up in dev without breaking the
        // user's flow. (Pre-PR review audit Major #5.)
        console.warn(
          '[image-editor] handleExportSlices reached the Fias branch with a local-only format',
          { format: opts.format, destination: opts.destination },
        );
        toast({
          title: `${opts.format.toUpperCase()} slices — local download only`,
          description: `Save to Fias does not accept ${opts.format.toUpperCase()} slices in this version.`,
          variant: 'destructive',
        });
        return;
      }
      const rasterFormat: FiasUploadFormat = opts.format;
      let succeeded = 0;
      const failedFiasNames: string[] = [];
      for (const file of result.files) {
        // Strip extension for the saveAs.fileName field — the API
        // appends its own extension based on output.format.
        const bareName = file.name.replace(/\.[a-z]+$/i, '');
        try {
          const fileDataUrl = await new Promise<string | null>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file.blob);
          });
          if (!fileDataUrl) {
            failedFiasNames.push(file.name);
            continue;
          }
          const apiResult = await exportToFias({
            sourceDataUrl: fileDataUrl,
            operations: [],
            output: { format: rasterFormat, quality: Math.round(opts.quality * 100) },
            saveAs: { fileName: bareName },
          });
          if (apiResult) {
            succeeded++;
          } else {
            failedFiasNames.push(file.name);
          }
        } catch {
          failedFiasNames.push(file.name);
        }
      }
      if (succeeded > 0) {
        markClean();
        consumeCloseAfterExport();
      }
      let summary: string;
      if (failedFiasNames.length === 0) {
        summary = `${succeeded} slice${succeeded === 1 ? '' : 's'} uploaded to Fias.`;
      } else {
        // Surface up to the first 3 failed names so the user knows
        // which slices to retry. Long failure lists collapse to a
        // "+N more" suffix to keep the toast compact.
        const sample = failedFiasNames.slice(0, 3).join(', ');
        const more = failedFiasNames.length > 3 ? ` and ${failedFiasNames.length - 3} more` : '';
        summary = `${succeeded} of ${result.files.length} uploaded; failed: ${sample}${more}.`;
      }
      toast({
        title:
          failedFiasNames.length === 0 ? 'Slices saved to Fias' : 'Slice upload partially failed',
        description: summary,
        variant: failedFiasNames.length === 0 ? undefined : 'destructive',
      });
    },
    [
      canvas,
      state.slices,
      state.canvasWidth,
      state.canvasHeight,
      exportToFias,
      markClean,
      consumeCloseAfterExport,
    ],
  );

  /** Sub-region export — Selection / Active Layer / Visible Layers.
   *
   *  Mirrors the slice export's shape (snapshot → build sub-canvas →
   *  scale → encode → dispatch) but produces a single image instead
   *  of an N-file ZIP. Each region's bounds-resolution logic lives in
   *  `lib/region-export.ts`; this handler is the glue that fetches
   *  the inputs from the canvas hook + state and routes the encoded
   *  blob to the chosen destination.
   *
   *  Filename construction: `${fileName}${suffix}.${ext}` where
   *  `suffix` is region-specific (`-selection` / `-layer-<slug>` /
   *  `-visible`). This keeps a multi-tab user's exports from
   *  overwriting each other on consecutive Saves.
   *
   *  Format support matches Slices (PNG/JPEG/WebP/AVIF/PDF/TIFF; PSD
   *  and SVG are dialog-gated out). Destination support DIFFERS:
   *  clipboard / share is allowed for sub-regions because the output
   *  is a single image, whereas Slices forces local-or-Fias because
   *  it produces multiple files. TIFF + PDF stay local-only (Fias's
   *  processImage doesn't accept them; no major browser writes them
   *  to the OS clipboard). */
  const handleExportRegion = useCallback(
    async (opts: {
      region: 'selection' | 'active-layer' | 'visible';
      destination: ExportDestination;
      format: SliceExportFormat;
      quality: number;
      scaleMultiplier: number;
      /** Final file name verbatim from the dialog's File Name
       *  input. The dialog seeds the input with the region's
       *  natural default (`${doc}-selection` / `${doc}-layer-<slug>`
       *  / `${doc}-visible`) on region pick, then the user can edit;
       *  whatever is in the input at Save time arrives here. */
      fileName: string;
    }) => {
      // 1. Build the region's sub-canvas. Selection + Visible Layers
      // both need a full-canvas snapshot; Active Layer queries the
      // per-layer compositor directly via `getLayerExportPayload`.
      // We compute the full snapshot lazily (only when needed) so
      // the Active Layer path doesn't pay a render round-trip.
      //
      // Crucially we use `exportToDataURLExcludingOverlays` here —
      // the regular `exportToDataURL` would bake the marquee
      // outline + slice rectangles + transform handles into the
      // pixels, which is editor chrome the user never wants in
      // their export. Selection's whole point is the marquee, so
      // including it would be a visible bug. Reported via QA.
      const snapshotFullCanvas = async (): Promise<HTMLCanvasElement | null> => {
        const dataUrl = canvas.exportToDataURLExcludingOverlays('png', 1);
        if (!dataUrl) return null;
        const img = await new Promise<HTMLImageElement | null>((resolve) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = () => resolve(null);
          el.src = dataUrl;
        });
        if (!img) return null;
        const c = document.createElement('canvas');
        c.width = state.canvasWidth;
        c.height = state.canvasHeight;
        const ctx = c.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, state.canvasWidth, state.canvasHeight);
        return c;
      };

      let regionCanvas: HTMLCanvasElement | null = null;
      if (opts.region === 'selection') {
        const bounds = canvas.getMarqueeBounds();
        const maskCanvas = canvas.getSelectionAsMaskCanvas();
        if (!bounds || !maskCanvas) {
          toast({
            title: 'No selection',
            description: 'Make a selection with the marquee tool first.',
            variant: 'destructive',
          });
          return;
        }
        const fullCanvas = await snapshotFullCanvas();
        if (!fullCanvas) {
          toast({
            title: 'Selection export failed',
            description: 'Could not snapshot the canvas.',
            variant: 'destructive',
          });
          return;
        }
        regionCanvas = buildSelectionRegionCanvas(fullCanvas, maskCanvas, bounds);
      } else if (opts.region === 'active-layer') {
        const layerId = state.activeLayerId;
        if (!layerId) {
          toast({
            title: 'No active layer',
            description: 'Select a layer in the Layers panel first.',
            variant: 'destructive',
          });
          return;
        }
        const payload = canvas.getLayerExportPayload(layerId);
        if (!payload) {
          toast({
            title: 'Active layer export failed',
            description: 'The active layer has no rasterizable content.',
            variant: 'destructive',
          });
          return;
        }
        regionCanvas = buildActiveLayerRegionCanvas(payload);
      } else {
        // 'visible'
        const fullCanvas = await snapshotFullCanvas();
        if (!fullCanvas) {
          toast({
            title: 'Visible layers export failed',
            description: 'Could not snapshot the canvas.',
            variant: 'destructive',
          });
          return;
        }
        regionCanvas = buildVisibleLayersRegionCanvas(fullCanvas);
      }
      if (!regionCanvas) {
        toast({
          title: 'Region export failed',
          description: 'Region produced no output (empty bbox or composite error).',
          variant: 'destructive',
        });
        return;
      }

      // 2. Apply the Scale multiplier and encode through the same
      // per-format dispatcher slices use. Reusing `encodeSliceCanvas`
      // avoids duplicating the canvas-native / PDF / TIFF branches —
      // the dispatcher is format-driven, not slice-specific.
      const scaledCanvas = scaleRegionCanvas(regionCanvas, opts.scaleMultiplier);
      const blob = await encodeSliceCanvas(scaledCanvas, opts.format, opts.quality);
      if (!blob) {
        toast({
          title: 'Region export failed',
          description: `Could not encode the region as ${opts.format.toUpperCase()}.`,
          variant: 'destructive',
        });
        return;
      }

      // 3. Build the output filename and dispatch by destination.
      // The File Name input is the source of truth — the dialog has
      // already seeded the input with the region's natural default
      // (`-selection` / `-layer-<slug>` / `-visible`) when the user
      // picked a region, and we use whatever the user typed (or
      // accepted) verbatim. Just append the format extension.
      const ext = opts.format;
      const fullFileName = `${opts.fileName}.${ext}`;
      const bareName = opts.fileName;

      if (opts.destination === 'local') {
        try {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fullFileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Defer revoke so the browser finishes reading the blob
          // before the URL becomes invalid (mirrors the slice + SVG
          // export paths).
          setTimeout(() => URL.revokeObjectURL(url), 0);
          markClean();
          consumeCloseAfterExport();
          toast({ title: 'Region saved', description: fullFileName });
        } catch (err) {
          toast({
            title: 'Region export failed',
            description: err instanceof Error ? err.message : 'Unknown error.',
            variant: 'destructive',
          });
        }
        return;
      }

      if (opts.destination === 'fias') {
        // Fias's processImage doesn't accept TIFF or PDF; the dialog
        // gates these via `isLocalOnlyExportFormat`, but defensively
        // refuse here too in case a future code path skirts the gate.
        if (opts.format === 'pdf' || opts.format === 'tiff') {
          // Same defensive-log idiom as the slice handler's
          // local-only branch: hitting this indicates the dialog's
          // local-only-format destination effect failed to fire
          // before Save was clicked — a state-machine bug, not a
          // routine path. Surface to the console so a regression in
          // the gating logic shows up in dev without breaking the
          // user's flow.
          console.warn(
            '[image-editor] handleExportRegion reached the Fias branch with a local-only format',
            { format: opts.format, destination: opts.destination, region: opts.region },
          );
          toast({
            title: `${opts.format.toUpperCase()} region — local download only`,
            description: `Save to Fias does not accept ${opts.format.toUpperCase()} in this version.`,
            variant: 'destructive',
          });
          return;
        }
        const rasterFormat: FiasUploadFormat = opts.format;
        const dataUrl = await new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
        if (!dataUrl) {
          toast({
            title: 'Region upload failed',
            description: 'Could not read encoded blob for upload.',
            variant: 'destructive',
          });
          return;
        }
        const apiResult = await exportToFias({
          sourceDataUrl: dataUrl,
          operations: [],
          output: { format: rasterFormat, quality: Math.round(opts.quality * 100) },
          saveAs: { fileName: bareName },
        });
        if (apiResult) {
          markClean();
          consumeCloseAfterExport();
          toast({ title: 'Region saved to Fias', description: fullFileName });
        } else {
          toast({
            title: 'Region upload failed',
            description: 'The Fias upload did not complete.',
            variant: 'destructive',
          });
        }
        return;
      }

      // Clipboard / share. PDF / TIFF aren't supported here for the
      // same reason as the Fias branch — the OS clipboard / native
      // share sheet don't accept them via web APIs.
      if (opts.format === 'pdf' || opts.format === 'tiff') {
        toast({
          title: `${opts.format.toUpperCase()} region — local download only`,
          description: `Copy / share does not accept ${opts.format.toUpperCase()} in this version.`,
          variant: 'destructive',
        });
        return;
      }
      const isCoarse =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(pointer: coarse)').matches;
      const canShareFile =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function';
      if (isCoarse && canShareFile) {
        try {
          const file = new File([blob], fullFileName, { type: blob.type });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: fullFileName });
            markClean();
            consumeCloseAfterExport();
            return;
          }
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') return;
          // Fall through to clipboard path on other errors.
        }
      }
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.clipboard?.write === 'function' &&
        typeof ClipboardItem !== 'undefined'
      ) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
          markClean();
          consumeCloseAfterExport();
          toast({ title: 'Region copied to clipboard', description: fullFileName });
          return;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Copy failed';
          toast({
            title: 'Copy failed',
            description:
              opts.format !== 'png'
                ? 'Most browsers only support PNG on the clipboard. Try exporting as PNG.'
                : message,
            variant: 'destructive',
          });
          return;
        }
      }
      toast({
        title: 'Not supported',
        description: 'Your browser does not support copying images to the clipboard.',
        variant: 'destructive',
      });
    },
    [
      canvas,
      state.activeLayerId,
      state.canvasWidth,
      state.canvasHeight,
      exportToFias,
      markClean,
      consumeCloseAfterExport,
    ],
  );

  /** Derived flag: at least one layer is EFFECTIVELY visible — i.e.,
   *  its own `visible` flag is true AND every ancestor group is
   *  visible too. Drives the Visible-Layers Region gate in the
   *  ExportDialog. Mirrors the parent-chain walk in
   *  `useFabricCanvas.applyEffectiveVisibility` (a layer is rendered
   *  only when the whole chain is visible) — so a child marked
   *  `visible: true` inside a hidden group does NOT count, matching
   *  what Fabric actually renders.
   *
   *  Without the walk, a document where every leaf has `visible: true`
   *  but a parent group has `visible: false` would falsely activate
   *  the Visible Layers option even though the export would produce
   *  a fully transparent canvas (pre-PR review Major #1). */
  const hasAnyVisibleLayer = useMemo(() => {
    const layers = state.document.layers;
    for (const id in layers) {
      let cur: string | null = id;
      let effective = true;
      while (cur !== null) {
        const node: import('../types').LayerNode | undefined = layers[cur];
        if (!node || !node.visible) {
          effective = false;
          break;
        }
        cur = node.parentId;
      }
      if (effective) return true;
    }
    return false;
  }, [state.document.layers]);

  // Copy-to-clipboard / native-share export. On mobile (coarse pointer
  // + Web Share API with files), prefer the native share sheet — that's
  // the affordance users expect for "send this image to another app."
  // Desktop falls back to navigator.clipboard.write with a PNG blob.
  // Errors surface as a toast so the user knows nothing happened, since
  // there's otherwise no visible feedback for an in-place clipboard op.
  const handleExportClipboard = useCallback(
    async (
      fileName: string,
      format: CanvasNativeExportFormat,
      quality: number,
      target?: { width?: number; height?: number },
    ) => {
      const blob = await canvas.exportToBlob(format, quality, target);
      if (!blob) {
        toast({
          title: 'Export failed',
          description: 'Could not generate image data.',
          variant: 'destructive',
        });
        return;
      }

      const isCoarse =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(pointer: coarse)').matches;
      const canShareFile =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function';

      // Mobile share sheet path.
      if (isCoarse && canShareFile) {
        try {
          const file = new File([blob], `${fileName}.${format}`, { type: blob.type });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: fileName });
            markClean();
            consumeCloseAfterExport();
            return;
          }
        } catch (err) {
          // User cancellation throws AbortError — silent. Other errors
          // fall through to clipboard fallback.
          if (err instanceof Error && err.name === 'AbortError') return;
        }
      }

      // Desktop / fallback: write blob to OS clipboard.
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.clipboard?.write === 'function' &&
        typeof ClipboardItem !== 'undefined'
      ) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
          toast({ title: 'Copied to clipboard' });
          markClean();
          consumeCloseAfterExport();
          return;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Copy failed';
          toast({
            title: 'Copy failed',
            description:
              format !== 'png'
                ? 'Most browsers only support PNG on the clipboard. Try exporting as PNG.'
                : message,
            variant: 'destructive',
          });
          return;
        }
      }

      toast({
        title: 'Not supported',
        description: 'Your browser does not support copying images to the clipboard.',
        variant: 'destructive',
      });
    },
    [canvas, markClean, consumeCloseAfterExport],
  );

  // Prefer the File System Access API so a later Save can overwrite the
  // original file in place. In Firefox / Safari (no FSA), fall back to the
  // hidden `<input type="file">` — that path can't capture a writable
  // handle, so Save will degrade to a download.
  /** Dispatch a freshly-opened file to the right loader. PSD / PSB go
   *  through `loadPsdFile`; SVG goes through `loadSvgFile`; everything
   *  else uses the existing `loadFile` path. Extension-based — File.type
   *  for `.psd` / `.svg` is browser-dependent (varies between
   *  'application/x-photoshop', 'image/svg+xml', 'image/vnd.adobe.photoshop',
   *  or empty), so name-based dispatch is more reliable. */
  const dispatchOpenedFile = useCallback(
    (file: File, handle?: FileSystemFileHandle | null) => {
      const name = file.name.toLowerCase();
      if (name.endsWith('.psd') || name.endsWith('.psb')) {
        void loadPsdFile(file, handle ?? null);
      } else if (name.endsWith('.svg')) {
        void loadSvgFile(file, handle ?? null);
      } else {
        loadFile(file, handle ?? null);
      }
    },
    [loadFile, loadPsdFile, loadSvgFile],
  );

  const pickAndOpenLocalFile = useCallback(async () => {
    type ShowOpenFilePicker = (opts?: {
      types?: { description: string; accept: Record<string, string[]> }[];
      multiple?: boolean;
      excludeAcceptAllOption?: boolean;
    }) => Promise<FileSystemFileHandle[]>;
    const picker = (window as unknown as { showOpenFilePicker?: ShowOpenFilePicker })
      .showOpenFilePicker;
    if (!picker) {
      fileInputRef.current?.click();
      return;
    }
    try {
      const [handle] = await picker({
        types: [
          {
            description: 'Image',
            accept: {
              'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp'],
              // PSD / PSB share Photoshop's official MIME but it isn't
              // a registered "image/*" subtype; declare it under the
              // top-level type so the picker actually surfaces them.
              'application/octet-stream': ['.psd', '.psb'],
              // SVG has its own official MIME — list explicitly so the
              // picker surfaces it as a recognized file type rather
              // than greying it out.
              'image/svg+xml': ['.svg'],
            },
          },
        ],
        multiple: false,
      });
      if (!handle) return;
      const file = await handle.getFile();
      dispatchOpenedFile(file, handle);
    } catch (err) {
      // User dismissed the picker (AbortError) — silently abort.
      // Anything else is unexpected; surface to the console for debugging
      // without breaking the UI.
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        console.warn('[image-editor] showOpenFilePicker failed:', err);
      }
    }
  }, [dispatchOpenedFile]);

  const handleOpenFile = useCallback(() => {
    void pickAndOpenLocalFile();
  }, [pickAndOpenLocalFile]);

  const handleOpenFias = useCallback(() => {
    setFiasPickerOpen(true);
  }, []);

  // File menu — `New` creates a blank 1024×1024 white canvas in a new tab,
  // routed through loadFile so it follows the same per-tab plumbing as a
  // user-opened image.
  const handleNewBlankFile = useCallback(
    (opts?: { width?: number; height?: number; fileName?: string; dpi?: number | null }) => {
      const W = Math.max(1, Math.round(opts?.width ?? 1024));
      const H = Math.max(1, Math.round(opts?.height ?? 1024));
      const fileName = (opts?.fileName?.trim() || 'Untitled') + '.png';
      const offscreen = document.createElement('canvas');
      offscreen.width = W;
      offscreen.height = H;
      const ctx = offscreen.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      offscreen.toBlob((blob) => {
        if (!blob) return;
        const file = new File([blob], fileName, { type: 'image/png' });
        // Always pass an explicit DPI through to loadFile — even
        // `null` — so the new tab's DPI is set inside the
        // FileReader.onload (after the tab-switch effect has
        // saved the outgoing tab's snapshot). This both fixes
        // the outgoing-tab DPI bleed AND clears any inherited
        // DPI when the user creates a plain blank document.
        loadFile(file, null, opts?.dpi ?? null);
      }, 'image/png');
    },
    [loadFile],
  );

  // Tier 3 Group D Slice 1c — bridge from the template picker to
  // the existing new-blank-file flow. Width / height / name flow
  // through; the optional DPI piggybacks via `handleNewBlankFile`'s
  // `dpi` arg so it lands on the new tab's state. Layer content for
  // user templates is deferred — see `lib/user-templates-prefs.ts`.
  const handleNewFromTemplate = useCallback(
    (template: DocumentTemplate) => {
      handleNewBlankFile({
        width: template.width,
        height: template.height,
        fileName: template.name,
        dpi: template.dpi ?? null,
      });
    },
    [handleNewBlankFile],
  );

  /** Save the current canvas to either Local or Fias, awaiting completion.
   *  Used by the toolbar Save handler and the close-confirmation Save
   *  action. For local saves, prefers a stored FileSystemFileHandle
   *  (overwrites the original file in place); falls back to a download in
   *  browsers without the File System Access API. Returns false on failure
   *  and surfaces the underlying error via `saveError` for the caller to
   *  display. */
  const quickSaveCurrent = useCallback(
    async (destination: SaveDestination): Promise<boolean> => {
      const fileName = state.fileName || 'Untitled';
      setSaveError(null);
      try {
        if (destination === 'local') {
          const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
          const handle = activeTab?.fileHandle ?? null;
          if (!handle || !activeTab) {
            // No handle to write back to — fall back to Save-As-Local
            // via `exportToLocal` (FSA picker when available, else
            // `<a download>`). The caller (Cmd+S / File → Save)
            // already routes brand-new docs to the Save-As dialog
            // before reaching here; this is the defensive fallback
            // for browsers without FSA, or the rare path where a tab
            // arrived without a handle.
            const fallbackResult = await exportToLocal(fileName, 'png', 1);
            if (fallbackResult.status === 'cancelled') return false;
            // Always refresh `sourceFileName` so the next Save-As
            // dialog's format dropdown reflects this save's format,
            // even on non-FSA browsers where the handle field stays
            // null. Without this, the dropdown falls through to a
            // stale sourceFileName and shows the wrong format.
            tabs.updateActiveTabMetadata({
              fileName,
              sourceFileName: `${fileName}.png`,
              ...(fallbackResult.status === 'saved-with-handle'
                ? { fileHandle: fallbackResult.handle }
                : {}),
            });
            markClean();
            return true;
          }
          // Honor the handle's extension. Writing the wrong format
          // into the file (e.g. PNG bytes into a `.psd`) would
          // silently corrupt the user's data — the file's bytes
          // change, the extension doesn't, and the user only notices
          // when their next Open shows a flat raster instead of
          // their layered document.
          const handleName = handle.name.toLowerCase();
          let blob: Blob | null = null;
          if (handleName.endsWith('.psd') || handleName.endsWith('.psb')) {
            const result = await exportDocumentToPsd({
              documentId: activeTab.documentId,
              document: state.document,
              canvasWidth: state.canvasWidth,
              canvasHeight: state.canvasHeight,
              layerCanvasProvider: canvas.getLayerExportPayload,
              layerTextProvider: canvas.getLayerTextPayload,
              layerMaskProvider: canvas.getRawMaskCanvas,
              layerShapePathProvider: canvas.getShapeVectorPath,
            });
            blob = new Blob([result.bytes], { type: 'image/vnd.adobe.photoshop' });
          } else if (handleName.endsWith('.svg')) {
            const result = await exportDocumentToSvg({
              documentId: activeTab.documentId,
              document: state.document,
              canvasWidth: state.canvasWidth,
              canvasHeight: state.canvasHeight,
              layerCanvasProvider: canvas.getLayerExportPayload,
              layerTextProvider: canvas.getLayerTextPayload,
            });
            blob = new Blob([result.bytes], { type: 'image/svg+xml' });
          } else if (handleName.endsWith('.pdf')) {
            const canvasDataUrl = canvas.exportToDataURL('jpeg', PDF_IMAGE_JPEG_QUALITY);
            const result = await exportDocumentToPdf({
              documentId: activeTab.documentId,
              document: state.document,
              canvasWidth: state.canvasWidth,
              canvasHeight: state.canvasHeight,
              dpi: state.dpi,
              canvasDataUrl,
            });
            blob = new Blob([result.bytes], { type: 'application/pdf' });
          } else if (handleName.endsWith('.jpg') || handleName.endsWith('.jpeg')) {
            // 0.92 mirrors the browser's `canvas.toBlob` default —
            // high quality without ballooning the file on second-save.
            blob = await canvas.exportToBlob('jpeg', 0.92);
          } else if (handleName.endsWith('.webp')) {
            blob = await canvas.exportToBlob('webp', 0.92);
          } else if (handleName.endsWith('.avif')) {
            blob = await canvas.exportToBlob('avif', 0.92);
          } else if (handleName.endsWith('.tiff') || handleName.endsWith('.tif')) {
            // TIFF is lossless (`utif` encoder) — quality flag is
            // ignored, but pass 1 for clarity.
            blob = await canvas.exportToBlob('tiff', 1);
          } else {
            // `.png` and everything else — default to PNG. Unknown
            // extensions land here too; PNG is the safe lossless
            // choice when we can't tell what the user intended.
            if (!handleName.endsWith('.png')) {
              console.warn(
                `[image-editor] save: unrecognized handle extension "${handleName}", writing as PNG`,
              );
            }
            blob = await canvas.exportToBlob('png', 1);
          }
          if (!blob) {
            setSaveError(`Failed to encode the canvas for ${handleName}.`);
            return false;
          }
          const writable = await handle.createWritable();
          try {
            await writable.write(blob);
          } finally {
            // `close()` flushes — must run even if `write()` threw,
            // or the underlying file is left half-written on disk.
            await writable.close();
          }
        } else {
          // Honor the source format so a Fias-opened JPEG (or WebP /
          // AVIF) saves back as the same format on Cmd+S instead of
          // silently turning into PNG. handleExportFias updates
          // sourceFileName to '<name>.<format>' on every Save-As, so
          // reading it here always reflects the user's most recent
          // explicit format pick. Fias only accepts the four
          // FiasUploadFormat values; anything else (TIFF, structured
          // formats — none of which can be opened from Fias today)
          // falls back to PNG.
          const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
          const inferredFormat = inferExportFormatFromFileName(
            activeTab?.fileHandle?.name ?? activeTab?.sourceFileName ?? state.fileName,
          );
          const fiasFormat: FiasUploadFormat =
            inferredFormat === 'jpeg' || inferredFormat === 'webp' || inferredFormat === 'avif'
              ? inferredFormat
              : 'png';
          // 0.92 mirrors canvas.toBlob's default for lossy formats —
          // high quality without ballooning the file on re-save.
          // PNG is lossless so the quality arg is ignored.
          const lossyQuality = fiasFormat === 'png' ? 1 : 0.92;
          const dataUrl = canvas.exportToDataURL(fiasFormat, lossyQuality);
          const result = await exportToFias({
            sourceDataUrl: dataUrl,
            operations: [],
            output: { format: fiasFormat },
            saveAs: { fileName },
          });
          if (!result) {
            setSaveError('Failed to upload to Fias storage.');
            return false;
          }
        }
        markClean();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Save failed.';
        setSaveError(message);
        console.warn('[image-editor] save failed:', err);
        return false;
      }
    },
    [
      state.fileName,
      state.document,
      state.canvasWidth,
      state.canvasHeight,
      state.dpi,
      exportToLocal,
      exportToFias,
      canvas,
      markClean,
      tabs.tabs,
      tabs.activeTabId,
    ],
  );

  // In-place save (toolbar Save / Ctrl+S / file menu Save). Routes through
  // quickSaveCurrent (which uses the FSA handle for local or processImage
  // for Fias). Falls back to Save As when neither destination is known —
  // the very first save of a brand-new doc, for example.
  const handleToolbarSave = useCallback(async () => {
    if (!tabs.activeTabId) return;
    const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
    if (!activeTab) return;
    const canSaveInPlace = activeTab.fileId !== null || activeTab.fileHandle !== null;
    if (!canSaveInPlace) {
      setExportDialogDestination(activeTab.fileId ? 'fias' : 'local');
      setExportDialogOpen(true);
      return;
    }
    const destination: SaveDestination = activeTab.fileId ? 'fias' : 'local';
    await quickSaveCurrent(destination);
  }, [tabs.activeTabId, tabs.tabs, quickSaveCurrent]);

  const handleSaveAs = useCallback(() => {
    const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
    setExportDialogDestination(activeTab?.fileId ? 'fias' : 'local');
    setExportDialogOpen(true);
  }, [tabs.activeTabId, tabs.tabs]);

  // Tab bar handlers
  const handleTabSwitch = useCallback(
    (id: string) => {
      tabs.setActiveTabId(id);
    },
    [tabs],
  );

  const handleTabClose = useCallback(
    (id: string) => {
      // Don't open a second-level dialog if the export dialog or the
      // close-confirm dialog is already mounted — that creates an
      // ambiguous "what does Save-As close?" race window.
      if (exportDialogOpen || confirmCloseTabId !== null) return;
      const tab = tabs.tabs.find((t) => t.id === id);
      if (!tab) return;
      if (!tab.isDirty) {
        void tabs.closeTab(id);
        return;
      }
      // Switch to the dirty tab first so the canvas reflects its content
      // when the user is asked. The tab-switch effect will load its
      // snapshot (if it has one).
      if (tabs.activeTabId !== id) tabs.setActiveTabId(id);
      setConfirmCloseTabId(id);
    },
    [tabs, exportDialogOpen, confirmCloseTabId],
  );

  /** File menu — Close the active tab. Handles the dirty-confirm flow via
   *  the same path as clicking the X on the tab. */
  const handleCloseActiveFile = useCallback(() => {
    if (tabs.activeTabId) handleTabClose(tabs.activeTabId);
  }, [tabs.activeTabId, handleTabClose]);

  const handleConfirmCancel = useCallback(() => {
    setConfirmCloseTabId(null);
    setSaveError(null);
  }, []);

  const handleConfirmDiscard = useCallback(() => {
    if (confirmCloseTabId) void tabs.closeTab(confirmCloseTabId);
    setConfirmCloseTabId(null);
    setSaveError(null);
  }, [confirmCloseTabId, tabs]);

  const handleConfirmSave = useCallback(async () => {
    if (!confirmCloseTabId) return;
    const tab = tabs.tabs.find((t) => t.id === confirmCloseTabId);
    // Save back to wherever the file came from. Fias if it has a fileId,
    // local otherwise — overrides happen in the Save-As dialog, not here.
    const destination: SaveDestination = tab?.fileId ? 'fias' : 'local';
    setIsQuickSaving(true);
    const ok = await quickSaveCurrent(destination);
    setIsQuickSaving(false);
    if (!ok) return; // Leave the dialog open so the user can retry / Save As.
    void tabs.closeTab(confirmCloseTabId);
    setConfirmCloseTabId(null);
  }, [confirmCloseTabId, tabs, quickSaveCurrent]);

  const handleConfirmSaveAs = useCallback(() => {
    if (!confirmCloseTabId) return;
    // Stash the tab id so the export handlers can close the tab after a
    // successful export, then dismiss the confirm dialog and open the full
    // Save As / Export dialog with the tab's original destination prefilled.
    const tab = tabs.tabs.find((t) => t.id === confirmCloseTabId);
    closeAfterExportTabIdRef.current = confirmCloseTabId;
    setExportDialogDestination(tab?.fileId ? 'fias' : 'local');
    setConfirmCloseTabId(null);
    setExportDialogOpen(true);
  }, [confirmCloseTabId, tabs.tabs]);

  const handleExportDialogClose = useCallback(() => {
    setExportDialogOpen(false);
    // Drop any pending close-after-export request — set when Save-As was
    // chosen but cleared whether the user cancelled OR the export already
    // ran (the export handlers fire BEFORE onClose, so by the time we get
    // here a successful save has already closed the tab).
    closeAfterExportTabIdRef.current = null;
  }, []);

  const handleNewTab = useCallback(() => {
    // Same as toolbar Open — funnel through the file picker so the user
    // immediately picks what to open. (A blank tab waiting for content
    // would be confusing; both entry points go through loadFile.)
    void pickAndOpenLocalFile();
  }, [pickAndOpenLocalFile]);

  const handleFiasImageSelected = useCallback(
    async (file: { fileId: string; fileName: string; dataUrl: string }) => {
      const displayName = file.fileName.replace(/\.[^.]+$/, '');
      // Always open in a new tab (matches loadFile behavior).
      tabs.addTab({ fileName: displayName, fileId: file.fileId, sourceFileName: file.fileName });
      const fitZoom = await canvas.loadImage(file.dataUrl);
      const bgLayer: Layer = {
        id: nextLayerId(),
        name: 'Background',
        type: 'image',
        visible: true,
        parentId: null,
      };
      canvas.setBackgroundLayerId(bgLayer.id);
      resetLayers([bgLayer], bgLayer.id);
      setZoom(fitZoom);
      setFileInfo(displayName, file.fileId);
      setHasImage(true);
      markClean();
      // See the comment in `loadFile` — direct saveSnapshot here would
      // capture the layers list before React commits resetLayers, so
      // the first undo wipes the Background layer. Use the clean-pending
      // flag so the loaded file isn't marked dirty.
      history.clearHistory();
      cleanSnapshotPendingRef.current = true;
      cleanSnapshotLabelRef.current = 'Open';
    },
    [canvas, setZoom, setFileInfo, markClean, history, tabs, nextLayerId, resetLayers],
  );

  const handleRemoveBackground = useCallback(async () => {
    setRemoveBackgroundError(null);
    setIsRemovingBackground(true);
    try {
      // imgly path: hook handles the imgly call internally — no override.
      // Paid path: hand the layer's source blob to a processor that hits
      // the backend route, then return the resulting transparent PNG so
      // the hook can swap it onto the active layer (preserving placement).
      if (removeBackgroundEngine === 'imgly') {
        await canvas.removeBackground();
      } else {
        await canvas.removeBackground({
          processBlob: async (sourceBlob) => {
            const sourceDataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
              reader.readAsDataURL(sourceBlob);
            });
            const response = await api.smartRemoveBackgroundImage({
              imageDataUrl: sourceDataUrl,
              engine: removeBackgroundEngine,
            });
            const resultResponse = await fetch(response.imageDataUrl);
            return await resultResponse.blob();
          },
        });
      }
      markDirty();
      history.saveSnapshot();
    } catch (err) {
      setRemoveBackgroundError(err instanceof Error ? err.message : 'Failed to remove background');
    } finally {
      setIsRemovingBackground(false);
    }
  }, [canvas, markDirty, history, removeBackgroundEngine]);

  /** Apply a Canvas Size resize from the dialog. Anchor-based shift,
   *  no pixel resampling. Side-effects in order:
   *   1. Refuse when a modal canvas mode (crop / transform / quick
   *      mask / path edit) is active — those modes carry overlay
   *      objects that would get shifted alongside scene objects and
   *      end up out of sync with their targets. The dialog itself
   *      doesn't surface this gate; the toast is the fallback.
   *   2. Discard any active marquee selection (its bbox is in scene
   *      coords; shifting it would land it in a stale spot relative
   *      to the new bounds, and re-shifting via canvas.setCanvasSize
   *      is harder than just clearing).
   *   3. Delegate to `canvas.setCanvasSize`, which shifts every
   *      Fabric object + paint surface and returns the applied
   *      (dx, dy).
   *   4. Shift slices in state by the same offset so they stay
   *      anchored to the same image pixels.
   *   5. markDirty + history.saveSnapshot — single undo entry per
   *      apply, label includes the new dimensions so the History
   *      panel reads meaningfully ("Canvas size 1024×768"). */
  const handleApplyCanvasSize = useCallback(
    (
      newWidth: number,
      newHeight: number,
      anchorX: AnchorFactor,
      anchorY: AnchorFactor,
      fillColor: string | null,
    ) => {
      if (
        state.crop.isActive ||
        state.transformActive ||
        state.quickMaskActive ||
        state.pathEditActive
      ) {
        toast({
          title: 'Finish the current operation first',
          description:
            'Canvas resize is disabled while Crop, Transform, Quick Mask, or Path Edit is active.',
          variant: 'destructive',
        });
        return;
      }
      if (canvas.hasMarqueeSelection) {
        canvas.clearMarquee();
      }
      const result = canvas.setCanvasSize(newWidth, newHeight, anchorX, anchorY);
      if (!result) return;
      if (result.dx !== 0 || result.dy !== 0) {
        shiftSlices(result.dx, result.dy);
      }
      // Extension fill: chrome rect at canvas bounds sent to the
      // back. Scoped to document bounds by the rect's own geometry
      // (no clipPath trick needed). Renders BEHIND every user layer
      // so the fill shows only where layers are transparent or
      // absent (e.g. the new area after an expand, or the gaps
      // around a Bg image the user has dragged within the visible
      // canvas). Non-destructive — the bg layer's pixel buffer is
      // never modified, so shrinking the canvas hides pixels
      // visually but preserves them; select + drag the bg reveals
      // hidden regions.
      // Known limit (deferred): PSD / SVG / PDF exports don't
      // currently pick up the chrome fill rect — raster exports
      // (PNG / JPEG / WebP / AVIF / TIFF) do.
      canvas.setCanvasFillColor(fillColor);
      markDirty();
      history.saveSnapshot({ label: `Canvas size ${newWidth}×${newHeight}` });
    },
    [
      canvas,
      shiftSlices,
      markDirty,
      history,
      state.crop.isActive,
      state.transformActive,
      state.quickMaskActive,
      state.pathEditActive,
    ],
  );

  // Stable onClose for CanvasSizeDialog so its Escape-key effect
  // doesn't tear down and re-subscribe on every parent render.
  const handleCloseCanvasSize = useCallback(() => setCanvasSizeOpen(false), []);

  // Adjustments modal lifecycle. Picking a kind from the picker
  // captures the active-layer paint target (or background fallback);
  // the modal then drives live preview by re-applying the kind's
  // spec to the cached source on every value change. Apply commits
  // + snapshots; Cancel reverts via a putImageData(source) blit.
  // No history entry is created when the user cancels.
  // Tracks the deferred-clear timer so a quick close→reopen cycle
  // doesn't let the previous close's `setAdjustmentTargets([])` fire
  // AFTER the new open populated targets — which would silently wipe
  // them and leave a blank dialog. Cleared at the top of each open.
  const adjustmentClearTimerRef = useRef<number | null>(null);

  const handleOpenAdjustment = useCallback(
    (kind: AdjustmentKind) => {
      const targets = canvas.getActiveAdjustmentTargets(state.document);
      if (targets.length === 0) return;
      // Cancel any pending clear from a recent close.
      if (adjustmentClearTimerRef.current !== null) {
        window.clearTimeout(adjustmentClearTimerRef.current);
        adjustmentClearTimerRef.current = null;
      }
      setAdjustmentTargets(targets);
      // Sync the fabric-objs ref alongside the state so preview-tick
      // (read through the ref) doesn't lag a render behind. Updating
      // here at the lifecycle boundary avoids a render-phase side
      // effect that would re-run on every parent re-render.
      adjustmentFabricObjsRef.current = targets.map((t) => t.fabricObj);
      // Picker-driven opens are always destructive — clear any
      // stale edit-mode state from a recent close→reopen cycle.
      setEditingAdjustment(null);
      setAdjustmentSessionId((n) => n + 1);
      setActiveAdjustmentKind(kind);
    },
    [canvas, state.document],
  );

  // Derived from React state alone (cheap; doesn't need to inspect
  // Fabric objects). Drives the picker's disabled state — when the
  // active layer is text/shape/adjustment/empty-group, opening the
  // picker would no-op. Better to surface that up front than to let
  // the user click and see nothing happen. Disabled when no layer
  // is selected too (matches the resolver, which requires
  // `activeLayerId`).
  const canAdjustActiveLayer = useMemo(
    () => hasRasterizableTarget(state.document, state.activeLayerId),
    [state.document, state.activeLayerId],
  );

  // Live targets' fabric objects, kept in a ref so the preview-tick
  // callback's identity doesn't change with the state. Updated at
  // open + close lifecycle boundaries — NOT in render phase, which
  // would re-run on every parent re-render and regenerate the array
  // even when the dialog isn't open.
  const adjustmentFabricObjsRef = useRef<fabric.FabricObject[]>([]);

  // Memoized `canvas` array passed to AdjustmentsModal. Without
  // this, the inline `.map` allocates a fresh array on every
  // EditorPage render — and EditorPage has many state hooks that
  // change frequently (cursor position, AI status, viewport, etc.).
  // The body's source-capture effect deps on this prop; a fresh
  // ref re-fires the effect, which re-runs `getImageData` per
  // target plus the full-res→preview-size canvas downscale on
  // every parent render. On a 4K image that's ~30 MB allocated
  // and a 4K→768 stretch per render — the lag the user was seeing.
  const adjustmentTargetCanvases = useMemo(
    () => adjustmentTargets.map((t) => t.canvas),
    [adjustmentTargets],
  );

  // Tell Fabric the backing pixels of every adjustment target
  // changed so the next render reads the fresh image data instead
  // of cached. Called by the modal after every preview re-paint and
  // after the cancel-repaint-source pass.
  /** Synchronous slow-path re-composite. The destructive preview
   *  modal writes new pixels into the source layer's canvas at
   *  every slider tick, but `state.document` doesn't change — so
   *  the rAF-coalesced slow-path effect doesn't re-fire on its
   *  own. Without this, every adjustment layer above the source
   *  would keep showing its pre-edit backing pixels, occluding
   *  the live preview. Running synchronously inside the modal's
   *  preview-tick callback (itself already rAF-throttled to one
   *  pass per frame) keeps total per-frame work bounded; the
   *  per-pixel mask blend was replaced with GPU composite ops so
   *  the slow-path itself completes in a few ms even on 4K.
   *
   *  Reads `documentRef.current` (not the closure-captured
   *  `state.document`) so that when this callback is invoked from an
   *  rAF scheduled BEFORE a state.document mutation, the composite
   *  still runs against the freshest document. The bug this guards
   *  against: "Apply as adjustment layer" calls `revertToSources` →
   *  `onPreviewTick` → schedules an rAF here against the pre-Apply
   *  document. Then `handleCreateAdjustmentLayer` adds the new
   *  adjustment to state.document. The slow-path effect's own rAF
   *  scheduling sees `slowPathRafRef.current !== null` (this path's
   *  rAF) and bails. When the rAF fires, it must use the LATEST
   *  document or the freshly-added adjustment layer gets skipped —
   *  its backing stays empty and the user only sees the effect after
   *  a visibility toggle (which mutates state.document again and
   *  re-triggers the slow path). */
  const runSlowPathCompositeSync = useCallback(() => {
    const fc = canvasRef.current;
    if (!fc) return;
    const doc = documentRef.current;
    if (!requiresCustomComposite(doc)) return;
    if (hasUnimplementedSlowPath(doc)) return;
    renderDocument({
      document: doc,
      width: state.canvasWidth,
      height: state.canvasHeight,
      paintLeaf: paintLeafToCanvas,
      getAdjustmentBacking: getAdjustmentBackingCanvas,
      cache: compositorCacheRef.current,
      proxyScratch: proxyScratchRef.current,
      // Commit path: always full-resolution. handleAdjustmentCommit
      // invokes this AFTER clearing the interactive flag so the
      // post-Apply history snapshot captures crisp pixels even if
      // the user dragged at proxy resolution beforehand.
      interactiveMode: false,
    });
    for (const id in doc.layers) {
      const node = doc.layers[id];
      if (node.type !== 'adjustment') continue;
      for (const obj of fc.getObjects()) {
        const objLayerId = (obj as unknown as { layerId?: string }).layerId;
        if (objLayerId !== id) continue;
        obj.dirty = true;
      }
    }
    fc.requestRenderAll();
  }, [
    canvasRef,
    paintLeafToCanvas,
    getAdjustmentBackingCanvas,
    // No `state.document` — body reads `documentRef.current` so
    // late-firing rAFs use the freshest document. Including
    // state.document here would re-create the callback on every
    // document change, which has no benefit and forces every caller
    // (preview-tick handler, commit handler) to re-memo too.
    state.canvasWidth,
    state.canvasHeight,
  ]);

  const handleAdjustmentPreviewTick = useCallback(() => {
    const fc = canvas.canvasRef.current;
    if (!fc) return;
    for (const obj of adjustmentFabricObjsRef.current) {
      obj.dirty = true;
      // The adjustment preview pump writes pixels into the target
      // canvas directly (`putImageData` / `drawImage`) without going
      // through `FabricImage.setElement` — so the paint-surface
      // `getSrc` dataURL cache (`installPaintSurfaceSerializer`) is
      // never auto-invalidated. Without this explicit punch, the
      // post-Apply history snapshot serializes the PRE-Apply cached
      // dataURL and a refresh reverts the layer to its un-adjusted
      // pixels — visually dramatic for Black & White, silently lossy
      // for Hue/Sat / Curves / Levels / etc.
      if (obj instanceof fabric.FabricImage) invalidatePaintSurfaceSrcCache(obj);
    }
    fc.requestRenderAll();
    // Destructive preview is the one path that mutates a leaf's
    // pixel content WITHOUT changing state.document — the modal
    // writes new pixels straight into the active target canvas each
    // rAF tick. The compositor cache fingerprints state.document
    // only (Fabric-side pixel content is intentionally excluded —
    // see render-pipeline.ts), so without this invalidation the
    // next slow-path pass would reuse stale backings for every
    // adjustment above the modified leaf and the preview would
    // appear frozen. Edit-mode and eye-toggle paths DON'T flow
    // through here (edit mode dispatches state.document changes
    // which invalidate via fingerprint), so the cache penalty is
    // scoped to destructive preview drags.
    clearRenderCache(compositorCacheRef.current);
    // Schedule the slow-path on the NEXT animation frame instead of
    // running it inline. The modal's own preview rAF is what called
    // us; running the heavy compositor synchronously here would
    // stretch a single frame to (source-apply + slow-path) which on
    // 4K with stacked adjustments visibly stutters the slider. By
    // deferring one frame the source updates immediately and the
    // cumulative composite catches up next frame — imperceptible
    // delay (16ms), no main-thread stall. Coalesces with the
    // state.document-driven scheduling so rapid ticks collapse.
    if (slowPathRafRef.current !== null) return;
    slowPathRafRef.current = requestAnimationFrame(() => {
      slowPathRafRef.current = null;
      runSlowPathCompositeSync();
    });
  }, [canvas, runSlowPathCompositeSync]);

  const handleAdjustmentCommit = useCallback(() => {
    // Pass the adjustment / filter kind's display name through so the
    // History panel reads "Color Balance" / "Gaussian Blur" instead of
    // resolving to the active tool's label (modal-driven actions
    // have no tool context).
    const label = activeAdjustmentKind ? adjustmentDisplayName(activeAdjustmentKind) : undefined;
    history.saveSnapshot(label ? { label } : undefined);
    markDirty();
    // For non-destructive edit-mode Apply: the live-edit pipeline
    // schedules an rAF-coalesced composite on every slider tick, but
    // a late slider tick's rAF can still be pending when the user
    // clicks Apply — and the modal close that follows doesn't itself
    // re-trigger the slow path. The pending rAF *should* fire on the
    // next animation frame and read `documentRef.current` correctly,
    // but in practice users reported the adjustment not appearing
    // after close until they toggled visibility. Running a
    // synchronous composite here makes the post-Apply display
    // deterministic — the backings are guaranteed to hold the
    // committed config's pixels before Fabric's next render. Cost is
    // one extra composite pass; if the rAF was already going to do
    // identical work, the redundant sync pass just pre-empts it.
    if (editingAdjustment) {
      runSlowPathCompositeSync();
      return;
    }
    // Tier 2 Group F — Destructive Apply (`editingAdjustment` null
    // AND "Apply as adjustment layer" was OFF — the layer-creation
    // path runs `onCreateAdjustmentLayer`, NOT `onCommit`, so reaching
    // this branch means the modal body wrote pixels into each target
    // layer's surface). The pure helper handles the
    // edit-mode-vs-destructive branching + layerId extraction +
    // dedupe so we can unit-test that decision in isolation; here we
    // just dispatch.
    const dispatcher = setLayerRasterModifiedRef.current;
    if (!dispatcher) return;
    const targets = adjustmentFabricObjsRef.current.map((obj) => ({
      layerId: getObjectLayerId(obj),
    }));
    const layerIds = getDestructiveAdjustmentLayerIds(targets, editingAdjustment);
    for (const layerId of layerIds) {
      dispatcher(layerId, true);
    }
  }, [history, markDirty, editingAdjustment, runSlowPathCompositeSync, activeAdjustmentKind]);

  // Close: clear `kind` immediately so the open prop flips false and
  // radix plays its close animation. Defer clearing the targets +
  // freeing the source ImageDatas (held in body state) until after
  // the animation finishes — clearing synchronously would render the
  // modal with empty props mid-animation and lose its content.
  // The deferred clear is tracked via a ref so a quick close→reopen
  // can cancel it before it stomps on the next session's targets.
  const handleAdjustmentClose = useCallback(() => {
    setActiveAdjustmentKind(null);
    adjustmentClearTimerRef.current = window.setTimeout(() => {
      adjustmentClearTimerRef.current = null;
      setAdjustmentTargets([]);
      adjustmentFabricObjsRef.current = [];
      setEditingAdjustment(null);
    }, 250);
  }, []);

  /** User tapped an adjustment-layer thumb in the LayersPanel.
   *  Open the modal in non-destructive edit mode pre-filled with
   *  the layer's current config. `editingAdjustment` carries the
   *  original so Cancel can revert via one final
   *  `UPDATE_ADJUSTMENT`. */
  const handleEditAdjustmentLayer = useCallback((layerId: string) => {
    const node = documentRef.current.layers[layerId];
    if (!node || node.type !== 'adjustment' || !node.adjustment) return;
    // Cancel any pending close-side cleanup from a recent close.
    if (adjustmentClearTimerRef.current !== null) {
      window.clearTimeout(adjustmentClearTimerRef.current);
      adjustmentClearTimerRef.current = null;
    }
    setEditingAdjustment({ layerId, original: node.adjustment });
    // Edit mode bypasses the destructive preview pipeline — no
    // target canvases to populate.
    setAdjustmentTargets([]);
    adjustmentFabricObjsRef.current = [];
    setAdjustmentSessionId((n) => n + 1);
    setActiveAdjustmentKind(node.adjustment.kind);
  }, []);

  /** Edit-mode live preview: every value change in the modal
   *  dispatches an `UPDATE_ADJUSTMENT` against the editing layer.
   *  The slow-path effect on `state.document` re-runs the
   *  compositor and the user sees the new config. */
  const handleAdjustmentLiveEdit = useCallback(
    (config: AdjustmentConfig) => {
      if (!editingAdjustment) return;
      updateAdjustment(editingAdjustment.layerId, config);
    },
    [editingAdjustment, updateAdjustment],
  );

  /** User toggled "Apply as adjustment layer" + clicked Apply.
   *  Body already reverted the destructive preview pixels — our
   *  job is to:
   *  1. Add the adjustment node immediately above the active
   *     layer, marked `clipToBelow: true` so its effect is scoped
   *     to JUST that layer's silhouette (matches user intent of
   *     "adjust THIS layer" — sidesteps the Photoshop default of
   *     affecting every layer below, which is confusing on first
   *     encounter and the wrong default for the picker entry-point).
   *  2. Create the Fabric display object that wraps the
   *     adjustment's backing canvas.
   *  3. Keep the source layer active so the user can immediately
   *     re-open Adjust… on it (auto-activating the new adjustment
   *     would disable the picker — adjustment layers themselves
   *     fail `hasRasterizableTarget`).
   *  The slow-path effect on `state.document` fires on the next
   *  tick, runs the compositor (which now honors `clipToBelow`),
   *  and writes the post-adjustment pixels into the new backing
   *  canvas. */
  const handleCreateAdjustmentLayer = useCallback(
    (config: AdjustmentConfig) => {
      const existingAdjustments = Object.values(state.document.layers).filter(
        (l) => l.type === 'adjustment',
      ).length;
      // Soft cap on adjustment-layer count. Each adjustment owns a
      // full-canvas-sized backing Canvas (4K ≈ 64MB), so an
      // unbounded stack exhausts browser memory with no signal to
      // the user. The cap matches a typical pro-photo retouch
      // workflow envelope; users who need more should bake earlier
      // adjustments into the source first (or split into multiple
      // documents). Surfaced as a toast at the creation site rather
      // than silently dropped.
      if (existingAdjustments >= MAX_ADJUSTMENT_LAYERS) {
        toast({
          title: 'Adjustment layer limit reached',
          description: `Documents are limited to ${MAX_ADJUSTMENT_LAYERS} adjustment layers. Delete an existing one before creating another.`,
          variant: 'destructive',
        });
        return;
      }
      const layerId = nextLayerId();
      const adjustmentCount = existingAdjustments + 1;
      const name = `${adjustmentDisplayName(config.kind)} ${adjustmentCount}`;
      // Resolve insertion position. When an active layer exists,
      // insert at its sibling-index so the adjustment lands DIRECTLY
      // above it (front-most-first ordering means lower index =
      // closer to front). When no active layer (rare), fall back to
      // root front.
      const activeId = state.activeLayerId;
      const activeNode = activeId ? state.document.layers[activeId] : null;
      const parentId = activeNode?.parentId ?? null;
      const siblings =
        parentId === null
          ? state.document.rootIds
          : (state.document.layers[parentId]?.childIds ?? []);
      const activeIdx = activeId ? siblings.indexOf(activeId) : -1;
      const insertIndex = activeIdx >= 0 ? activeIdx : 0;
      addAdjustment(layerId, name, config, {
        parentId,
        index: insertIndex,
        clipToBelow: activeNode != null,
        setActive: false,
      });
      // Pass dims explicitly — the hook's `imageDimsRef` is only
      // written by `initEmptyCanvas` / `loadImage`. After a session
      // restore + a fresh adjustment add, the ref can still be {0,0}
      // and the backing canvas allocation would bail silently. Same
      // rationale as the mount effect (see ~line 3698).
      canvas.addAdjustmentLayer({
        layerId,
        width: state.canvasWidth,
        height: state.canvasHeight,
      });
      // Pre-compute the post-insert layer order so Fabric reorders
      // immediately rather than waiting for the next state-driven
      // applyLayerOrder effect tick.
      const existingIds = state.layers.map((l) => l.id);
      const activeFlatIdx = activeId ? existingIds.indexOf(activeId) : -1;
      const flatInsertAt = activeFlatIdx >= 0 ? activeFlatIdx : 0;
      const orderedIds = [
        ...existingIds.slice(0, flatInsertAt),
        layerId,
        ...existingIds.slice(flatInsertAt),
      ];
      canvas.applyLayerOrder(orderedIds);
      // Defer the history snapshot via `layerSnapshotPendingRef` so it
      // fires from the post-commit useEffect — an inline
      // `history.saveSnapshot()` would capture `layersStateRef.current`
      // BEFORE React commits the `addAdjustment` dispatch above, so
      // the entry's `extra.document` would lack the just-added
      // adjustment layer. On refresh, `history.restoreHistory` loads
      // the latest entry and routes that stale `extra.document` back
      // into state via `resetLayerDocument`, dropping the layer.
      // (Same fix as `handleApplyPathEdit` — see the comment there.)
      // markDirty fires alongside saveSnapshot inside the pending-
      // snapshot effect; no need to call it inline.
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = `Add ${adjustmentDisplayName(config.kind)} layer`;
    },
    [addAdjustment, canvas, nextLayerId, state.activeLayerId, state.document, state.layers],
  );

  // Cancel the deferred clear on unmount so a stale state setter
  // doesn't fire after the editor tears down.
  useEffect(
    () => () => {
      if (adjustmentClearTimerRef.current !== null) {
        window.clearTimeout(adjustmentClearTimerRef.current);
        adjustmentClearTimerRef.current = null;
      }
    },
    [],
  );

  // Cost-protection ref guard for AI handlers.
  //
  // The AI panel button is disabled-while-busy via `aiStatus`, but state
  // commits aren't synchronous — between the user's first click and
  // React painting the disabled button there's a small window where a
  // double-click, a programmatic re-trigger, or a strict-mode double
  // invoke could fire a second handler. Each fired handler calls a paid
  // upstream provider (BFL or OpenAI), so we treat it like money: a ref
  // guard that closes the door before any state mutation, and never
  // opens it again until the handler's `finally` runs. Deliberately
  // shared between Smart Delete and Smart Create because Smart Delete's
  // first run also incurs a (free, but expensive in user time) model
  // download, and we don't want to interleave the two.
  const aiInFlightRef = useRef(false);

  // AI Select Subject — runs `@imgly/background-removal` (the same
  // free in-browser ONNX model that powers the default Remove BG
  // path) on the active layer's source pixels, alpha-thresholds the
  // cutout into a binary mask, and seeds a wand-style marquee. No
  // server round-trip, no credits charged. Image pixels are not
  // modified.
  const handleSelectSubject = useCallback(async () => {
    if (aiInFlightRef.current) return;
    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Selecting subject…' });
    try {
      const maskCanvas = await canvas.extractSubjectMaskCanvas();
      if (!maskCanvas) throw new Error('No image to segment');

      // Switch React's marquee shape to 'wand' so the marquee
      // panel's shape buttons highlight the wand tile (the AI-
      // installed selection IS a wand-style mask). The hook's
      // `setMarqueeShape` effect is idempotent when the canvas-
      // side shape already matches, so this is a UI-sync push,
      // not a defensive workaround.
      if (state.marqueeSettings.shape !== 'wand') {
        updateMarqueeSettings({ shape: 'wand' });
      }

      const installed = canvas.setSelectionFromMask(maskCanvas);
      if (!installed) throw new Error("Couldn't find a subject in the image");

      // Switch to the marquee tool so the new selection feels
      // "active" — the marquee panel surfaces the selection ops
      // immediately. The status-bar chip exposes the same ops
      // tool-independently, but the tool switch matches what the
      // user just asked for.
      if (state.activeTool !== 'marquee') {
        setActiveTool('marquee');
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Select Subject failed';
      setAiStatus({ kind: 'error', message });
    } finally {
      aiInFlightRef.current = false;
    }
  }, [canvas, state.activeTool, state.marqueeSettings.shape, setActiveTool, updateMarqueeSettings]);

  // AI tool handlers. Smart Delete runs the in-browser MI-GAN pipeline
  // on the active layer's marqueed region (no upstream cost). Smart
  // Create either:
  //   • Generates a new image from prompt (BFL FLUX 2 Pro) when no
  //     marquee is active — added as a new layer.
  //   • Inpaints into the marquee with mask-aware blending (OpenAI
  //     gpt-image-1.5 edits) when a marquee is active — composited back
  //     onto the active layer's paint surface.
  const handleSmartDelete = useCallback(async () => {
    if (aiInFlightRef.current) return;
    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Preparing…' });
    try {
      const ok = await canvas.runSmartDeleteOnActiveLayer((source, mask) =>
        runSmartDelete(source, mask, (status) => {
          // Translate the lib's bare progress events to the AIPanel's
          // banner format (running needs a user-facing label).
          if (status.kind === 'loading-model') {
            setAiStatus({ kind: 'loading-model', progressPct: status.progressPct });
          } else {
            setAiStatus({ kind: 'running', label: 'Running Smart Delete…' });
          }
        }),
      );
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Smart Delete needs a marquee selection on a paintable layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Smart Delete failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Smart Delete failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [canvas]);

  // Mask-aware inpaint — replaces the marquee selection with content
  // matching `inpaintPrompt`. Three engines, picked via the dropdown:
  //   - Stability (default): full source + grayscale mask, supports
  //     negative prompt and grow_mask. Min input 64 px.
  //   - Recraft: full source + grayscale mask (server re-encodes to
  //     single-channel). Min input 256 px. Prompt-only.
  //   - OpenAI: cropped to bbox (gpt-image-1 fills the whole frame
  //     otherwise), inverted-alpha mask. Min input 256 px.
  //
  // The composite step is shared: feather the editor mask, alpha-clip
  // the engine result to that feathered mask, draw over the source.
  // The wrapper's destination-in with the un-feathered mask then
  // strips anything outside the original marquee, so off-marquee
  // pixels stay byte-identical regardless of engine.
  const handleInpaint = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const prompt = inpaintPrompt.trim();
    if (prompt.length === 0) return;

    const bbox = canvas.getMarqueeBounds();
    if (!bbox) {
      setAiStatus({
        kind: 'error',
        message: 'Inpaint requires a marquee selection. Draw a selection first.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Inpainting selection…' });
    try {
      const ok = await canvas.runSmartDeleteOnActiveLayer(async (source, mask, maskBbox) => {
        const sourceW = source.width;
        const sourceH = source.height;

        let imageDataUrl: string;
        let maskDataUrl: string;
        let resultPatchRect: { left: number; top: number; width: number; height: number };

        if (inpaintEngine === 'stability') {
          // Stability respects mask boundaries strictly, so surrounding
          // context helps the model produce coherent fills. Send the
          // full source + grayscale mask, scaled to fit Stability's
          // [64, 1536] per-side bounds.
          const fittedSource = fitCanvasToMaxDim(
            source,
            SMART_CREATE_MAX_INPUT_EDGE_PX,
            STABILITY_MIN_INPUT_EDGE_PX,
          );
          const grayscaleMask = toGrayscaleMask(mask);
          const fittedMask = fitCanvasToMaxDim(
            grayscaleMask,
            SMART_CREATE_MAX_INPUT_EDGE_PX,
            STABILITY_MIN_INPUT_EDGE_PX,
          );
          imageDataUrl = fittedSource.toDataURL('image/png');
          maskDataUrl = fittedMask.toDataURL('image/png');
          resultPatchRect = { left: 0, top: 0, width: sourceW, height: sourceH };
        } else {
          // Recraft + OpenAI — crop to bbox. Both engines size their
          // generated content for the whole input frame regardless of
          // the mask, so a full-source send produces output where the
          // generated subject fills the entire image and only a tiny
          // slice of it falls inside the marquee. With crop-to-bbox the
          // marquee bbox *is* the input frame, so the generated subject
          // fills the marquee. Recraft min input is 256 px (matches
          // OpenAI), so the same constant works for both.
          const cropLeft = maskBbox.left;
          const cropTop = maskBbox.top;
          const cropW = maskBbox.width;
          const cropH = maskBbox.height;
          const minDim =
            inpaintEngine === 'recraft' ? RECRAFT_MIN_INPUT_EDGE_PX : OPENAI_MIN_INPUT_EDGE_PX;

          const croppedSource = document.createElement('canvas');
          croppedSource.width = cropW;
          croppedSource.height = cropH;
          croppedSource
            .getContext('2d')
            ?.drawImage(source, cropLeft, cropTop, cropW, cropH, 0, 0, cropW, cropH);

          const croppedMask = document.createElement('canvas');
          croppedMask.width = cropW;
          croppedMask.height = cropH;
          croppedMask
            .getContext('2d')
            ?.drawImage(mask, cropLeft, cropTop, cropW, cropH, 0, 0, cropW, cropH);

          const fittedSource = fitCanvasToMaxDim(
            croppedSource,
            SMART_CREATE_MAX_INPUT_EDGE_PX,
            minDim,
          );
          // Recraft reads the mask as grayscale; the editor's mask
          // works as-is with the server's sharp re-encode. OpenAI gets
          // its alpha inverted server-side. Either way, no extra
          // client-side conversion is needed for the cropped path.
          const fittedMask = fitCanvasToMaxDim(croppedMask, SMART_CREATE_MAX_INPUT_EDGE_PX, minDim);
          imageDataUrl = fittedSource.toDataURL('image/png');
          maskDataUrl = fittedMask.toDataURL('image/png');
          resultPatchRect = { left: cropLeft, top: cropTop, width: cropW, height: cropH };
        }

        const negativePrompt = inpaintNegativePrompt.trim();
        const stylePreset = inpaintStylePreset.trim();
        const response = await api.smartInpaintImage({
          imageDataUrl,
          maskDataUrl,
          prompt,
          engine: inpaintEngine,
          ...(negativePrompt.length > 0 && { negativePrompt }),
          ...(inpaintGrowMaskPx > 0 && { growMask: inpaintGrowMaskPx }),
          ...(stylePreset.length > 0 && { stylePreset }),
        });
        const inpaintResult = await loadImageToCanvas(response.imageDataUrl);

        // Composite path: feather the mask, build an alpha-clipped
        // patch, draw it over the source. Stability/Recraft return a
        // full-source-sized result (resultPatchRect = 0,0,W,H);
        // OpenAI returns a bbox-sized result (drawn back into bbox).
        const featherRadius = computeFeatherRadius(
          maskBbox.width,
          maskBbox.height,
          inpaintFeatherRatio,
        );
        const featheredMask = featherRadius > 0 ? featherMask(mask, featherRadius) : mask;
        const patch = document.createElement('canvas');
        patch.width = sourceW;
        patch.height = sourceH;
        const patchCtx = patch.getContext('2d');
        if (patchCtx) {
          patchCtx.drawImage(
            inpaintResult,
            0,
            0,
            inpaintResult.width,
            inpaintResult.height,
            resultPatchRect.left,
            resultPatchRect.top,
            resultPatchRect.width,
            resultPatchRect.height,
          );
          patchCtx.globalCompositeOperation = 'destination-in';
          patchCtx.drawImage(featheredMask, 0, 0);
        }

        const fullResult = document.createElement('canvas');
        fullResult.width = sourceW;
        fullResult.height = sourceH;
        const ctx = fullResult.getContext('2d');
        if (ctx) {
          ctx.drawImage(source, 0, 0);
          ctx.drawImage(patch, 0, 0);
        }
        return fullResult;
      });
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Inpaint needs a paintable active layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Inpaint failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Inpaint failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [
    inpaintPrompt,
    inpaintNegativePrompt,
    inpaintEngine,
    inpaintGrowMaskPx,
    inpaintFeatherRatio,
    inpaintStylePreset,
    canvas,
  ]);

  // Paid AI cleanup. Mirrors handleInpaint but with no prompt and a
  // dedicated /smart-erase route that dispatches to Stability's
  // /v2beta/stable-image/edit/erase endpoint. Costs ~4 credits per call.
  // The feather post-process matches Inpaint's so the result blends back
  // into the layer cleanly; `eraseGrowMaskPx` is forwarded to Stability
  // server-side via the `grow_mask` form field.
  const handleErase = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const bbox = canvas.getMarqueeBounds();
    if (!bbox) {
      setAiStatus({
        kind: 'error',
        message: 'Erase requires a marquee selection. Draw a selection first.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Erasing selection…' });
    try {
      const ok = await canvas.runSmartDeleteOnActiveLayer(async (source, mask, maskBbox) => {
        const sourceW = source.width;
        const sourceH = source.height;

        // Stability `/edit/erase` — send the FULL source + grayscale
        // mask so the model has surrounding context to fill from.
        // Scaled to fit Stability's per-side bounds (≥64 px, ≤1536 px).
        const fittedSource = fitCanvasToMaxDim(
          source,
          SMART_CREATE_MAX_INPUT_EDGE_PX,
          STABILITY_MIN_INPUT_EDGE_PX,
        );
        const grayscaleMask = toGrayscaleMask(mask);
        const fittedMask = fitCanvasToMaxDim(
          grayscaleMask,
          SMART_CREATE_MAX_INPUT_EDGE_PX,
          STABILITY_MIN_INPUT_EDGE_PX,
        );
        const imageDataUrl = fittedSource.toDataURL('image/png');
        const maskDataUrl = fittedMask.toDataURL('image/png');
        // Result is full-source-sized; draw back over the full canvas.
        const resultPatchRect = { left: 0, top: 0, width: sourceW, height: sourceH };

        const response = await api.smartEraseImage({
          imageDataUrl,
          maskDataUrl,
          ...(eraseGrowMaskPx > 0 && { growMask: eraseGrowMaskPx }),
        });
        const eraseResult = await loadImageToCanvas(response.imageDataUrl);

        // Composite path: feather the mask, build an alpha-clipped
        // "patch" canvas at full source resolution, draw it over the
        // original source. The wrapper's destination-in clip then
        // strips anything outside the original mask so off-marquee
        // pixels stay byte-identical.
        const featherRadius = computeFeatherRadius(
          maskBbox.width,
          maskBbox.height,
          eraseFeatherRatio,
        );
        const featheredMask = featherRadius > 0 ? featherMask(mask, featherRadius) : mask;
        const patch = document.createElement('canvas');
        patch.width = sourceW;
        patch.height = sourceH;
        const patchCtx = patch.getContext('2d');
        if (patchCtx) {
          // Draw the engine result back at its original frame.
          // - Stability path: result is full-source-sized → draw across
          //   the whole canvas (resultPatchRect = 0,0,W,H).
          // - OpenAI path: result is bbox-sized → draw at the bbox.
          patchCtx.drawImage(
            eraseResult,
            0,
            0,
            eraseResult.width,
            eraseResult.height,
            resultPatchRect.left,
            resultPatchRect.top,
            resultPatchRect.width,
            resultPatchRect.height,
          );
          patchCtx.globalCompositeOperation = 'destination-in';
          patchCtx.drawImage(featheredMask, 0, 0);
        }

        const fullResult = document.createElement('canvas');
        fullResult.width = sourceW;
        fullResult.height = sourceH;
        const ctx = fullResult.getContext('2d');
        if (ctx) {
          ctx.drawImage(source, 0, 0);
          ctx.drawImage(patch, 0, 0);
        }
        return fullResult;
      });
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Erase needs a paintable active layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erase failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Erase failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [eraseGrowMaskPx, eraseFeatherRatio, canvas]);

  // Search & Replace. Sends the active layer's full image to Stability
  // alongside two prompts (what to find, what to replace it with);
  // Stability auto-segments and returns the modified full image. No
  // marquee required. Replaces the layer's surface pixels with the
  // result via `runOnActiveLayerFullImage` — no mask composite, no
  // feather (Stability already blends the edges).
  const handleSearchReplace = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const searchPrompt = searchReplaceSearchPrompt.trim();
    const replacePrompt = searchReplacePrompt.trim();
    if (searchPrompt.length === 0 || replacePrompt.length === 0) return;

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Searching and replacing…' });
    try {
      const ok = await canvas.runOnActiveLayerFullImage(async (source) => {
        // Same Stability bounds as the other tools — at least 64 px per
        // side, capped at 1536. The result returns at the input
        // dimensions; the layer-replace helper rescales it back to the
        // surface's native size.
        const fittedSource = fitCanvasToMaxDim(
          source,
          SMART_CREATE_MAX_INPUT_EDGE_PX,
          STABILITY_MIN_INPUT_EDGE_PX,
        );
        const sourceDataUrl = fittedSource.toDataURL('image/png');
        const negativePrompt = searchReplaceNegativePrompt.trim();
        const stylePreset = searchReplaceStylePreset.trim();
        const response = await api.smartSearchReplaceImage({
          imageDataUrl: sourceDataUrl,
          searchPrompt,
          prompt: replacePrompt,
          ...(negativePrompt.length > 0 && { negativePrompt }),
          ...(searchReplaceGrowMaskPx > 0 && { growMask: searchReplaceGrowMaskPx }),
          ...(stylePreset.length > 0 && { stylePreset }),
          ...(searchReplaceSeed !== null && { seed: searchReplaceSeed }),
        });
        return loadImageToCanvas(response.imageDataUrl);
      });
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Search & Replace needs an active paintable layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search & Replace failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Search & Replace failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [
    searchReplaceSearchPrompt,
    searchReplacePrompt,
    searchReplaceNegativePrompt,
    searchReplaceGrowMaskPx,
    searchReplaceStylePreset,
    searchReplaceSeed,
    canvas,
  ]);

  // Search & Recolor — mirrors handleSearchReplace but with selectPrompt
  // (what to recolor) instead of searchPrompt, and prompt describes the
  // new color/effect. Stability returns the full image with the recolor
  // already composited; we swap the active layer's pixels via
  // `runOnActiveLayerFullImage`.
  const handleSearchRecolor = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const selectPrompt = searchRecolorSelectPrompt.trim();
    const colorPrompt = searchRecolorPrompt.trim();
    if (selectPrompt.length === 0 || colorPrompt.length === 0) return;

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Recoloring selection…' });
    try {
      const ok = await canvas.runOnActiveLayerFullImage(async (source) => {
        const fittedSource = fitCanvasToMaxDim(
          source,
          SMART_CREATE_MAX_INPUT_EDGE_PX,
          STABILITY_MIN_INPUT_EDGE_PX,
        );
        const sourceDataUrl = fittedSource.toDataURL('image/png');
        const negativePrompt = searchRecolorNegativePrompt.trim();
        const stylePreset = searchRecolorStylePreset.trim();
        const response = await api.smartSearchRecolorImage({
          imageDataUrl: sourceDataUrl,
          selectPrompt,
          prompt: colorPrompt,
          ...(negativePrompt.length > 0 && { negativePrompt }),
          ...(searchRecolorGrowMaskPx > 0 && { growMask: searchRecolorGrowMaskPx }),
          ...(stylePreset.length > 0 && { stylePreset }),
          ...(searchRecolorSeed !== null && { seed: searchRecolorSeed }),
        });
        return loadImageToCanvas(response.imageDataUrl);
      });
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Search & Recolor needs an active paintable layer.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search & Recolor failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Search & Recolor failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [
    searchRecolorSelectPrompt,
    searchRecolorPrompt,
    searchRecolorNegativePrompt,
    searchRecolorGrowMaskPx,
    searchRecolorStylePreset,
    searchRecolorSeed,
    canvas,
  ]);

  // Outpaint — extends the background image by leftPx/rightPx/upPx/downPx
  // pixels. Stability returns a single image at the new (extended)
  // dimensions; the canvas helper grows the bg surface and shifts non-bg
  // overlay objects so original content stays visually anchored.
  const handleOutpaint = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const leftPx = Math.max(0, Math.floor(outpaintLeftPx));
    const rightPx = Math.max(0, Math.floor(outpaintRightPx));
    const upPx = Math.max(0, Math.floor(outpaintUpPx));
    const downPx = Math.max(0, Math.floor(outpaintDownPx));
    if (leftPx + rightPx + upPx + downPx === 0) return;

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Outpainting…' });
    // Defense-in-depth: even after the source-downsampling fix, a
    // pathologically slow Stability queue could still leave the user
    // staring at "Outpainting…" indefinitely. A 60 s client-side abort
    // bounds the worst-case wait — past that, surface a friendly
    // timeout and free the in-flight lock so the user can retry.
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 60_000);
    try {
      const ok = await canvas.runOutpaintOnBackground(
        { left: leftPx, right: rightPx, up: upPx, down: downPx },
        async (source, ext) => {
          // Downsample the source before upload, exactly like Search &
          // Replace / Recolor. Without this, a 4032×3024 phone photo
          // produces a ~12 MB PNG, blows past the 30 s fetch timeout,
          // and (worst case) makes Stability queue the job async — at
          // which point `pollForResult` waits up to 3 min before
          // timing out, hence the "stuck for several minutes" report.
          //
          // The extensions are pixel values relative to the un-scaled
          // source, so they must be scaled by the SAME factor for
          // Stability to add proportionally the right strip width.
          // The canvas helper later scales Stability's response back
          // up to full resolution via its drawImage stretch.
          const fittedSource = fitCanvasToMaxDim(
            source,
            SMART_CREATE_MAX_INPUT_EDGE_PX,
            STABILITY_MIN_INPUT_EDGE_PX,
          );
          const scale = fittedSource.width / source.width;
          const scaledLeft = Math.round(ext.left * scale);
          const scaledRight = Math.round(ext.right * scale);
          const scaledUp = Math.round(ext.up * scale);
          const scaledDown = Math.round(ext.down * scale);

          const sourceDataUrl = fittedSource.toDataURL('image/png');
          const promptText = outpaintPrompt.trim();
          const negativePrompt = outpaintNegativePrompt.trim();
          const stylePreset = outpaintStylePreset.trim();
          const response = await api.smartOutpaintImage(
            {
              imageDataUrl: sourceDataUrl,
              ...(scaledLeft > 0 && { left: scaledLeft }),
              ...(scaledRight > 0 && { right: scaledRight }),
              ...(scaledUp > 0 && { up: scaledUp }),
              ...(scaledDown > 0 && { down: scaledDown }),
              ...(promptText.length > 0 && { prompt: promptText }),
              creativity: outpaintCreativity,
              ...(negativePrompt.length > 0 && { negativePrompt }),
              ...(stylePreset.length > 0 && { stylePreset }),
              ...(outpaintSeed !== null && { seed: outpaintSeed }),
            },
            { signal: abortController.signal },
          );
          return loadImageToCanvas(response.imageDataUrl);
        },
      );
      if (!ok) {
        setAiStatus({
          kind: 'error',
          message: 'Outpaint needs a background image and at least one edge > 0.',
        });
        return;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      // An AbortController-driven abort surfaces as a DOMException with
      // name 'AbortError' (or, depending on the path, an ApiError whose
      // cause is an AbortError). Translate either into a friendlier
      // timeout message — the raw "The user aborted a request." string
      // is misleading because the user did NOT cancel; the timer did.
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Outpaint timed out after 60 seconds. Stability may be busy — please try again.'
        : err instanceof Error
          ? err.message
          : 'Outpaint failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Outpaint failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    outpaintLeftPx,
    outpaintRightPx,
    outpaintUpPx,
    outpaintDownPx,
    outpaintPrompt,
    outpaintCreativity,
    outpaintNegativePrompt,
    outpaintStylePreset,
    outpaintSeed,
    canvas,
  ]);

  // Image to Image — sends the active layer as a reference image plus a
  // prompt; Stability redraws guided by both, with `strength` controlling
  // how far to deviate from the source. Same `runOnActiveLayerFullImage`
  // path as Search & Replace — the result lands at the source's aspect
  // ratio, helper scales it back into the surface dims.
  const handleImageToImage = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const promptText = i2iPrompt.trim();
    if (promptText.length === 0) return;

    // Snapshot the active layer before kicking off the request — we use
    // its scene bounds to drop the new layer at the same location, so
    // the user gets a non-destructive overlay they can move/hide rather
    // than a baked-in replacement.
    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Image to Image needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Reimagining…' });
    // Same 60 s defense-in-depth abort as Outpaint — caps the worst-case
    // wait if Stability queues the job.
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 60_000);
    try {
      const fittedSource = fitCanvasToMaxDim(
        snapshot.source,
        SMART_CREATE_MAX_INPUT_EDGE_PX,
        STABILITY_MIN_INPUT_EDGE_PX,
      );
      const sourceDataUrl = fittedSource.toDataURL('image/png');
      const negativePrompt = i2iNegativePrompt.trim();
      const stylePreset = i2iStylePreset.trim();
      const response = await api.smartImageToImageImage(
        {
          imageDataUrl: sourceDataUrl,
          prompt: promptText,
          engine: i2iEngine,
          strength: i2iStrength,
          ...(negativePrompt.length > 0 && { negativePrompt }),
          ...(stylePreset.length > 0 && { stylePreset }),
          ...(i2iSeed !== null && { seed: i2iSeed }),
        },
        { signal: abortController.signal },
      );
      if (i2iMode === 'replace') {
        // Bake the redraw into the active layer's pixels — same path as
        // Search & Replace / Search & Recolor. The helper scales the
        // result back into the surface's native resolution.
        const ok = await canvas.runOnActiveLayerFullImage(async () =>
          loadImageToCanvas(response.imageDataUrl),
        );
        if (!ok) {
          setAiStatus({
            kind: 'error',
            message: 'Image to Image needs an active paintable layer.',
          });
          return;
        }
      } else {
        // Drop the redraw on top as an editable layer aligned to the
        // source's scene rect — same pattern as Smart Create's Insert
        // mode. Non-destructive: the source layer stays untouched.
        const resultResponse = await fetch(response.imageDataUrl);
        const resultBlob = await resultResponse.blob();
        const layer = makeLayer('image', 'Reimagined');
        addLayer(layer, true);
        await canvas.addImageLayer(resultBlob, {
          layerId: layer.id,
          fitTo: snapshot.bounds,
        });
        // Snapshot is captured by the pending-flag effect on the next commit.
        layerSnapshotPendingRef.current = true;
        layerSnapshotLabelRef.current = 'Image to Image';
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Image to Image timed out after 60 seconds. Stability may be busy — please try again.'
        : err instanceof Error
          ? err.message
          : 'Image to Image failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Image to Image failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    i2iPrompt,
    i2iEngine,
    i2iStrength,
    i2iNegativePrompt,
    i2iStylePreset,
    i2iSeed,
    i2iMode,
    canvas,
    makeLayer,
    addLayer,
  ]);

  // Replace Background — sends the full active layer (engines segment
  // internally) plus a prompt and/or reference image. Stability also
  // accepts light direction / strength / reference for relighting and
  // preserve-subject / preserve-old-bg sliders. Mode toggle decides
  // whether to bake into the active layer or drop on top as a new
  // editable layer (default).
  const handleReplaceBackground = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const promptText = replaceBgPrompt.trim();
    // Stability allows prompt OR reference; Recraft requires prompt.
    if (
      replaceBgEngine === 'recraft'
        ? promptText.length === 0
        : promptText.length === 0 && replaceBgReferenceBlob === null
    ) {
      return;
    }

    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Replace Background needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Replacing background…' });
    // Generous abort — Stability's relight model can run 10-20 s under load.
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 90_000);
    try {
      const fittedSource = fitCanvasToMaxDim(
        snapshot.source,
        SMART_CREATE_MAX_INPUT_EDGE_PX,
        STABILITY_MIN_INPUT_EDGE_PX,
      );
      const sourceDataUrl = fittedSource.toDataURL('image/png');
      // Convert blobs to data URLs so they fit the JSON payload shape.
      const blobToDataUrl = (blob: Blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
      const backgroundReferenceDataUrl = replaceBgReferenceBlob
        ? await blobToDataUrl(replaceBgReferenceBlob)
        : undefined;
      const lightReferenceDataUrl = replaceBgLightReferenceBlob
        ? await blobToDataUrl(replaceBgLightReferenceBlob)
        : undefined;

      const response = await api.smartReplaceBackgroundImage(
        {
          imageDataUrl: sourceDataUrl,
          engine: replaceBgEngine,
          ...(promptText.length > 0 && { backgroundPrompt: promptText }),
          ...(replaceBgEngine === 'stability' && {
            ...(backgroundReferenceDataUrl && { backgroundReferenceDataUrl }),
            ...(lightReferenceDataUrl && { lightReferenceDataUrl }),
            // 'none' is the editor's UI sentinel — drop the field
            // entirely so Stability doesn't bias the relight.
            ...(replaceBgLightDirection !== 'none' && {
              lightDirection: replaceBgLightDirection,
              lightStrength: replaceBgLightStrength,
            }),
            preserveSubject: replaceBgPreserveSubject,
            preserveOriginalBg: replaceBgPreserveOriginalBg,
          }),
          ...(replaceBgEngine === 'recraft' && replaceBgStyle && { style: replaceBgStyle }),
          ...(replaceBgSeed !== null && { seed: replaceBgSeed }),
        },
        { signal: abortController.signal },
      );

      if (replaceBgMode === 'replace') {
        const ok = await canvas.runOnActiveLayerFullImage(async () =>
          loadImageToCanvas(response.imageDataUrl),
        );
        if (!ok) {
          setAiStatus({
            kind: 'error',
            message: 'Replace Background needs an active paintable layer.',
          });
          return;
        }
      } else {
        const resultResponse = await fetch(response.imageDataUrl);
        const resultBlob = await resultResponse.blob();
        const layer = makeLayer('image', 'New Background');
        addLayer(layer, true);
        await canvas.addImageLayer(resultBlob, {
          layerId: layer.id,
          fitTo: snapshot.bounds,
        });
        layerSnapshotPendingRef.current = true;
        layerSnapshotLabelRef.current = 'Replace Background';
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      // Stability's safety classifier on `replace-background-and-relight`
      // is more aggressive than its other endpoints and refuses requests
      // it deems suggestive — even on subjects that aren't. The provider
      // tags those failures with `CONTENT_FILTERED`; we translate that
      // into a user-friendly message that steers toward Recraft (which
      // doesn't run Stability's filter) instead of a cryptic API error.
      // Credit was refunded automatically by the gateway — surfacing
      // that here so the user doesn't worry about the charge.
      const isContentFiltered = err instanceof Error && /CONTENT_FILTERED/i.test(err.message);
      const message = aborted
        ? 'Replace Background timed out after 90 seconds. Try again — Stability may be busy.'
        : isContentFiltered
          ? 'Stability’s safety filter blocked this request — try the Recraft engine, or adjust the prompt / source image. No credits were charged.'
          : err instanceof Error
            ? err.message
            : 'Replace Background failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Replace Background failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    replaceBgPrompt,
    replaceBgEngine,
    replaceBgReferenceBlob,
    replaceBgLightReferenceBlob,
    replaceBgLightDirection,
    replaceBgLightStrength,
    replaceBgPreserveSubject,
    replaceBgPreserveOriginalBg,
    replaceBgStyle,
    replaceBgSeed,
    replaceBgMode,
    canvas,
    makeLayer,
    addLayer,
  ]);

  // Upscale handler — three modes map to five entities behind the
  // server-side dispatch. For Stability HQ + reimagine the prompt is
  // required; the panel disables the action button until it's filled.
  // 120 s abort budget — heavier than I2I (60 s) and Replace BG (90 s)
  // because Stability's async upscales can sit in the queue for a
  // while under load. The output mode toggle decides whether we bake
  // into the active layer or drop a new layer aligned to the source.
  const handleUpscale = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const promptText = upscalePrompt.trim();
    const acceptsPrompt =
      upscaleEngine === 'stability' &&
      (upscaleMode === 'high-quality' || upscaleMode === 'reimagine');
    // High-quality forces Stability server-side regardless of the
    // local engine state, so we re-derive the effective engine here.
    const effectiveEngine: UpscaleEngine =
      upscaleMode === 'high-quality' ? 'stability' : upscaleEngine;
    const effectiveAcceptsPrompt =
      effectiveEngine === 'stability' &&
      (upscaleMode === 'high-quality' || upscaleMode === 'reimagine');
    if (effectiveAcceptsPrompt && promptText.length === 0) return;
    // Silence the unused warning when the local-state effectiveEngine
    // and promptText branches diverge — we need both for the gating
    // above. The variable usage below makes this explicit.
    void acceptsPrompt;

    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Upscale needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Upscaling…' });
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 120_000);
    try {
      // No fitCanvasToMaxDim here — upscale's whole point is to enlarge
      // a small input. Down-fitting first would defeat the operation.
      // The provider-side endpoints accept inputs up to ~4 MP without
      // complaint; users with sources beyond that probably don't need
      // an upscale anyway.
      //
      // Flatten transparent edges before sending. Stability and Recraft
      // don't preserve alpha — Stability composites on black, Recraft
      // on white — so a transparent-edge source comes back framed by a
      // provider-specific border. Trim + flatten gives the upscaler a
      // pure-content input and the user gets back a clean upscaled
      // image at the actual content's aspect ratio. Identity-equal
      // return when no flatten was needed (opaque source) — no extra
      // allocation in the common case.
      const flattened = flattenForExport(snapshot.source);
      const sourceDataUrl = flattened.toDataURL('image/png');
      const response = await api.smartUpscaleImage(
        {
          imageDataUrl: sourceDataUrl,
          mode: upscaleMode,
          engine: effectiveEngine,
          ...(effectiveAcceptsPrompt && { prompt: promptText }),
          ...(effectiveAcceptsPrompt && { creativity: upscaleCreativity }),
          // Factor only matters for Recraft engines — Stability rejects
          // unknown fields, so we gate the include.
          ...(effectiveEngine === 'recraft' && { factor: upscaleFactor }),
          ...(effectiveAcceptsPrompt && upscaleSeed !== null && { seed: upscaleSeed }),
        },
        { signal: abortController.signal },
      );

      if (upscaleOutputMode === 'replace') {
        // `runUpscaleOnActiveLayer` replaces the layer's FabricImage
        // with one wrapping the upscaled canvas at scale 1, so the
        // larger pixel grid translates to a larger displayed image.
        // For the BG layer it also grows the document dims and
        // re-fits the viewport. Critically NOT `runOnActiveLayerFullImage`,
        // which would scale the result back down into the source dims
        // (the original bug — image stayed visually the same size and
        // the editor's dark background bled around the canvas edges).
        const ok = await canvas.runUpscaleOnActiveLayer(async () =>
          loadImageToCanvas(response.imageDataUrl),
        );
        if (!ok) {
          setAiStatus({
            kind: 'error',
            message: 'Upscale needs an active paintable layer.',
          });
          return;
        }
      } else {
        // Drop the result as a new layer at its NATURAL size — no
        // `fitTo`, since fitTo would scale the upscaled image back
        // down to the source's scene rect (defeating the upscale).
        // `addImageLayer` without fitTo centers the new layer on the
        // photo dims, so visually the upscaled layer overlays the
        // source centered, larger. The viewport re-fit below makes
        // sure the user sees the whole thing.
        const resultResponse = await fetch(response.imageDataUrl);
        const resultBlob = await resultResponse.blob();
        const layer = makeLayer('image', 'Upscaled');
        addLayer(layer, true);
        await canvas.addImageLayer(resultBlob, { layerId: layer.id });
        // Re-fit so the user can actually see the larger result. Without
        // this the new layer extends past the visible viewport and the
        // user thinks "nothing happened" (or "weird black border").
        const fitZoom = canvas.fitToContainer();
        if (fitZoom !== null && fitZoom !== undefined) setZoom(fitZoom);
        layerSnapshotPendingRef.current = true;
        layerSnapshotLabelRef.current = 'Upscale';
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Upscale timed out after 120 seconds. Try again — Stability may be busy.'
        : err instanceof Error
          ? err.message
          : 'Upscale failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Upscale failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    upscaleMode,
    upscaleEngine,
    upscalePrompt,
    upscaleCreativity,
    upscaleFactor,
    upscaleSeed,
    upscaleOutputMode,
    canvas,
    makeLayer,
    addLayer,
  ]);

  // Vectorize is treated as an EXPORT op rather than an edit — the
  // editor canvas is left untouched and the result lands either as a
  // local download or a Fias library file via the in-editor
  // VectorizeSaveDialog. Same Local/Fias destination toggle as the
  // toolbar's regular Save As, so the two flows feel consistent.
  //
  // Flow:
  //   1. Click Vectorize → API runs (status banner shows progress)
  //   2. SVG returns → stash in ref, open VectorizeSaveDialog
  //   3. User picks filename + destination + clicks Save
  //   4. Save handler routes to anchor-download (local) or the 3-step
  //      Fias upload (fias).
  //   5. If user cancels the dialog, the SVG stays in the ref so the
  //      panel's "Save again" button can re-open the dialog without
  //      re-running the API.
  const handleVectorize = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Vectorize needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: 'Vectorizing…' });
    setVectorizeStatus('idle');
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 60_000);
    try {
      // Trim transparent edges before sending — same flatten pass
      // upscale uses. The vectorizer doesn't have the per-provider
      // border bug (Recraft's vectorize ignores transparent regions
      // cleanly in its output paths), but trimming saves credits
      // indirectly: fewer paths to compute around empty space.
      const flattened = flattenForExport(snapshot.source);
      const sourceDataUrl = flattened.toDataURL('image/png');
      const response = await api.smartVectorizeImage(
        { imageDataUrl: sourceDataUrl },
        { signal: abortController.signal },
      );
      vectorizeSvgRef.current = response.svg;
      setAiStatus({ kind: 'idle' });
      // Hand off to the in-editor save dialog. The user picks
      // filename + destination there; the dialog's onSave callback
      // (handleVectorizeSave) routes to the right backend.
      setVectorizeSaveDialogOpen(true);
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Vectorize timed out after 60 seconds. Try again — Recraft may be busy.'
        : err instanceof Error
          ? err.message
          : 'Vectorize failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Vectorize failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [canvas]);

  // Save again: re-open the dialog with the SVG that's still in the
  // ref. No new API call → no additional credit charge. Lets the user
  // save the same vector to a different location, switch destinations,
  // or recover from accidentally hitting Cancel on the first dialog.
  const handleSaveVectorizeAgain = useCallback(() => {
    if (!vectorizeSvgRef.current) return;
    setVectorizeSaveDialogOpen(true);
  }, [vectorizeSvgRef]);

  // Dialog → Save click. Routes to the right backend based on the
  // user's destination pick.
  const handleVectorizeSave = useCallback(
    async (fileName: string, destination: VectorizeSaveDestination) => {
      const svg = vectorizeSvgRef.current;
      if (!svg) return;
      // Persist the destination so a "Save again" pre-selects the
      // user's last pick. Matches the ExportDialog's UX.
      setVectorizeSaveDialogDestination(destination);
      const outcome =
        destination === 'fias'
          ? await uploadSvgToFias(svg, fileName)
          : await downloadSvgLocal(svg, fileName);
      if (outcome === 'saved') {
        setVectorizeStatus('saved');
        setVectorizeSavedFileName(/\.svg$/i.test(fileName) ? fileName : `${fileName}.svg`);
      } else {
        setAiStatus({
          kind: 'error',
          message:
            destination === 'fias'
              ? 'Failed to upload SVG to Fias files.'
              : 'Failed to save SVG locally.',
        });
        setVectorizeStatus('cancelled');
      }
    },
    [vectorizeSvgRef],
  );

  const handleVectorizeSaveDialogClose = useCallback(() => {
    setVectorizeSaveDialogOpen(false);
    // If the dialog closes without a save (Cancel), don't clear the
    // SVG — the panel's Save again button needs it. The 'saved'
    // state was already set in handleVectorizeSave on success.
    if (vectorizeStatus === 'idle') setVectorizeStatus('cancelled');
  }, [vectorizeStatus]);

  // Control / Guided — four Stability v2beta endpoints behind a
  // single mode-first tool. Each mode has different required inputs
  // (prompt for first three, style reference image for style-transfer)
  // and different optional knobs. The handler validates locally,
  // calls the route, then either bakes into the active layer or
  // drops a new layer aligned to the source — same Replace / New
  // Layer pattern as I2I / Replace BG / Upscale.
  const handleControl = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const promptText = controlPrompt.trim();
    const acceptsPrompt = controlMode !== 'style-transfer';
    if (acceptsPrompt && promptText.length === 0) return;
    if (controlMode === 'style-transfer' && !controlStyleReferenceBlob) return;

    const snapshot = canvas.getActiveLayerSnapshot();
    if (!snapshot) {
      setAiStatus({
        kind: 'error',
        message: 'Control needs an active paintable layer.',
      });
      return;
    }

    aiInFlightRef.current = true;
    setAiStatus({ kind: 'running', label: `${CONTROL_RUNNING_LABEL[controlMode]}…` });
    // 90 s abort matches Replace BG — Stability's control endpoints
    // are similar weight (single API call, not async polling) so
    // they don't need Upscale's 120 s window.
    const abortController = new AbortController();
    const abortTimer = window.setTimeout(() => abortController.abort(), 90_000);
    try {
      // Same flatten pre-process used by Upscale + Vectorize. None of
      // the control endpoints preserve alpha, so transparent edges
      // would composite onto Stability's default black and produce
      // the same border bug we hit with Replace Background.
      const flattened = flattenForExport(snapshot.source);
      const sourceDataUrl = flattened.toDataURL('image/png');
      // Convert the style reference (style-transfer only) to a data URL.
      const blobToDataUrl = (blob: Blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
      const styleImageDataUrl =
        controlMode === 'style-transfer' && controlStyleReferenceBlob
          ? await blobToDataUrl(controlStyleReferenceBlob)
          : undefined;

      const response = await api.smartControlImage(
        {
          imageDataUrl: sourceDataUrl,
          mode: controlMode,
          ...(acceptsPrompt && { prompt: promptText }),
          ...(controlNegativePrompt.trim().length > 0 && {
            negativePrompt: controlNegativePrompt.trim(),
          }),
          // Per-mode knobs: only forward what the endpoint actually
          // accepts. The route would drop unknowns silently, but
          // sending less is cleaner.
          ...((controlMode === 'from-sketch' || controlMode === 'match-structure') && {
            controlStrength,
          }),
          ...(controlMode === 'match-style' && { fidelity: controlFidelity }),
          ...(controlMode === 'style-transfer' && {
            styleImageDataUrl,
            styleStrength: controlStyleStrength,
            compositionFidelity: controlCompositionFidelity,
            changeStrength: controlChangeStrength,
          }),
          ...(controlSeed !== null && { seed: controlSeed }),
        },
        { signal: abortController.signal },
      );

      if (controlOutputMode === 'replace') {
        const ok = await canvas.runOnActiveLayerFullImage(async () =>
          loadImageToCanvas(response.imageDataUrl),
        );
        if (!ok) {
          setAiStatus({
            kind: 'error',
            message: 'Control needs an active paintable layer.',
          });
          return;
        }
      } else {
        const resultResponse = await fetch(response.imageDataUrl);
        const resultBlob = await resultResponse.blob();
        const layer = makeLayer('image', CONTROL_LAYER_LABEL[controlMode]);
        addLayer(layer, true);
        await canvas.addImageLayer(resultBlob, {
          layerId: layer.id,
          fitTo: snapshot.bounds,
        });
        layerSnapshotPendingRef.current = true;
        layerSnapshotLabelRef.current = `Control: ${CONTROL_LAYER_LABEL[controlMode]}`;
      }
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const aborted =
        abortController.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /aborted/i.test(err.message));
      const message = aborted
        ? 'Control timed out after 90 seconds. Stability may be busy — please try again.'
        : err instanceof Error
          ? err.message
          : 'Control failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Control failed:', err);
    } finally {
      window.clearTimeout(abortTimer);
      aiInFlightRef.current = false;
    }
  }, [
    controlMode,
    controlPrompt,
    controlNegativePrompt,
    controlStrength,
    controlFidelity,
    controlStyleReferenceBlob,
    controlStyleStrength,
    controlCompositionFidelity,
    controlChangeStrength,
    controlSeed,
    controlOutputMode,
    canvas,
    makeLayer,
    addLayer,
  ]);

  const handleSmartCreate = useCallback(async () => {
    if (aiInFlightRef.current) return;
    const prompt = smartCreatePrompt.trim();
    if (prompt.length === 0) return;

    const bbox = canvas.getMarqueeBounds();
    aiInFlightRef.current = true;

    // Smart Create is text-to-image only now (the previous "Blend"
    // mode lives under the Inpaint picker entry).
    // Generate the subject as text-to-image (cheap, fast — BFL FLUX
    // 2 Pro), then run remove-background locally on the result so
    // we can drop just the subject onto a fresh layer with no
    // surrounding photo bleeding through. The user controls
    // composition by moving / scaling the layer afterward, so we
    // don't need any pixel-level blending — clean alpha edges from
    // the cutout do the job.
    //
    // The prompt suffix nudges the model toward a clean isolated
    // subject (otherwise it sometimes generates a scene with the
    // subject in it, which the bg-removal step then mangles).
    // Map the marquee's aspect to the closest universally-supported
    // ratio. The 5-bucket set is the intersection of what every wired
    // image provider's resolver accepts natively (BFL, OpenAI, Google
    // Imagen / Nano Banana, Recraft, Stability). Thresholds are at
    // the geometric mean of adjacent ratios so each marquee aspect
    // lands in its closest bucket.
    const aspect = bbox ? bbox.width / bbox.height : 1;
    const size: '1:1' | '4:3' | '3:4' | '16:9' | '9:16' =
      aspect >= 1.55
        ? '16:9'
        : aspect >= 1.155
          ? '4:3'
          : aspect >= 0.866
            ? '1:1'
            : aspect >= 0.645
              ? '3:4'
              : '9:16';
    const decoratedPrompt = `${prompt}. Plain white background, isolated subject, fully visible, centered, no other objects.`;

    setAiStatus({ kind: 'running', label: 'Generating image…' });
    try {
      // Scope the response data URL to a block so it can be GC'd as
      // soon as we have the Blob — a 1536² PNG is ~2-3 MB and we
      // don't want the base64 string pinned alongside the Blob copy
      // and the eventual FabricImage all the way through bg-removal.
      let generatedBlob: Blob;
      {
        const { imageDataUrl } = await api.smartCreateImage({
          prompt: decoratedPrompt,
          size,
          model: smartCreateInsertModel,
        });
        generatedBlob = await (await fetch(imageDataUrl)).blob();
      }

      setAiStatus({ kind: 'running', label: 'Removing background…' });
      const cutoutBlob = await removeBackgroundViaEntity(generatedBlob);

      // Add as a new editable layer. The hook handles placement +
      // scale + render in one shot, so there's no chance of the
      // image landing in the wrong place because a follow-up render
      // didn't fire.
      const layer = makeLayer('image', 'AI Image');
      addLayer(layer, true);
      await canvas.addImageLayer(cutoutBlob, {
        layerId: layer.id,
        fitTo: bbox ?? undefined,
      });
      // Snapshot is captured by the pending-flag effect on the next commit.
      layerSnapshotPendingRef.current = true;
      layerSnapshotLabelRef.current = 'Smart Create';
      setAiStatus({ kind: 'idle' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Smart Create failed.';
      setAiStatus({ kind: 'error', message });
      console.warn('[image-editor] Smart Create (insert) failed:', err);
    } finally {
      aiInFlightRef.current = false;
    }
  }, [
    smartCreatePrompt,
    smartCreateInsertModel,
    canvas,
    makeLayer,
    addLayer,
    removeBackgroundViaEntity,
  ]);

  return (
    <div className="flex flex-col h-screen bg-background">
      <EditorToolbar
        registry={commandRegistryRef.current}
        dispatch={dispatchCommand}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
        hasActiveFile={tabs.activeTabId !== null}
        canCutCopy={canvas.hasMarqueeSelection}
        canPaste={canvas.hasClipboard || SUPPORTS_OS_CLIPBOARD_READ}
        transparencyCheckerVisible={transparencyCheckerVisible}
        viewSettings={state.viewSettings}
        isMac={isMac}
      />

      {tabs.tabs.length > 0 && (
        <EditorTabBar
          tabs={tabs.tabs}
          activeTabId={tabs.activeTabId}
          onSwitch={handleTabSwitch}
          onClose={handleTabClose}
          onNewTab={handleNewTab}
        />
      )}

      {alignSelectionIds.length >= 2 && (
        <AlignPanel
          selectedCount={alignSelectionIds.length}
          onAlign={handleAlign}
          onDistribute={handleDistribute}
        />
      )}

      <div className={`flex flex-1 overflow-hidden ${isLandscape ? 'flex-row' : 'flex-col'}`}>
        <div className="flex flex-row flex-1 overflow-hidden min-w-0 min-h-0">
          <ToolPalette
            activeTool={state.activeTool}
            onToolSelect={handleToolSelect}
            registry={commandRegistryRef.current}
            isMac={isMac}
          />

          <CanvasArea
            canvasElRef={canvasElRef}
            wrapperRef={wrapperRef}
            onFileDropped={dispatchOpenedFile}
            onFileSelected={dispatchOpenedFile}
            onOpenFias={handleOpenFias}
            hasImage={hasImage}
            transparencyCheckerVisible={transparencyCheckerVisible}
            viewport={canvas.viewport}
            overlays={
              hasImage ? (
                <>
                  {state.viewSettings.gridVisible && (
                    <Grid
                      viewport={canvas.viewport}
                      config={state.viewSettings.gridConfig}
                      rulersVisible={state.viewSettings.rulersVisible}
                    />
                  )}
                  {state.viewSettings.guidesVisible && (
                    <Guides
                      viewport={canvas.viewport}
                      guides={state.viewSettings.guides}
                      rulersVisible={state.viewSettings.rulersVisible}
                      lockGuides={state.viewSettings.lockGuides}
                      onMoveGuide={moveGuide}
                      onRemoveGuide={removeGuide}
                      subscribeToSnapMatch={canvas.subscribeToSnapMatch}
                    />
                  )}
                  <SmartGuides
                    viewport={canvas.viewport}
                    rulersVisible={state.viewSettings.rulersVisible}
                    subscribeToSmartSnapMatch={canvas.subscribeToSmartSnapMatch}
                  />
                  {state.viewSettings.rulersVisible && (
                    <Rulers
                      viewport={canvas.viewport}
                      subscribeToCursorMove={canvas.subscribeToCursorMove}
                      onSpawnGuide={addGuide}
                    />
                  )}
                </>
              ) : null
            }
            brushCursor={
              hasImage && (state.activeTool === 'draw' || state.activeTool === 'eraser')
                ? {
                    // Brush width is in canvas pixels; the screen footprint
                    // scales with zoom so the preview matches what the user
                    // will actually paint.
                    sizePx: Math.max(state.brushSettings.width * state.zoom, 4),
                    color: state.activeTool === 'eraser' ? '#ffffff' : state.brushSettings.color,
                  }
                : hasImage && state.activeTool === 'retouch'
                  ? {
                      // Retouch variants paint via per-cursor stamps sized
                      // by `retouchSettings.size`. Showing the footprint as
                      // a circle with a center `+` makes Clone's
                      // destination cursor visually pair with the source
                      // reticle (also a `+`) — the user sees source ↔
                      // destination as one gesture.
                      sizePx: Math.max(state.retouchSettings.size * state.zoom, 4),
                      color: '#0078d7',
                      withCenterCross: true,
                    }
                  : null
            }
            sourceSetMode={cloneSourceSetMode}
          />
        </div>

        <div
          className={
            isLandscape
              ? 'flex flex-col h-full w-[320px] max-w-[40vw] shrink-0'
              : 'flex flex-col w-full'
          }
        >
          <PropertiesPanel
            isOpen={state.propertiesPanelOpen}
            placement={isLandscape ? 'right' : 'bottom'}
            activeTool={state.activeTool}
            brushSettings={state.brushSettings}
            textSettings={state.textSettings}
            shapeSettings={state.shapeSettings}
            marqueeSettings={state.marqueeSettings}
            retouchSettings={state.retouchSettings}
            penSettings={state.penSettings}
            cloneSourcePoint={cloneSourcePoint}
            cloneArmed={cloneArmed}
            onSetCloneSource={handleSetCloneSourceFromPanel}
            onClearCloneSource={handleClearCloneSource}
            onUpdateBrush={updateBrushSettings}
            onUpdateText={updateTextSettings}
            textPathBoundInfo={textPathBoundInfo}
            textPathBindCandidate={textPathBindCandidate}
            isActiveTextBoundToPath={isActiveTextBoundToPath}
            onBindTextToPath={handleBindTextToPath}
            onUnbindTextFromPath={handleUnbindTextFromPath}
            onUpdateShape={updateShapeSettings}
            onUpdateMarquee={updateMarqueeSettings}
            onUpdateRetouch={updateRetouchSettings}
            onUpdatePen={updatePenSettings}
            penEditPathAvailable={
              state.activeLayerId !== null &&
              state.document.layers[state.activeLayerId]?.type === 'vector'
            }
            onBeginPathEditActive={
              state.activeLayerId
                ? () => handleBeginPathEdit(state.activeLayerId as string)
                : undefined
            }
            pathEditActive={state.pathEditActive}
            pathEditSelectedAnchorIndex={state.pathEditSelectedAnchorIndex}
            pathEditSelectedAnchorType={pathEditSelectedAnchorType}
            onPathEditAnchorTypeChange={handlePathEditAnchorType}
            onApplyPathEdit={handleApplyPathEdit}
            onCancelPathEdit={handleCancelPathEdit}
            eligibleVectorLayerCount={eligibleVectorLayerCount}
            onVectorBooleanOp={handleVectorBooleanOp}
            onOpenAdjustment={handleOpenAdjustment}
            onOpenFilter={handleOpenAdjustment}
            canAdjustActiveLayer={canAdjustActiveLayer}
            autoSelectLayers={state.autoSelectLayers}
            onAutoSelectLayersChange={setAutoSelectLayers}
            onRotateLeft={() => withHistoryLabel('Rotate left 90°', () => canvas.rotateCanvas(-90))}
            onRotateRight={() =>
              withHistoryLabel('Rotate right 90°', () => canvas.rotateCanvas(90))
            }
            onFlipHorizontal={() =>
              withHistoryLabel('Flip horizontal', () => canvas.flipCanvas('horizontal'))
            }
            onFlipVertical={() =>
              withHistoryLabel('Flip vertical', () => canvas.flipCanvas('vertical'))
            }
            hasMarqueeSelection={canvas.hasMarqueeSelection}
            canGrowMarquee={canvas.canGrowMarquee()}
            onGrowMarqueeSelection={(deltaPx) => canvas.growMarqueeBy(deltaPx)}
            onInvertMarqueeSelection={handleInvertSelection}
            onSelectSubject={handleSelectSubject}
            selectSubjectBusy={
              aiStatus.kind === 'running' && aiStatus.label === 'Selecting subject…'
            }
            quickMaskActive={state.quickMaskActive}
            onToggleQuickMask={handleToggleQuickMask}
            onOpenColorRange={() => {
              setSourceCanvas(canvas.getSourceCanvasForSampling());
              setColorRangeOpen(true);
            }}
            onOpenRefineEdge={() => {
              setSourceCanvas(canvas.getSourceCanvasForSampling());
              setRefineEdgeOpen(true);
            }}
            onActivateSlices={() => handleToolSelect('slice')}
            quickMaskMode={state.quickMaskMode}
            onChangeQuickMaskMode={setQuickMaskMode}
            onApplyQuickMask={handleApplyQuickMask}
            onCancelQuickMask={handleCancelQuickMask}
            canApplyQuickMaskAsLayerMask={state.activeLayerId != null}
            transformActive={state.transformActive}
            transformMode={state.transformMode}
            onChangeTransformMode={handleSetTransformMode}
            getTransformMatrix={canvas.getTransformMatrix}
            onChangeTransformMatrix={canvas.setTransformMatrix}
            onApplyTransform={handleApplyTransform}
            onCancelTransform={handleCancelTransform}
            onApplyWarpPreset={canvas.applyWarpPresetToOverlay}
            onBeginFreeTransform={
              state.activeLayerId
                ? () => handleBeginTransform(state.activeLayerId as string)
                : undefined
            }
            smartCreatePrompt={smartCreatePrompt}
            onSmartCreatePromptChange={setSmartCreatePrompt}
            smartCreateInsertModel={smartCreateInsertModel}
            onSmartCreateInsertModelChange={setSmartCreateInsertModel}
            aiStatus={aiStatus}
            onSmartDelete={() => void handleSmartDelete()}
            onSmartCreate={handleSmartCreate}
            inpaintPrompt={inpaintPrompt}
            onInpaintPromptChange={setInpaintPrompt}
            inpaintNegativePrompt={inpaintNegativePrompt}
            onInpaintNegativePromptChange={setInpaintNegativePrompt}
            inpaintEngine={inpaintEngine}
            onInpaintEngineChange={handleInpaintEngineChange}
            inpaintGrowMaskPx={inpaintGrowMaskPx}
            onInpaintGrowMaskPxChange={setInpaintGrowMaskPx}
            inpaintFeatherRatio={inpaintFeatherRatio}
            onInpaintFeatherRatioChange={setInpaintFeatherRatio}
            inpaintStylePreset={inpaintStylePreset}
            onInpaintStylePresetChange={setInpaintStylePreset}
            onInpaint={() => void handleInpaint()}
            eraseGrowMaskPx={eraseGrowMaskPx}
            onEraseGrowMaskPxChange={setEraseGrowMaskPx}
            eraseFeatherRatio={eraseFeatherRatio}
            onEraseFeatherRatioChange={setEraseFeatherRatio}
            onErase={() => void handleErase()}
            searchReplaceSearchPrompt={searchReplaceSearchPrompt}
            onSearchReplaceSearchPromptChange={setSearchReplaceSearchPrompt}
            searchReplacePrompt={searchReplacePrompt}
            onSearchReplacePromptChange={setSearchReplacePrompt}
            searchReplaceNegativePrompt={searchReplaceNegativePrompt}
            onSearchReplaceNegativePromptChange={setSearchReplaceNegativePrompt}
            searchReplaceGrowMaskPx={searchReplaceGrowMaskPx}
            onSearchReplaceGrowMaskPxChange={setSearchReplaceGrowMaskPx}
            searchReplaceStylePreset={searchReplaceStylePreset}
            onSearchReplaceStylePresetChange={setSearchReplaceStylePreset}
            searchReplaceSeed={searchReplaceSeed}
            onSearchReplaceSeedChange={setSearchReplaceSeed}
            onSearchReplace={() => void handleSearchReplace()}
            searchRecolorSelectPrompt={searchRecolorSelectPrompt}
            onSearchRecolorSelectPromptChange={setSearchRecolorSelectPrompt}
            searchRecolorPrompt={searchRecolorPrompt}
            onSearchRecolorPromptChange={setSearchRecolorPrompt}
            searchRecolorNegativePrompt={searchRecolorNegativePrompt}
            onSearchRecolorNegativePromptChange={setSearchRecolorNegativePrompt}
            searchRecolorGrowMaskPx={searchRecolorGrowMaskPx}
            onSearchRecolorGrowMaskPxChange={setSearchRecolorGrowMaskPx}
            searchRecolorStylePreset={searchRecolorStylePreset}
            onSearchRecolorStylePresetChange={setSearchRecolorStylePreset}
            searchRecolorSeed={searchRecolorSeed}
            onSearchRecolorSeedChange={setSearchRecolorSeed}
            onSearchRecolor={() => void handleSearchRecolor()}
            outpaintLeftPx={outpaintLeftPx}
            onOutpaintLeftPxChange={setOutpaintLeftPx}
            outpaintRightPx={outpaintRightPx}
            onOutpaintRightPxChange={setOutpaintRightPx}
            outpaintUpPx={outpaintUpPx}
            onOutpaintUpPxChange={setOutpaintUpPx}
            outpaintDownPx={outpaintDownPx}
            onOutpaintDownPxChange={setOutpaintDownPx}
            outpaintPrompt={outpaintPrompt}
            onOutpaintPromptChange={setOutpaintPrompt}
            outpaintCreativity={outpaintCreativity}
            onOutpaintCreativityChange={setOutpaintCreativity}
            outpaintNegativePrompt={outpaintNegativePrompt}
            onOutpaintNegativePromptChange={setOutpaintNegativePrompt}
            outpaintStylePreset={outpaintStylePreset}
            onOutpaintStylePresetChange={setOutpaintStylePreset}
            outpaintSeed={outpaintSeed}
            onOutpaintSeedChange={setOutpaintSeed}
            onOutpaint={() => void handleOutpaint()}
            i2iPrompt={i2iPrompt}
            onI2iPromptChange={setI2iPrompt}
            i2iEngine={i2iEngine}
            onI2iEngineChange={setI2iEngine}
            i2iStrength={i2iStrength}
            onI2iStrengthChange={setI2iStrength}
            i2iNegativePrompt={i2iNegativePrompt}
            onI2iNegativePromptChange={setI2iNegativePrompt}
            i2iStylePreset={i2iStylePreset}
            onI2iStylePresetChange={setI2iStylePreset}
            i2iSeed={i2iSeed}
            onI2iSeedChange={setI2iSeed}
            i2iMode={i2iMode}
            onI2iModeChange={setI2iMode}
            onImageToImage={() => void handleImageToImage()}
            selectedAITool={state.selectedAITool}
            onOpenAIPicker={() => setAIPickerOpen(true)}
            onRemoveBackground={handleRemoveBackground}
            isRemovingBackground={isRemovingBackground}
            canRemoveBackground={hasImage}
            removeBackgroundEngine={removeBackgroundEngine}
            onRemoveBackgroundEngineChange={setRemoveBackgroundEngine}
            replaceBgEngine={replaceBgEngine}
            onReplaceBgEngineChange={setReplaceBgEngine}
            replaceBgPrompt={replaceBgPrompt}
            onReplaceBgPromptChange={setReplaceBgPrompt}
            replaceBgReferenceBlob={replaceBgReferenceBlob}
            onReplaceBgReferenceBlobChange={setReplaceBgReferenceBlob}
            replaceBgLightReferenceBlob={replaceBgLightReferenceBlob}
            onReplaceBgLightReferenceBlobChange={setReplaceBgLightReferenceBlob}
            replaceBgLightDirection={replaceBgLightDirection}
            onReplaceBgLightDirectionChange={setReplaceBgLightDirection}
            replaceBgLightStrength={replaceBgLightStrength}
            onReplaceBgLightStrengthChange={setReplaceBgLightStrength}
            replaceBgPreserveSubject={replaceBgPreserveSubject}
            onReplaceBgPreserveSubjectChange={setReplaceBgPreserveSubject}
            replaceBgPreserveOriginalBg={replaceBgPreserveOriginalBg}
            onReplaceBgPreserveOriginalBgChange={setReplaceBgPreserveOriginalBg}
            replaceBgStyle={replaceBgStyle}
            onReplaceBgStyleChange={setReplaceBgStyle}
            replaceBgSeed={replaceBgSeed}
            onReplaceBgSeedChange={setReplaceBgSeed}
            replaceBgMode={replaceBgMode}
            onReplaceBgModeChange={setReplaceBgMode}
            onReplaceBackground={() => void handleReplaceBackground()}
            upscaleMode={upscaleMode}
            onUpscaleModeChange={(m) => {
              setUpscaleMode(m);
              // HQ's conservative endpoint rejects creativity < 0.2.
              // Bump the live value when entering HQ from a mode that
              // allowed lower so the slider, default, and server-side
              // gate all agree.
              if (m === 'high-quality' && upscaleCreativity < 0.2) {
                setUpscaleCreativity(0.2);
              }
            }}
            upscaleEngine={upscaleEngine}
            onUpscaleEngineChange={setUpscaleEngine}
            upscalePrompt={upscalePrompt}
            onUpscalePromptChange={setUpscalePrompt}
            upscaleCreativity={upscaleCreativity}
            onUpscaleCreativityChange={setUpscaleCreativity}
            upscaleFactor={upscaleFactor}
            onUpscaleFactorChange={setUpscaleFactor}
            upscaleSeed={upscaleSeed}
            onUpscaleSeedChange={setUpscaleSeed}
            upscaleOutputMode={upscaleOutputMode}
            onUpscaleOutputModeChange={setUpscaleOutputMode}
            upscaleSourceWidth={state.canvasWidth}
            upscaleSourceHeight={state.canvasHeight}
            onUpscale={() => void handleUpscale()}
            vectorizeStatus={vectorizeStatus}
            vectorizeSavedFileName={vectorizeSavedFileName}
            onVectorize={() => void handleVectorize()}
            onSaveVectorizeAgain={() => void handleSaveVectorizeAgain()}
            controlMode={controlMode}
            onControlModeChange={setControlMode}
            controlPrompt={controlPrompt}
            onControlPromptChange={setControlPrompt}
            controlNegativePrompt={controlNegativePrompt}
            onControlNegativePromptChange={setControlNegativePrompt}
            controlStrength={controlStrength}
            onControlStrengthChange={setControlStrength}
            controlFidelity={controlFidelity}
            onControlFidelityChange={setControlFidelity}
            controlStyleReferenceBlob={controlStyleReferenceBlob}
            onControlStyleReferenceBlobChange={setControlStyleReferenceBlob}
            controlStyleStrength={controlStyleStrength}
            onControlStyleStrengthChange={setControlStyleStrength}
            controlCompositionFidelity={controlCompositionFidelity}
            onControlCompositionFidelityChange={setControlCompositionFidelity}
            controlChangeStrength={controlChangeStrength}
            onControlChangeStrengthChange={setControlChangeStrength}
            controlSeed={controlSeed}
            onControlSeedChange={setControlSeed}
            controlOutputMode={controlOutputMode}
            onControlOutputModeChange={setControlOutputMode}
            onControl={() => void handleControl()}
            onApplyCrop={handleApplyCrop}
            onToggle={togglePropertiesPanel}
            slices={state.slices}
            onUpdateSlice={updateSlice}
            onRemoveSlice={removeSlice}
            onClearSlices={clearSlices}
            canvasWidth={state.canvasWidth}
            canvasHeight={state.canvasHeight}
          />
          <HistoryPanel
            isOpen={historyPanelOpen}
            placement={isLandscape ? 'right' : 'bottom'}
            onToggle={() => setHistoryPanelOpen((v) => !v)}
            entries={history.getEntries()}
            currentEntryId={history.getCurrentEntryId()}
            onJumpTo={(id) => void history.jumpTo(id)}
            onManualSnapshot={() => dispatchCommand('history.manual-snapshot')}
            onClearHistory={() => history.clearHistory()}
          />
          <LayersPanel
            isOpen={state.layersPanelOpen}
            placement={isLandscape ? 'right' : 'bottom'}
            layers={state.layers}
            activeLayerId={state.activeLayerId}
            hasSelection={canvas.hasMarqueeSelection}
            onAddLayer={handleAddLayer}
            onSelectLayer={handleSelectLayer}
            onSelectLayerFromName={handleSelectLayerFromName}
            onToggleVisibility={handleToggleLayerVisibility}
            onRenameLayer={handleRenameLayer}
            onDeleteLayer={handleDeleteLayer}
            onReorderLayer={handleReorderLayer}
            onSetLayerOpacity={handleSetLayerOpacity}
            onSetLayerBlendMode={handleSetLayerBlendMode}
            onSetLayerEffects={handleSetLayerEffects}
            onAddMaskToLayer={handleAddMaskToLayer}
            onDeleteMaskFromLayer={handleDeleteMaskFromLayer}
            onToggleMaskEnabled={handleToggleMaskEnabled}
            onToggleMaskInverted={handleToggleMaskInverted}
            onAddVectorMaskToLayer={handleAddVectorMaskToLayer}
            onConvertMaskToVector={handleConvertMaskToVector}
            onConvertMaskToRaster={handleConvertMaskToRaster}
            onEditVectorMaskPath={handleEditVectorMaskPath}
            onEditAdjustment={handleEditAdjustmentLayer}
            onSetActiveTool={handleToolSelect}
            editTargets={state.editTargets}
            onSetEditTarget={setEditTarget}
            selectedLayerIds={selectedLayerIds}
            onShiftSelectLayer={handleShiftSelectLayer}
            multiSelectMode={multiSelectMode}
            onToggleMultiSelectMode={toggleMultiSelectMode}
            onToggleSelectLayer={handleToggleSelectLayer}
            layerDepths={layerDepths}
            collapsedGroupIds={collapsedGroupIds}
            onToggleCollapse={handleToggleCollapse}
            onGroupActiveLayer={handleGroupActiveLayer}
            onUngroup={handleUngroup}
            onTransformLayer={handleBeginTransform}
            onLiquifyLayer={handleBeginLiquify}
            onEditPathLayer={handleBeginPathEdit}
            onMoveNodeInTree={handleMoveNodeInTree}
            onToggle={toggleLayersPanel}
          />
        </div>
      </div>

      <StatusBar
        zoom={state.zoom}
        canvasWidth={state.canvasWidth}
        canvasHeight={state.canvasHeight}
        subscribeToCursorMove={canvas.subscribeToCursorMove}
        currentColor={state.brushSettings.color}
        zoomControls={{
          onZoomIn: () => canvas.zoomIn(),
          onZoomOut: () => canvas.zoomOut(),
          onZoomTo: (z) => canvas.setZoom(z),
          onZoomFit: () => canvas.fitToContainer(),
        }}
        selection={
          // Status-bar chip is rendered whenever an image is loaded
          // (canvas dimensions known) so Quick Mask + Color Range
          // remain reachable even with no selection. Modify-rows
          // hide themselves inside the popover when there's nothing
          // to modify.
          state.canvasWidth > 0
            ? (() => {
                const b = canvas.hasMarqueeSelection ? canvas.getMarqueeBounds() : null;
                return {
                  hasSelection: canvas.hasMarqueeSelection,
                  width: b?.width ?? 0,
                  height: b?.height ?? 0,
                  canGrow: canvas.canGrowMarquee(),
                  onGrow: (deltaPx) => canvas.growMarqueeBy(deltaPx),
                  onInvert: handleInvertSelection,
                  onSelectSubject: handleSelectSubject,
                  selectSubjectBusy: aiStatus.kind === 'running',
                  quickMaskActive: state.quickMaskActive,
                  onToggleQuickMask: handleToggleQuickMask,
                  onOpenColorRange: () => {
                    // Snapshot the sampling source on open so the
                    // preview doesn't drift if the canvas changes
                    // mid-pick.
                    setSourceCanvas(canvas.getSourceCanvasForSampling());
                    setColorRangeOpen(true);
                  },
                  onOpenRefineEdge: () => {
                    setSourceCanvas(canvas.getSourceCanvasForSampling());
                    setRefineEdgeOpen(true);
                  },
                };
              })()
            : null
        }
      />

      {exportDialogOpen && (
        <ExportDialog
          onClose={handleExportDialogClose}
          onExportLocal={handleExportLocal}
          onExportFias={handleExportFias}
          onExportClipboard={handleExportClipboard}
          onExportPsd={handleExportPsd}
          onExportSvg={handleExportSvg}
          onExportPdf={handleExportPdf}
          onExportSlices={handleExportSlices}
          sliceCount={state.slices.length}
          onExportRegion={handleExportRegion}
          hasMarqueeSelection={canvas.hasMarqueeSelection}
          hasActiveLayer={state.activeLayerId != null}
          hasAnyVisibleLayer={hasAnyVisibleLayer}
          activeLayerName={
            state.activeLayerId ? state.document.layers[state.activeLayerId]?.name : undefined
          }
          onEstimateBlob={handleEstimateBlob}
          defaultFileName={state.fileName}
          defaultDestination={exportDialogDestination}
          /* Seed the format dropdown from the active tab's source.
             Priority: `fileHandle.name` (post-Save-As truth on FSA
             browsers) → `sourceFileName` (the original-with-extension
             captured at open time; covers non-FSA browsers and
             pre-Save-As state) → `state.fileName` (last resort; the
             dialog's own fileName display strips the extension, so
             this rarely fires) → PNG default. "Open JPEG → Save"
             lands the dropdown on JPEG everywhere. */
          defaultFormat={(() => {
            const activeTab = tabs.tabs.find((t) => t.id === tabs.activeTabId);
            return inferExportFormatFromFileName(
              activeTab?.fileHandle?.name ?? activeTab?.sourceFileName ?? state.fileName,
            );
          })()}
          canvasWidth={state.canvasWidth}
          canvasHeight={state.canvasHeight}
        />
      )}

      <AdjustmentsModal
        kind={activeAdjustmentKind}
        sessionId={adjustmentSessionId}
        targetCanvases={adjustmentTargetCanvases}
        onPreviewTick={handleAdjustmentPreviewTick}
        onCommit={handleAdjustmentCommit}
        onCreateAdjustmentLayer={handleCreateAdjustmentLayer}
        editingAdjustment={editingAdjustment ?? undefined}
        onLiveEditValue={handleAdjustmentLiveEdit}
        onClose={handleAdjustmentClose}
      />

      <ColorRangeDialog
        open={colorRangeOpen}
        source={sourceCanvas}
        onClose={() => setColorRangeOpen(false)}
        onApply={(maskCanvas) => {
          // Match the AI Select Subject UX — install via
          // `setSelectionFromMask`, ensure the marquee panel
          // reflects the new wand-style shape, and drop the user
          // into the marquee tool so the modify-selection rows
          // become reachable.
          const installed = canvas.setSelectionFromMask(maskCanvas);
          if (installed) {
            if (state.marqueeSettings.shape !== 'wand') {
              updateMarqueeSettings({ shape: 'wand' });
            }
            if (state.activeTool !== 'marquee') {
              setActiveTool('marquee');
            }
          }
        }}
      />

      {refineEdgeOpen && sourceCanvas && (
        <RefineEdgeWorkspace
          source={sourceCanvas}
          initialMaskCanvas={canvas.getSelectionAsMaskCanvas()}
          onClose={() => setRefineEdgeOpen(false)}
          onApply={(maskCanvas, outputTo) => {
            if (outputTo === 'selection') {
              canvas.setSelectionFromMask(maskCanvas);
              if (state.marqueeSettings.shape !== 'wand') {
                updateMarqueeSettings({ shape: 'wand' });
              }
            }
            // Other output modes (Mask / New Layer / New Layer With
            // Mask) are listed in the roadmap but ship in a follow-
            // up — the layer-mask wiring needs richer state plumbing
            // than this PR adds, and a single-output v1 is honest
            // about scope. The dropdown is rendered disabled for
            // those options.
            setRefineEdgeOpen(false);
          }}
        />
      )}

      {liquifyOpen && liquifySource && (
        <LiquifyWorkspace
          source={liquifySource}
          onClose={handleCloseLiquify}
          onApply={(deformedCanvas) => {
            void handleApplyLiquify(deformedCanvas);
          }}
        />
      )}

      {vectorizeSaveDialogOpen && (
        <VectorizeSaveDialog
          defaultFileName={(state.fileName || 'image').replace(/\.[^.]+$/, '')}
          defaultDestination={vectorizeSaveDialogDestination}
          onClose={handleVectorizeSaveDialogClose}
          onSave={handleVectorizeSave}
        />
      )}

      {confirmCloseTabId !== null && (
        <ConfirmCloseDialog
          fileName={tabs.tabs.find((t) => t.id === confirmCloseTabId)?.fileName ?? ''}
          isSaving={isQuickSaving}
          saveError={saveError}
          onCancel={handleConfirmCancel}
          onDiscard={handleConfirmDiscard}
          onSave={handleConfirmSave}
          onSaveAs={handleConfirmSaveAs}
        />
      )}

      {pendingWarpMode !== null &&
        (() => {
          const layerId = state.transformLayerId ?? state.activeLayerId;
          const node = layerId ? state.document.layers[layerId] : null;
          if (!node || (node.type !== 'vector' && node.type !== 'shape' && node.type !== 'text')) {
            // Defensive: should not happen — handleSetTransformMode
            // only opens the dialog when the active layer matches
            // one of these. If it does (e.g. the layer was deleted
            // between chip click and render), don't render.
            return null;
          }
          return (
            <RasterizeBeforeWarpDialog
              layerName={node.name}
              sourceType={node.type}
              mode={pendingWarpMode}
              onCancel={handleCancelRasterizeBeforeWarp}
              onConfirm={handleConfirmRasterizeBeforeWarp}
            />
          );
        })()}

      {pendingRasterizeBeforePaint !== null &&
        (() => {
          const { layerId, sourceType, action } = pendingRasterizeBeforePaint;
          const node = state.document.layers[layerId];
          if (!node) {
            // Layer disappeared between callback and render — close
            // the dialog silently. Cancel cleans the per-layer
            // pending flag inside the canvas hook.
            canvas.cancelLayerForPaint(layerId);
            setPendingRasterizeBeforePaint(null);
            return null;
          }
          return (
            <RasterizeBeforePaintDialog
              layerName={node.name}
              sourceType={sourceType}
              action={action}
              onCancel={() => {
                canvas.cancelLayerForPaint(layerId);
                setPendingRasterizeBeforePaint(null);
              }}
              onConfirm={() => {
                canvas.approveLayerForPaint(layerId);
                setPendingRasterizeBeforePaint(null);
              }}
            />
          );
        })()}

      {helpDialogOpen && (
        <IconLegendDialog
          onClose={() => setHelpDialogOpen(false)}
          registry={commandRegistryRef.current}
          isMac={isMac}
        />
      )}

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        registry={commandRegistryRef.current}
        context={commandContext}
        isMac={isMac}
      />

      <KeyboardSettingsDialog
        open={keyboardSettingsOpen}
        onClose={() => setKeyboardSettingsOpen(false)}
        registry={commandRegistryRef.current}
        isMac={isMac}
        onOverridesChanged={handleKeyboardOverridesChanged}
      />

      {templatePickerOpen && (
        <TemplatePickerDialog
          onClose={() => setTemplatePickerOpen(false)}
          onPick={handleNewFromTemplate}
        />
      )}

      {saveTemplateOpen && (
        <SaveTemplateDialog
          onClose={() => setSaveTemplateOpen(false)}
          width={state.canvasWidth}
          height={state.canvasHeight}
          dpi={state.dpi ?? undefined}
          defaultName={state.fileName || 'My template'}
        />
      )}

      <DocumentSettingsDialog
        open={documentSettingsOpen}
        onClose={() => setDocumentSettingsOpen(false)}
        width={state.canvasWidth}
        height={state.canvasHeight}
        dpi={state.dpi}
        onDpiChanged={setDpi}
      />

      <CanvasSizeDialog
        open={canvasSizeOpen}
        onClose={handleCloseCanvasSize}
        currentWidth={state.canvasWidth}
        currentHeight={state.canvasHeight}
        onApply={handleApplyCanvasSize}
      />

      <GridSettingsDialog
        open={gridSettingsOpen}
        config={state.viewSettings.gridConfig}
        onApply={(next) => updateViewSettings({ gridConfig: next })}
        onClose={() => setGridSettingsOpen(false)}
      />

      <NewGuideDialog
        open={newGuideOpen}
        onApply={addGuide}
        onClose={() => setNewGuideOpen(false)}
      />

      <FiasImagePickerDialog
        open={fiasPickerOpen}
        onOpenChange={setFiasPickerOpen}
        onImageSelected={handleFiasImageSelected}
      />

      <AIToolPickerDialog
        open={state.aiPickerOpen}
        onOpenChange={setAIPickerOpen}
        onSelect={setSelectedAITool}
      />

      {/* Hidden file input for toolbar open. Widened to accept PSD / PSB
          alongside raster formats — the `image/*` pattern doesn't cover
          Photoshop files (their MIME isn't in the image/* tree), so we
          enumerate extensions explicitly. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.psd,.psb,.svg"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) dispatchOpenedFile(file);
          e.target.value = '';
        }}
      />

      {/* Loading overlay — full-screen, blocking, shown while a long-
          running async op (export, AI inference, AI background removal)
          is in flight so the user always knows something is happening. */}
      {(isExporting ||
        isRemovingBackground ||
        aiStatus.kind === 'loading-model' ||
        aiStatus.kind === 'running') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-background rounded-lg p-6 shadow-xl">
            <p className="text-sm">
              {isRemovingBackground
                ? 'Removing background…'
                : aiStatus.kind === 'loading-model'
                  ? `Loading AI model${
                      typeof aiStatus.progressPct === 'number' ? ` ${aiStatus.progressPct}%` : '…'
                    }`
                  : aiStatus.kind === 'running'
                    ? aiStatus.label
                    : 'Exporting...'}
            </p>
          </div>
        </div>
      )}

      {removeBackgroundError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-destructive text-destructive-foreground rounded-md px-4 py-2 shadow-lg flex items-center gap-3">
          <span className="text-sm">{removeBackgroundError}</span>
          <button
            type="button"
            className="text-xs underline"
            onClick={() => setRemoveBackgroundError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
